import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Directive, Input, Component, ChangeDetectionStrategy, HostBinding, EventEmitter, Output, Pipe, ViewChildren, TemplateRef, ViewContainerRef, ViewChild, ContentChild, Inject, HostListener, ContentChildren, InjectionToken, Optional, NgModule } from '@angular/core';
import * as i7 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i2$2 from '@angular/material/checkbox';
import { MatCheckboxModule } from '@angular/material/checkbox';
import * as i3$2 from '@angular/material/core';
import { MatRippleModule } from '@angular/material/core';
import * as i1$2 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import * as i2 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as i6 from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as i4 from '@firestitch/common';
import { getNormalizedPath, FsCommonModule } from '@firestitch/common';
import * as i3$1 from '@firestitch/file';
import { FsFileModule } from '@firestitch/file';
import * as i2$3 from '@firestitch/filter';
import { ItemType, FilterComponent, FsFilterModule } from '@firestitch/filter';
import * as i2$1 from '@firestitch/menu';
import { FsMenuModule } from '@firestitch/menu';
import * as i1$1 from '@firestitch/prompt';
import { FsPromptModule } from '@firestitch/prompt';
import * as i2$4 from '@firestitch/scroll';
import { FsScrollModule } from '@firestitch/scroll';
import { get, isString, isObject, isBoolean, isNumber, isFunction, cloneDeep, random, mergeWith, merge as merge$1 } from 'lodash-es';
import { BehaviorSubject, Subject, Observable, merge, from, combineLatest, of, EMPTY } from 'rxjs';
import { takeUntil, take, filter, tap, skip, distinctUntilChanged, map, shareReplay, debounceTime, switchMap, mapTo, catchError } from 'rxjs/operators';
import * as i4$1 from '@firestitch/drawer';
import * as i5 from '@firestitch/selection';
import * as i1$3 from '@firestitch/store';
import { FsPersistanceStore } from '@firestitch/store';

var ReorderPosition;
(function (ReorderPosition) {
    ReorderPosition["Left"] = "left";
    ReorderPosition["Right"] = "right";
})(ReorderPosition || (ReorderPosition = {}));
var ReorderStrategy;
(function (ReorderStrategy) {
    ReorderStrategy["Always"] = "always";
    ReorderStrategy["Manual"] = "manual";
    ReorderStrategy["Custom"] = "custom";
})(ReorderStrategy || (ReorderStrategy = {}));
class ReorderController {
    constructor() {
        this._enabled$ = new BehaviorSubject(false);
        this._manualReorderActivated$ = new BehaviorSubject(false);
        this._reorderDisabled$ = new BehaviorSubject(false);
        this._position = new BehaviorSubject(null);
        this._strategy = new BehaviorSubject(null);
        this._destroy$ = new Subject();
    }
    get enabled() {
        return this._enabled$.getValue();
    }
    set enabled(value) {
        this._enabled$.next(value);
        this._manualReorderActivated$.next(this.enabled && this.strategy === ReorderStrategy.Manual);
    }
    get strategy() {
        return this._strategy.getValue();
    }
    get position() {
        return this._position.getValue();
    }
    get dataController() {
        return this._dataController;
    }
    get selectionController() {
        return this._selectionController;
    }
    get enabled$() {
        return this._enabled$.asObservable();
    }
    get position$() {
        return this._position.asObservable();
    }
    get strategy$() {
        return this._strategy.asObservable();
    }
    get manualReorderActivated() {
        return this._manualReorderActivated$.getValue();
    }
    get reorderDisabled$() {
        return this._reorderDisabled$.asObservable();
    }
    get reorderDisabled() {
        return this._reorderDisabled$.getValue();
    }
    initWithConfig(data, dataController, actionsController, selectionController) {
        var _a, _b, _c;
        if (!data) {
            return;
        }
        this.menu = (_a = data.menu) !== null && _a !== void 0 ? _a : false;
        this._setPosition((_b = data.position) !== null && _b !== void 0 ? _b : ReorderPosition.Left);
        if (!data.disabled) {
            this._setStrategy(ReorderStrategy.Always);
        }
        if (data.toggle) {
            this._setStrategy(ReorderStrategy.Manual);
        }
        // this.strategy = data.strategy ?? ReorderStrategy.Manual;
        this.label = data.label;
        this.multiple = data.multiple;
        this.status = (_c = data.status) !== null && _c !== void 0 ? _c : true;
        this.startCallback = data.start;
        this.movedCallback = data.moved;
        this.doneCallback = data.done;
        this.moveDropCallback = data.moveDrop;
        this._dataController = dataController;
        this._actionsController = actionsController;
        this._selectionController = selectionController;
        if (this.strategy === ReorderStrategy.Always) {
            this.enableReorder();
        }
        else if (this.strategy === ReorderStrategy.Manual) {
            actionsController.addReorderAction({
                label: this.label || 'Reorder',
                menu: this.menu,
                primary: false,
                click: () => {
                    this.enableReorder();
                },
                disabled: () => {
                    return this.reorderDisabled;
                }
            });
            actionsController.addReorderDoneAction({
                label: 'Done',
                primary: false,
                click: () => {
                    this.disableReorder();
                },
                disabled: () => {
                    return this.reorderDisabled;
                },
            });
        }
    }
    enableReorder() {
        if ((this.strategy === ReorderStrategy.Manual || this.strategy === ReorderStrategy.Custom) && this.startCallback) {
            const returnedValue = this.startCallback();
            if (returnedValue && returnedValue instanceof Observable) {
                this.disableReorderAction();
                returnedValue
                    .pipe(takeUntil(this._destroy$))
                    .subscribe(() => {
                    this.enabled = true;
                    this.enableReorderAction();
                });
                return;
            }
        }
        this.enabled = true;
    }
    disableReorder() {
        if (this.strategy === ReorderStrategy.Manual || this.strategy === ReorderStrategy.Custom) {
            const returnedValue = this.reorderFinished();
            if (returnedValue && returnedValue instanceof Observable) {
                this.disableReorderAction();
                returnedValue
                    .pipe(takeUntil(this._destroy$))
                    .subscribe(() => {
                    this.enabled = false;
                    this.enableReorderAction();
                });
                return;
            }
        }
        this.enabled = false;
    }
    /**
     * Exec end callback
     */
    reorderFinished() {
        if (this.doneCallback) {
            return this.doneCallback(this._dataController.reorderData);
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    /**
     * Enable reorder action and update filter actions state
     */
    enableReorderAction() {
        this._reorderDisabled$.next(false);
        this._actionsController.updateDisabledState();
    }
    /**
     * Disable reorder action and update filter actions state
     */
    disableReorderAction() {
        this._reorderDisabled$.next(true);
        this._actionsController.updateDisabledState();
    }
    _setPosition(value) {
        this._position.next(value);
    }
    _setStrategy(value) {
        this._strategy.next(value);
    }
}
ReorderController.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ReorderController, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ReorderController.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ReorderController });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ReorderController, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

var SelectionChangeType;
(function (SelectionChangeType) {
    SelectionChangeType["AllVisibleSelectionChange"] = "AllVisibleSelectionChange";
    SelectionChangeType["RowSelectionChange"] = "RowSelectionChange";
    SelectionChangeType["SelectedAll"] = "SelectedAll";
})(SelectionChangeType || (SelectionChangeType = {}));
class SelectionController {
    constructor(config = {}, _trackBy, _selectionDialog) {
        this._trackBy = _trackBy;
        this._selectionDialog = _selectionDialog;
        // Options from passed config
        this.actions = [];
        this.selectAll = true;
        // Store for selected visible rows
        this.selectedRows = new Map();
        this.selectedGroups = new Map();
        // Reference to selection dialog
        this._selectionDialogRef = null;
        this._selectionChange = new Subject();
        // Selected only visible rows (ex.: selected only limited 15 rows when we have pagination)
        this._selectedAllVisible = false;
        // Selected all rows when was clicked "Select All" in selection dialog
        // or count of visible records is equal to total and all visible rows was selected
        this._selectedAll = false;
        this._selectedRecords = 0;
        this._visibleRecordsCount = 0;
        this._totalRecordsCount = 0;
        this._destroy$ = new Subject();
        this._disabled$ = new BehaviorSubject(false);
        this.actions = config.actions ? [...config.actions] : [];
        this.actionSelectedFn = config.actionSelected;
        this.allSelectedFn = config.allSelected;
        this.cancelledFn = config.cancelled;
        this.selectionChangedFn = config.selectionChanged;
        this.selectAll = config.selectAll;
        this._disabled$.next(!!config.disabled);
    }
    get disabled() {
        return this._disabled$.getValue();
    }
    get disabled$() {
        return this._disabled$.asObservable();
    }
    get selectedAll() {
        return this._selectedAll;
    }
    get selectionChange$() {
        return this._selectionChange.pipe(takeUntil(this._destroy$));
    }
    setRowsCallback(data) {
        this._getRows = data;
    }
    /**
     * Trigger when row was selected
     *
     * @param row
     * @param checked
     */
    rowSelectionChange(row, checked) {
        if (row) {
            if (checked) {
                this._selectRow(row);
                this.openDialog();
            }
            else {
                if (this._selectedAll) {
                    this._selectedAll = false;
                }
                this._deselectRow(row);
            }
        }
        this._updateSelectionRefSelected();
        this._updateSelectedVisibleStatus();
    }
    /**
     * Do check or uncheck of visible rows
     *
     * @param checked
     */
    selectAllVisibleRows(checked) {
        this.openDialog();
        this._selectedAllVisible = checked;
        this._selectedRecords = 0;
        const rows = this._getRows();
        if (checked) {
            rows.forEach((row) => {
                const identifier = this._rowIdentifier(row.data);
                if (row.isGroup) {
                    this._setNumberOfSelectedChildrenInGroup(identifier, row.children.length);
                }
                else {
                    this.selectedRows.set(identifier, row.data);
                    this._selectedRecords++;
                }
            });
        }
        else {
            rows.forEach((row) => {
                const identifier = this._rowIdentifier(row.data);
                if (row.isGroup) {
                    this._setNumberOfSelectedChildrenInGroup(identifier, 0);
                }
                else {
                    this.selectedRows.delete(identifier);
                }
            });
            this._selectedAll = false;
            this._selectedRecords = 0;
        }
        // Fire an event that all visible selection was changed
        this._visibleRowsSelectionChanged();
        this._updateSelectionRefSelected();
    }
    isRowSelected(rowData) {
        return this.selectedRows.has(this._rowIdentifier(rowData))
            || this.selectedGroups.has(this._rowIdentifier(rowData))
            || this.selectedAll;
    }
    isGroupSelected(row) {
        var _a;
        const identifier = this._rowIdentifier(row.data);
        if (this.selectedGroups.has(identifier)
            && ((_a = row.children) === null || _a === void 0 ? void 0 : _a.length) > this.selectedGroups.get(identifier)) {
            return 'indeterminate';
        }
        return this.selectedGroups.has(identifier) || this.selectedAll;
    }
    /**
     * Open selection dialog and create reference
     */
    openDialog() {
        if (!this._selectionDialogRef) {
            this._selectionDialogRef = this._selectionDialog.open({
                selectAll: this.selectAll,
                allCount: this._totalRecordsCount,
                actions: [...this.actions],
                // selectAll: this.sele
            });
            this._subscribeToSelection();
        }
    }
    /**
     * Update count of visible elements
     *
     * @param count
     */
    updateVisibleRecordsCount(count) {
        this._visibleRecordsCount = count;
    }
    /**
     * Update count of total available elemenets
     *
     * @param count
     */
    updateTotalRecordsCount(count) {
        this._totalRecordsCount = count;
        if (this._selectionDialogRef) {
            this._selectionDialogRef.updateAllCount(this._totalRecordsCount);
        }
    }
    pageChanged(infinityScrollEnabled) {
        if (this._selectedAll) {
            if (!infinityScrollEnabled) {
                this._resetSelection();
            }
        }
        else {
            this._selectedRecords = 0;
            this._getRows()
                .map((row) => row.data)
                .forEach((rowData) => {
                const identified = this._rowIdentifier(rowData);
                if (this.selectedRows.has(identified)) {
                    this._selectedRecords++;
                }
            });
            this._updateSelectionRefSelected();
            this._updateSelectedVisibleStatus();
        }
    }
    enableSelection() {
        this._disabled$.next(false);
    }
    disableSelection() {
        this._disabled$.next(true);
    }
    /**
     * Method will be called from List for remove row if it was selected
     *
     * BUT methods for update visible and etc. will be called a bit later
     *
     * @param row
     */
    removeRow(row) {
        this.selectedRows.delete(this._rowIdentifier(row));
        this._updateSelectionRefSelected();
        this._updateSelectionRefSelectedAll();
    }
    /**
     * Intersection of selected and passed rows to remove rows that we dont need more
     *
     * @param rows
     */
    selectedRowsIntersection(rows) {
        const rowsIndentifiers = rows.map((row) => this._rowIdentifier(row));
        this.selectedRows.forEach((selectedRow, identifier) => {
            if (rowsIndentifiers.indexOf(identifier) === -1) {
                this.removeRow(selectedRow);
            }
        });
    }
    updateConfig({ actions, actionSelected, allSelected, cancelled, selectionChanged, selectAll, }) {
        this.actions = actions ? [...actions] : this.actions;
        this.actionSelectedFn = actionSelected !== null && actionSelected !== void 0 ? actionSelected : this.actionSelectedFn;
        this.allSelectedFn = allSelected !== null && allSelected !== void 0 ? allSelected : this.allSelectedFn;
        this.cancelledFn = cancelled !== null && cancelled !== void 0 ? cancelled : this.cancelledFn;
        this.selectionChangedFn = selectionChanged !== null && selectionChanged !== void 0 ? selectionChanged : this.selectionChangedFn;
        this.selectAll = selectAll !== null && selectAll !== void 0 ? selectAll : this.selectAll;
    }
    // Reset actions to default set
    resetActions() {
        var _a;
        (_a = this._selectionDialogRef) === null || _a === void 0 ? void 0 : _a.resetActions();
    }
    closeSelectionDialog() {
        var _a;
        (_a = this._selectionDialogRef) === null || _a === void 0 ? void 0 : _a.close();
    }
    destroy() {
        this._resetSelection();
        this.actions = null;
        this.actionSelectedFn = null;
        this.allSelectedFn = null;
        this.cancelledFn = null;
        this.closeSelectionDialog();
        this._selectionDialogRef = null;
        this._destroy$.next();
        this._destroy$.complete();
        this._selectionChange.complete();
    }
    /**
     * Subscribe to selectionRef events
     */
    _subscribeToSelection() {
        this._selectionDialogRef.actionSelected$()
            .pipe(takeUntil(this._destroy$))
            .subscribe((data) => {
            this._onActionActions(data);
        });
        this._selectionDialogRef.cancelled$()
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._onCancelActions());
        this._selectionDialogRef.allSelected$()
            .pipe(takeUntil(this._destroy$))
            .subscribe((data) => {
            this._onSelectAllActions(data);
        });
    }
    /**
     * If some action was clicked on selection ref dialog
     *
     * @param data
     */
    _onActionActions(data) {
        if (!this.actionSelectedFn) {
            return;
        }
        // Execute callback
        const result = this.actionSelectedFn(Object.assign({ selected: Array.from(this.selectedRows.values()).map((row) => {
                return Object.assign({}, row);
            }) }, data));
        // If result is observable
        if (result instanceof Observable) {
            // Subscribe and whait why it resolved
            result.pipe(take(1), takeUntil(this._destroy$)).subscribe({
                next: () => {
                    // Close dialog
                    this.closeSelectionDialog();
                    // Uncheck all visible rows
                    this.selectAllVisibleRows(false);
                },
                error: () => { },
            });
        }
    }
    /**
     * If cancel was clicked on selection ref dialog
     */
    _onCancelActions() {
        this.selectAllVisibleRows(false);
        if (this.cancelledFn) {
            this.cancelledFn();
        }
        this._selectionDialogRef = null;
    }
    /**
     * If "Select All" action was clicked on selection ref dialog
     *
     * @param flag
     */
    _onSelectAllActions(flag) {
        this._selectedAll = flag;
        this.selectAllVisibleRows(flag);
        this._selectionDialogRef.updateSelected(this._totalRecordsCount);
        this._selectionChangeEvent(SelectionChangeType.SelectedAll, this._selectedAll);
        this._updateSelectionRefSelectedAll();
        if (!this.allSelectedFn) {
            return;
        }
        this.allSelectedFn(flag);
    }
    _selectionChangedActions() {
        // Remove actions if no rows selected
        // otherwise it will "blink" when user select any action
        if (this.selectedRows.size === 0) {
            this._selectionDialogRef.updateActions([]);
            return;
        }
        if (this.selectionChangedFn) {
            const result = this.selectionChangedFn(Array.from(this.selectedRows.values()), this.selectedAll, this._selectionDialogRef);
            if (result) {
                if (result instanceof Observable) {
                    result.pipe(take(1), takeUntil(this._destroy$)).subscribe({
                        next: (actions) => {
                            this._selectionDialogRef.updateActions(actions);
                        },
                    });
                }
                else if (Array.isArray(result)) {
                    this._selectionDialogRef.updateActions(result);
                }
            }
        }
    }
    /**
     * Update in Dialog Ref how much rows was selected
     */
    _updateSelectionRefSelected() {
        if (this._selectionDialogRef) {
            this._selectionDialogRef.updateSelected(this.selectedRows.size);
            // S-T1268
            if (this.selectedRows.size === 0) {
                this._selectionDialogRef.close();
            }
            this._selectionChangedActions();
        }
    }
    /**
     * Update in Dialog Ref selected all status
     *
     * Ex.: Was clicked "Select All" in Dialog and after that some checkbox was unchecked
     * Dialog Ref must know about it
     *
     */
    _updateSelectionRefSelectedAll() {
        if (this._selectionDialogRef) {
            this._selectionDialogRef.updateSelectedAllStatus(this._selectedAll);
        }
    }
    /**
     * Check if all visible rows was checked and send event to main header checkbox
     */
    _updateSelectedVisibleStatus() {
        this._selectedAllVisible = this._selectedRecords !== 0 && this._selectedRecords === this._visibleRecordsCount;
        this._selectionChangeEvent(SelectionChangeType.RowSelectionChange, this._selectedAllVisible);
    }
    /**
     * If select all checkbox in header was changed
     */
    _visibleRowsSelectionChanged() {
        this._selectionChangeEvent(SelectionChangeType.AllVisibleSelectionChange, this._selectedAllVisible);
    }
    /**
     * Method constructor for events
     *
     * @param type
     * @param payload
     */
    _selectionChangeEvent(type, payload) {
        this._selectionChange.next({
            type,
            payload,
        });
    }
    /**
     * Get row identified by trackBy path
     *
     * @param row
     */
    _rowIdentifier(row) {
        const identifier = get(row, this._trackBy, undefined);
        if (identifier === undefined) {
            console.warn('Selection can not recognize track by field for row. ' +
                'Please check if you had configured trackBy function.');
        }
        return identifier;
    }
    _selectRow(row) {
        const identifier = this._rowIdentifier(row.data);
        if (row.isGroup) {
            row.children.forEach((childRow) => {
                this._selectRow(childRow);
            });
        }
        else {
            if (row.isChild) {
                this._selectChildRow(row);
            }
            this.selectedRows.set(identifier, row.data);
            this._selectedRecords++;
        }
    }
    _deselectRow(row) {
        const identifier = this._rowIdentifier(row.data);
        if (row.isGroup) {
            this.selectedGroups.delete(identifier);
            row.children.forEach((childRow) => {
                this._deselectRow(childRow);
            });
        }
        else {
            if (row.isChild && this.selectedRows.has(identifier)) {
                this._deselectChildRow(row);
            }
            this.selectedRows.delete(identifier);
            this._selectedRecords--;
        }
    }
    _selectChildRow(row) {
        const parentIdentifier = this._rowIdentifier(row.parent.data);
        const selectedChildrenNumber = this.selectedGroups.get(parentIdentifier) || 0;
        this._setNumberOfSelectedChildrenInGroup(parentIdentifier, selectedChildrenNumber + 1);
    }
    _deselectChildRow(row) {
        const parentIdentifier = this._rowIdentifier(row.parent.data);
        const selectedChildrenNumber = this.selectedGroups.get(parentIdentifier) || 0;
        if (selectedChildrenNumber > 1) {
            this._setNumberOfSelectedChildrenInGroup(parentIdentifier, selectedChildrenNumber - 1);
        }
        else {
            this._setNumberOfSelectedChildrenInGroup(parentIdentifier, 0);
        }
    }
    _setNumberOfSelectedChildrenInGroup(identifier, n) {
        if (n === 0) {
            this.selectedGroups.delete(identifier);
            return;
        }
        this.selectedGroups.set(identifier, n);
    }
    /**
     * Reset selection
     */
    _resetSelection() {
        this._selectedAll = false;
        this._selectedAllVisible = false;
        this._visibleRecordsCount = 0;
        this._selectedRecords = 0;
        this.selectedRows.clear();
        this._updateSelectionRefSelected();
        this._updateSelectionRefSelectedAll();
        this._visibleRowsSelectionChanged();
    }
}

class FsListDraggableListDirective {
    constructor(_cdRef, _zone, _containerElement, _reorderController, _renderer) {
        this._cdRef = _cdRef;
        this._zone = _zone;
        this._containerElement = _containerElement;
        this._reorderController = _reorderController;
        this._renderer = _renderer;
        // Handlers
        this._dragToHandler = this.dragTo.bind(this);
        this._dragEndHandler = this.dragEnd.bind(this);
        this._windowTouchMoveHandler = () => { };
        this._dragInProgress = false;
        this._dragStart$ = new Subject();
        this._dragEnd$ = new Subject();
        this._draggableChildrenDirectives = [];
        this._selectedRowsDirectives = [];
        this._destroy$ = new Subject();
    }
    get dragStart$() {
        return this._dragStart$.pipe(takeUntil(this._destroy$));
    }
    get dragEnd$() {
        return this._dragEnd$.pipe(takeUntil(this._destroy$));
    }
    get draggableItem() {
        return this._rows[this._draggableElementIndex];
    }
    get _isMultipleDrag() {
        return this._reorderController.multiple && this._selectedRowsDirectives.length > 1;
    }
    addDraggableDirective(dir) {
        this._draggableChildrenDirectives.push(dir);
    }
    removeDraggableDirective(dir) {
        const idx = this._draggableChildrenDirectives.indexOf(dir);
        if (idx !== -1) {
            this._draggableChildrenDirectives.splice(idx, 1);
        }
    }
    /**
     * Prepare draggable elements and add events
     *
     * @param draggableElement
     */
    dragStart(draggableElement) {
        if (this._dragInProgress) {
            return;
        }
        if (this._reorderController.startCallback
            && this._reorderController.strategy === ReorderStrategy.Always) {
            this._reorderController.startCallback();
        }
        this._dragInProgress = true;
        window.document.body.classList.add('reorder-in-progress');
        this._draggableElement = draggableElement;
        this._detectSelectedRows();
        this.prepareElements();
        this.initDraggableElement();
        this._draggableElementStartIndex = this._draggableElementIndex;
        this._draggableElement.classList.add('draggable-elem');
        this._zone.runOutsideAngular(() => {
            window.addEventListener('touchmove', this._windowTouchMoveHandler, { passive: true });
            window.document.addEventListener('mousemove', this._dragToHandler, { passive: true });
            window.document.addEventListener('touchmove', this._dragToHandler, { passive: false });
        });
        window.document.addEventListener('mouseup', this._dragEndHandler);
        window.document.addEventListener('touchend', this._dragEndHandler);
        window.document.addEventListener('touchcancel', this._dragEndHandler);
        this._dragStart$.next();
    }
    /**
     * Move draggable elements and swap items
     *
     * @param event
     */
    dragTo(event) {
        this.touchFix(event);
        const elemIndex = this.lookupElementUnder(event);
        const targetRow = this._rows[elemIndex];
        if (this._multipleDraggableElementPreview) {
            this._multipleDraggableElementPreview.style.left = `${event.clientX}px`;
            this._multipleDraggableElementPreview.style.top = `${event.clientY}px`;
        }
        // Can not drag before first group and after last group
        const swapWithBoundaryGroupElement = (elemIndex === 0 || elemIndex === this._rows.length - 1)
            && targetRow.isGroup
            // TODO fix isChild & all
            && this.draggableItem.isChild;
        if (!swapWithBoundaryGroupElement) {
            if (elemIndex !== null) {
                if (targetRow.readyToSwap) {
                    this.swapWithIndex(elemIndex);
                    if (this._draggableElementPreview) {
                        this._draggableElementPreview.classList.remove('fs-list-no-drop');
                    }
                }
                else {
                    if (this._draggableElementPreview) {
                        this._draggableElementPreview.classList.add('fs-list-no-drop');
                    }
                }
            }
            // FIXME
            if (this._draggableElementPreview) {
                const topOffset = (event.y || event.clientY) - (this._draggableElementHeight / 2);
                this._draggableElementPreview.style.top = `${topOffset}px`;
            }
        }
    }
    /**
     * Remove events and classes after drag finish
     */
    dragEnd() {
        this._dragInProgress = false;
        this._reorderController.dataController.finishReorder();
        if (this._reorderController.movedCallback
            && this._draggableElementStartIndex !== this._draggableElementIndex) {
            this._reorderController.movedCallback(this._reorderController.dataController.reorderData);
        }
        if (this._reorderController.strategy === ReorderStrategy.Always) {
            this._zone.run(() => {
                if (this._reorderController.doneCallback) {
                    const result = this._reorderController.doneCallback(this._reorderController.dataController.reorderData);
                    this._waitUntilIsNotDone(result);
                }
            });
        }
        // this._reorderController.dataController.updateOrderByRows(this._rows);
        ///
        this._containerElement.nativeElement.classList.remove('drag-hidden');
        this._draggableElement.classList.remove('draggable-elem');
        window.document.body.classList.remove('reorder-in-progress');
        this._draggableElement = null;
        if (this._draggableElementPreview) {
            this._draggableElementPreview.remove();
            this._draggableElementPreview = null;
        }
        else {
            this._renderer.removeChild(document.body, this._multipleDraggableElementPreview);
            this._multipleDraggableElementPreview = null;
        }
        this._draggableElementHeight = null;
        this._draggableElementIndex = null;
        this._draggableElementStartIndex = null;
        this._selectedRowsDirectives = [];
        window.removeEventListener('touchmove', this._windowTouchMoveHandler);
        window.document.removeEventListener('mousemove', this._dragToHandler);
        window.document.removeEventListener('touchmove', this._dragToHandler);
        window.document.removeEventListener('mouseup', this._dragEndHandler);
        window.document.removeEventListener('touchend', this._dragEndHandler);
        window.document.removeEventListener('touchcancel', this._dragEndHandler);
        this._dragEnd$.next();
    }
    /**
     * looking row elements and save their dims
     */
    prepareElements() {
        this.lookupChildElements();
        this.calcElementsDimensions();
    }
    /**
     * Store child rows
     */
    lookupChildElements() {
        this._childRowElements = Array.from(this._containerElement.nativeElement.querySelectorAll('tr:not(.drag-hidden)'))
            .reduce((acc, rowElement, index) => {
            const element = { target: rowElement };
            if (rowElement === this._draggableElement) {
                this._draggableElementIndex = index;
                element.active = true;
            }
            acc.push(element);
            return acc;
        }, []);
    }
    /**
     * Calc child rows sizes/offsets
     */
    calcElementsDimensions() {
        this._childRowElements.forEach((el, index) => {
            const dims = el.target.getBoundingClientRect();
            el.top = dims.top;
            el.height = dims.height;
            el.center = dims.top + (dims.height / 2);
            el.index = index;
        });
    }
    /**
     * Init draggable element
     */
    initDraggableElement() {
        var _a;
        const el = this._draggableElement.cloneNode(true);
        const data = this._draggableElement.getBoundingClientRect();
        if (!(this._isMultipleDrag)) {
            el.style.width = `${data.width}px`;
            el.style.left = `${data.left}px`;
            el.style.top = `${data.top}px`;
            el.classList.add('draggable');
            this._containerElement.nativeElement.insertAdjacentElement('afterbegin', el);
            this._draggableElementPreview = el;
            this._draggableElementHeight = data.height;
            this.updateDraggableDims();
        }
        else {
            // Create preview DIV
            this._containerElement.nativeElement.classList.add('drag-hidden');
            const selectedCount = (_a = this._selectedRowsDirectives) === null || _a === void 0 ? void 0 : _a.length;
            const previewBlock = this._renderer.createElement('div');
            previewBlock.style.left = `${data.left}px`;
            previewBlock.style.top = `${data.top}px`;
            const text = this._renderer.createText(`${selectedCount} selected items`);
            this._renderer.appendChild(previewBlock, text);
            this._renderer.addClass(previewBlock, 'fs-list-preview-block');
            this._renderer.appendChild(document.body, previewBlock);
            this._multipleDraggableElementPreview = previewBlock;
        }
    }
    /**
     * Looking by stored row elements for overlapped row
     *
     * @param event
     */
    lookupElementUnder(event) {
        const top = event.y || event.clientY;
        const bottom = event.y || event.clientY;
        let elemIndex = null;
        for (let i = 0; i < this._childRowElements.length; i++) {
            const el = this._childRowElements[i];
            if (!el.active) {
                if (top < el.center + (el.height / 2) && el.index < this._draggableElementIndex
                    || bottom > el.center - (el.height / 2) && el.index > this._draggableElementIndex) {
                    elemIndex = i;
                }
            }
        }
        return elemIndex;
    }
    /**
     * Swap rows
     *
     * @param index
     */
    swapWithIndex(index) {
        const activeIndex = this._draggableElementIndex;
        const selectedRows = this._selectedRowsDirectives
            .map((d) => d.row)
            .filter((d) => d.readyToSwap);
        // Swap rows in global rows stack
        this._reorderController
            .dataController
            .swapRows(this._rows[activeIndex], this._rows[index], selectedRows, this._isMultipleDrag);
        // Swap visible rows
        if (!this._isMultipleDrag) {
            const activeRow = this._rows[activeIndex];
            this._rows[activeIndex] = this._rows[index];
            this._rows[index] = activeRow;
            const activeElement = this._childRowElements[activeIndex].target;
            this._childRowElements[activeIndex].active = false;
            this._childRowElements[activeIndex].target = this._childRowElements[index].target;
            this._childRowElements[index].target = activeElement;
            this._childRowElements[index].active = true;
        }
        this._draggableElementIndex = index;
        this._cdRef.detectChanges();
    }
    /**
     * Update cell width for draggable elem
     */
    updateDraggableDims() {
        const draggableCells = Array.from(this._draggableElementPreview.querySelectorAll('td'));
        Array.from(this._draggableElementPreview.querySelectorAll('td')).forEach((elem, index) => {
            const dims = elem.getBoundingClientRect();
            draggableCells[index].style.width = `${dims.width}px`;
        });
    }
    _waitUntilIsNotDone(doneResult) {
        if (doneResult instanceof Observable) {
            this._reorderController.disableReorderAction();
            doneResult
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                this._reorderController.enableReorderAction();
            });
        }
    }
    _detectSelectedRows() {
        this._draggableChildrenDirectives
            .forEach((dir) => {
            var _a;
            const isRowSelected = (_a = this._reorderController.selectionController) === null || _a === void 0 ? void 0 : _a.isRowSelected(dir.row.data);
            if (isRowSelected && !dir.row.isGroup) {
                this._selectedRowsDirectives.push(dir);
            }
        });
    }
    /**
     * Fix background when mobile
     *
     * @param e
     */
    touchFix(e) {
        if (!('clientX' in e) && !('clientY' in e)) {
            const touches = e.touches || e.originalEvent.touches;
            if (touches && touches.length) {
                e.clientX = touches[0].clientX;
                e.clientY = touches[0].clientY;
            }
            e.preventDefault();
        }
    }
}
FsListDraggableListDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListDraggableListDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i0.ElementRef }, { token: ReorderController }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
FsListDraggableListDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListDraggableListDirective, selector: "[fsListDraggableList]", inputs: { _rows: ["rows", "_rows"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListDraggableListDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsListDraggableList]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i0.ElementRef }, { type: ReorderController }, { type: i0.Renderer2 }]; }, propDecorators: { _rows: [{
                type: Input,
                args: ['rows']
            }] } });

var RowType;
(function (RowType) {
    RowType["Simple"] = "simple";
    RowType["Group"] = "group";
    RowType["GroupFooter"] = "groupFooter";
    RowType["GroupChild"] = "groupChild";
})(RowType || (RowType = {}));

class BaseRow {
    constructor(data = {}, rowType = RowType.Simple) {
        this.readyToSwap = true;
        this._data = {};
        this._data = data;
        this._rowType = rowType;
    }
    get data() {
        return this._data;
    }
    get type() {
        return this._rowType;
    }
    getReorderData() {
        return {
            type: this._rowType,
            data: this.data,
            parent: null,
        };
    }
}

class ChildRow extends BaseRow {
    constructor(data = {}, parent = null) {
        super(data, RowType.GroupChild);
        this.visible = true;
        this._parent = parent;
        if (this.parent) {
            this.visible = this._parent.expanded;
            this.index = this.parent.children.length;
        }
    }
    get parent() {
        return this._parent;
    }
    get isChild() {
        return true;
    }
    getReorderData() {
        return {
            type: this._rowType,
            data: this.data,
            parent: this.parent.getReorderData(),
        };
    }
    destroy() { }
}

class GroupFooterRow extends BaseRow {
    constructor(data = {}, parent = null) {
        super(data, RowType.GroupFooter);
        this.visible = true;
        this._parent = parent;
        if (this.parent) {
            this.visible = this._parent.expanded;
            this.index = this.parent.children.length;
        }
    }
    get parent() {
        return this._parent;
    }
    get isGroupFooter() {
        return true;
    }
    destroy() { }
}

class GroupRow extends BaseRow {
    constructor(data = {}, initialExpand = false) {
        super(data, RowType.Group);
        this.children = [];
        this._expanded = new BehaviorSubject(false);
        if (initialExpand) {
            this._expanded.next(initialExpand);
        }
    }
    get childrenData() {
        return this.children
            .map((child) => child.data);
    }
    get isGroup() {
        return true;
    }
    get expanded() {
        return this._expanded.getValue();
    }
    get expanded$() {
        return this._expanded.asObservable();
    }
    updateChildrenIndexes() {
        this.children.forEach((row, index) => {
            row.index = index;
        });
    }
    updateChildrenVisibility() {
        this.children.forEach((row) => {
            row.visible = this.expanded;
        });
    }
    toggleRowExpandStatus() {
        this._expanded.next(!this.expanded);
        this.updateChildrenVisibility();
    }
    destroy() {
        this.children.forEach((child) => child.destroy());
        this._expanded.complete();
    }
}

class SimpleRow extends BaseRow {
    constructor(data = {}) {
        super(data, RowType.Simple);
    }
    destroy() {
    }
}

class Row {
    constructor(data = {}, rowType = RowType.Simple, { parent, initialExpand } = {}) {
        this._actionsUpdate$ = new Subject();
        switch (rowType) {
            case RowType.Simple:
                {
                    this._row = new SimpleRow(data);
                }
                break;
            case RowType.Group:
                {
                    this._row = new GroupRow(data, initialExpand);
                }
                break;
            case RowType.GroupChild:
                {
                    this._row = new ChildRow(data, parent);
                }
                break;
        }
    }
    get index() {
        return this._row.index;
    }
    get actionsUpdate$() {
        return this._actionsUpdate$.asObservable();
    }
    actionsUpdate() {
        return this._actionsUpdate$.next();
    }
    set index(value) {
        this._row.index = value;
    }
    get readyToSwap() {
        return this._row.readyToSwap;
    }
    get visible() {
        return this._row.visible;
    }
    set readyToSwap(value) {
        this._row.readyToSwap = value;
    }
    get data() {
        return this._row.data;
    }
    get type() {
        return this._row.type;
    }
    get isGroup() {
        return this._row instanceof GroupRow;
    }
    get isGroupChild() {
        return this._row instanceof ChildRow;
    }
    get isGroupFooter() {
        return this._row instanceof GroupFooterRow;
    }
    get parent() {
        return this._row.parent;
    }
    get children() {
        return this._row.children;
    }
    get expanded() {
        return this._row.expanded;
    }
    get expanded$() {
        return this._row.expanded$;
    }
    updateChildrenIndexes() {
        this._row.updateChildrenIndexes();
    }
    toggleRowExpandStatus() {
        this._row.toggleRowExpandStatus();
    }
    getReorderData() {
        return this._row.getReorderData();
    }
    destroy() {
        this._row.destroy();
    }
}

var ActionType;
(function (ActionType) {
    ActionType["Basic"] = "basic";
    ActionType["Raised"] = "raised";
    ActionType["Icon"] = "icon";
    ActionType["Fab"] = "fab";
    ActionType["MiniFab"] = "mini-fab";
})(ActionType || (ActionType = {}));

class RowAction {
    constructor(config = {}) {
        this.label = '';
        this.classArray = [];
        this.isShown = true;
        this._isGroup = false;
        this._init(config);
        if (Array.isArray(this.rowActions)) {
            this._isGroup = true;
        }
    }
    get isGroup() {
        return this._isGroup;
    }
    _init(value) {
        var _a, _b, _c;
        this.icon = value.icon;
        this.menu = (_a = value.menu) !== null && _a !== void 0 ? _a : true;
        this.remove = value.remove;
        this.className = value.className;
        this.type = this.menu && this.icon && !this.label && !value.type ? ActionType.Icon : (_b = value.type) !== null && _b !== void 0 ? _b : ActionType.Basic;
        this.show = value.show;
        this.restore = value.restore;
        this.rowActions = (_c = value.rowActions) === null || _c === void 0 ? void 0 : _c.map((action) => new RowAction(action));
        // Re-assign click function
        const clickFn = value.click;
        this.click = (row, event, index, rowActionsRef = null) => {
            return this._clickEvent(row, event, index, rowActionsRef, clickFn);
        };
        this._linkFn = value.link;
        if (value.file) {
            this.fileConfig = Object.assign({}, value.file);
            if (this.fileConfig.select) {
                const selectFn = this.fileConfig.select;
                this.fileConfig.select = (selection, row, index) => {
                    selectFn(selection, row, index);
                };
            }
        }
        this.label = value.label;
        if (this.className) {
            this.classArray = this.className.split(' ').reduce((acc, elem) => {
                acc.push(elem);
                return acc;
            }, []);
        }
    }
    checkShowStatus(row, index) {
        if (this.isGroup) {
            this.rowActions.forEach((action) => {
                action.checkShowStatus(row, index);
            });
            const groupVisible = !this.show || this.show(row, index);
            this.isShown = groupVisible && this.rowActions.some((action) => action.isShown);
        }
        else if (this.show) {
            this.isShown = this.show(row, index);
        }
    }
    updateLink(row) {
        if (!this.isShown) {
            return;
        }
        if (this.isGroup) {
            this.rowActions.forEach((action) => {
                action.updateLink(row);
            });
        }
        else if (this._linkFn) {
            this.routerLink = this._linkFn(row);
            if (this.routerLink && !this.routerLink.target) {
                this.routerLink.target = null;
            }
        }
    }
    _clickEvent(row, event, index, rowActionsRef, clickFn) {
        // Stop event propagation for parent
        event.stopPropagation();
        // Close menu
        if (rowActionsRef) {
            rowActionsRef.closeMenu();
        }
        if (clickFn) {
            // Fire passed callback
            return clickFn(row, event, index);
        }
    }
}

class StyleConfig {
    constructor(config = {}) {
        this.className = []; // Can't be used in tempaltes!
        this.classesArray = []; // Can be used in tempaltes
        this.classesString = '';
        this._init(config);
    }
    /**
     * Create static array of styles for using in templates
     */
    updateClasesArray() {
        this.classesArray = [].concat(this.className, this.align);
        this.classesString = [].concat(this.className, this.align).join(' ');
    }
    /**
     * Prioritized merge for align options
     * @param prior1
     * @param prior2
     */
    mergeAlignByPriority(prior1, prior2) {
        let targetValue = this.align;
        if (targetValue === void 0) {
            if (prior1 && prior1.align !== void 0) {
                targetValue = prior1.align;
            }
            else if (prior2 && prior2.align !== void 0) {
                targetValue = prior2.align;
            }
            else {
                targetValue = 'left';
            }
        }
        this.align = targetValue;
    }
    /**
     * Prioritized merge for class options
     * @param prior1
     * @param prior2
     */
    mergeClassByPriority(prior1, prior2) {
        let targetValue = [];
        if (Array.isArray(this.className)) {
            targetValue = targetValue.concat(this.className);
        }
        else if (isString(this.className)) {
            targetValue.push(this.className);
        }
        if (prior1 && prior1.className !== void 0) {
            this.mergeAnythingIntoArray(targetValue, prior1.className);
        }
        else if (prior2 && prior2.className !== void 0) {
            this.mergeAnythingIntoArray(targetValue, prior2.className);
        }
        this.className = targetValue;
    }
    _init(config) {
        this.colspan = config.colspan;
        this.align = config.align;
        this.className = config.className;
    }
    /**
     * Merge params into array
     * @param to
     * @param from
     */
    mergeAnythingIntoArray(to, from) {
        if (isString(from)) {
            to.push(from);
        }
        else if (Array.isArray(from)) {
            to.push(...from);
        }
    }
}

var SortingDirection;
(function (SortingDirection) {
    SortingDirection["asc"] = "asc";
    SortingDirection["desc"] = "desc";
})(SortingDirection || (SortingDirection = {}));
const ALLOWED_DEFAULTS = [
    'title',
    'sortable',
    'align',
    'class',
];
class Column {
    constructor(colConfig, colDefaults = false) {
        this.headerConfigs = new StyleConfig();
        this.groupHeaderConfigs = new StyleConfig();
        this.groupFooterConfigs = new StyleConfig();
        this.cellConfigs = new StyleConfig();
        this.footerConfigs = new StyleConfig();
        this.headerColspanned = false;
        this.groupHeaderColspanned = false;
        this.groupFooterColspanned = false;
        this.cellColspanned = false;
        this.footerColspanned = false;
        this._ordered = false;
        this._parseConfig(colConfig);
        this.colStyles = new StyleConfig({
            className: this._attributes.className,
            align: this._attributes.align,
        });
        this.mergeWithColumnDefaults(colDefaults);
    }
    set title(value) {
        this._attributes.title = value;
    }
    get title() {
        return this._attributes.title;
    }
    get name() {
        return this._attributes.name;
    }
    get customize() {
        return this._attributes.customize;
    }
    get width() {
        return this._attributes.width;
    }
    set sortable(value) {
        this._attributes.sortable = value;
    }
    get sortable() {
        return this._attributes.sortable;
    }
    get sortableDefault() {
        return this._attributes.sortableDefault;
    }
    set sortingDirection(value) {
        this._attributes.direction = value;
    }
    get sortingDirection() {
        return this._attributes.direction;
    }
    get sortingDirection$() {
        return this._attributes.direction$;
    }
    get visible() {
        return this._attributes.visible;
    }
    get visible$() {
        return this._attributes.visible$;
    }
    get direction() {
        return (this.sortingDirection === SortingDirection.asc) ? 'asc' : 'desc';
    }
    get fullNameDirection() {
        return (this.sortingDirection === SortingDirection.asc) ? 'Ascending' : 'Descending';
    }
    get ordered() {
        return this._ordered;
    }
    set ordered(value) {
        this._ordered = value;
        if (value && !this.sortingDirection) {
            this.sortingDirection = SortingDirection.asc;
        }
    }
    /**
     * Merge with defaults with existing config
     *
     * @param defaults
     */
    mergeWithColumnDefaults(defaults) {
        if (!isObject(defaults)) {
            defaults = {};
        }
        ALLOWED_DEFAULTS.forEach((key) => {
            switch (key) {
                case 'title':
                    {
                        this.title = this.title || defaults.title;
                    }
                    break;
                case 'sortable':
                    {
                        if (isBoolean(defaults.sortable)) {
                            if (this.sortable === void 0) {
                                this.sortable = defaults.sortable;
                            }
                        }
                    }
                    break;
                case 'class':
                    {
                        this.headerConfigs.mergeClassByPriority(this.colStyles, defaults.header);
                        this.groupHeaderConfigs.mergeClassByPriority(this.colStyles, defaults.cell);
                        this.groupFooterConfigs.mergeClassByPriority(this.colStyles, defaults.cell);
                        this.cellConfigs.mergeClassByPriority(this.colStyles, defaults.cell);
                        this.footerConfigs.mergeClassByPriority(this.colStyles, defaults.footer);
                    }
                    break;
                case 'align':
                    {
                        this.headerConfigs.mergeAlignByPriority(this.colStyles, defaults.header);
                        this.groupHeaderConfigs.mergeAlignByPriority(this.colStyles, defaults.cell);
                        this.groupFooterConfigs.mergeAlignByPriority(this.colStyles, defaults.cell);
                        this.cellConfigs.mergeAlignByPriority(this.colStyles, defaults.cell);
                        this.footerConfigs.mergeAlignByPriority(this.colStyles, defaults.footer);
                    }
                    break;
            }
        });
        this.headerConfigs.updateClasesArray();
        this.groupHeaderConfigs.updateClasesArray();
        this.groupFooterConfigs.updateClasesArray();
        this.cellConfigs.updateClasesArray();
        this.footerConfigs.updateClasesArray();
    }
    /**
     * Change sorting direction
     */
    changeDirection() {
        this.sortingDirection = this.sortingDirection === SortingDirection.asc ? SortingDirection.desc : SortingDirection.asc;
    }
    updateVisibility(value) {
        this._attributes.visible = value;
    }
    _parseConfig(config) {
        this._attributes = config.attributes;
        this.headerTemplate = config.headerTemplate;
        this.groupHeaderTemplate = config.groupHeaderTemplate;
        this.groupFooterTemplate = config.groupFooterTemplate;
        this.cellTemplate = config.cellTemplate;
        this.footerTemplate = config.footerTemplate;
        this.headerConfigs = new StyleConfig(config.headerConfigs);
        this.groupHeaderConfigs = new StyleConfig(config.groupHeaderConfigs);
        this.groupFooterConfigs = new StyleConfig(config.groupFooterConfigs);
        this.cellConfigs = new StyleConfig(config.cellConfigs);
        this.footerConfigs = new StyleConfig(config.footerConfigs);
        this.expandTrigger = config.expandTrigger;
    }
}

class FsCellComponent {
    constructor() {
        this.isColl = true;
        this.role = 'gridcell';
        this.cellContext = {};
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._listenGroupOpen();
    }
    ngDoCheck() {
        // TODO fixme remove or improve
        if (this.row) {
            this.cellContext.groupIndex = this.row.index;
        }
    }
    ngOnChanges(changes) {
        var _a, _b, _c, _d;
        if (((_a = changes.rowIndex) === null || _a === void 0 ? void 0 : _a.currentValue) !== ((_b = changes.rowIndex) === null || _b === void 0 ? void 0 : _b.previousValue)) {
            this.cellContext.index = this.rowIndex;
            this.cellContext.groupIndex = this.row.index;
        }
        if (((_c = changes.column) === null || _c === void 0 ? void 0 : _c.currentValue) !== ((_d = changes.column) === null || _d === void 0 ? void 0 : _d.previousValue)) {
            this._initCellContext();
            this._initCellTemplate();
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _initCellContext() {
        this.cellContext.index = this.rowIndex;
        if (this.row) {
            if (this.row.isGroup) {
                this.cellContext.groupIndex = this.row.index;
            }
            else if (this.row.isChild || this.row.isGroupFooter) {
                this.cellContext.groupIndex = this.row.index;
                this.cellContext.groupRow = this.row.parent.data;
                this.cellContext.group = this.row.parent.data;
            }
            if (this.row.isGroup) {
                this.cellContext.group = this.row.data;
                this.cellContext.groupChildren = this.row.children
                    .map((child) => child.data);
            }
            else if (this.row.isGroupFooter) {
                this.cellContext.group = this.row.parent.data;
                this.cellContext.groupIndex = this.row.index;
                this.cellContext.groupChildren = this.row.parent.children
                    .map((child) => child.data);
            }
        }
        this.cellContext.column = this.column;
        if (this.row) {
            this.cellContext.$implicit = this.row.data;
            this.cellContext.row = this.row.data;
            this.cellContext.value = this.row.data[this.column.name];
            this.cellContext.expanded = this.row.expanded;
        }
    }
    _initCellTemplate() {
        var _a, _b;
        if ((_a = this.row) === null || _a === void 0 ? void 0 : _a.isGroup) {
            this.cellTemplate = this.column.groupHeaderTemplate || this.column.cellTemplate;
        }
        else if ((_b = this.row) === null || _b === void 0 ? void 0 : _b.isGroupFooter) {
            this.cellTemplate = this.column.groupFooterTemplate || this.column.cellTemplate;
        }
        else {
            this.cellTemplate = this.column.cellTemplate;
        }
    }
    _listenGroupOpen() {
        if (this.row && this.row.isGroup) {
            this.row.expanded$
                .pipe(takeUntil(this._destroy$))
                .subscribe((status) => {
                this.cellContext.expanded = status;
            });
        }
    }
}
FsCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsCellComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsCellComponent, selector: "[fs-cell]", inputs: { column: "column", row: "row", rowIndex: "rowIndex" }, host: { properties: { "class.fs-list-col": "this.isColl", "attr.role": "this.role" } }, usesOnChanges: true, ngImport: i0, template: "<ng-template [ngTemplateOutlet]=\"cellTemplate || defaultCellTemplate\" [ngTemplateOutletContext]=\"cellContext\"></ng-template>\n\n<ng-template #defaultCellTemplate let-value=\"value\">\n  {{value}}\n</ng-template>\n", directives: [{ type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsCellComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-cell]',
                    templateUrl: './cell.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { isColl: [{
                type: HostBinding,
                args: ['class.fs-list-col']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], column: [{
                type: Input
            }], row: [{
                type: Input
            }], rowIndex: [{
                type: Input
            }] } });

class FsRowInlineActionComponent {
    constructor() {
        this.clicked = new EventEmitter();
        this.fileSelect = new EventEmitter();
    }
    actionClick(event) {
        this.clicked.emit(event);
    }
    fileSelected(event) {
        this.fileSelect.emit(event);
    }
}
FsRowInlineActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowInlineActionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsRowInlineActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsRowInlineActionComponent, selector: "fs-list-row-inline-action", inputs: { action: "action" }, outputs: { clicked: "clicked", fileSelect: "fileSelect" }, ngImport: i0, template: "<ng-container *ngIf=\"action.isShown\" [ngSwitch]=\"action.type\">\n  <!-- Basic button -->\n  <ng-container *ngSwitchCase=\"'basic'\">\n    <ng-container *ngIf=\"!action.routerLink; else link\">\n      <button type=\"button\"\n              mat-button\n              (click)=\"actionClick($event)\"\n              [ngClass]=\"action.classArray\"\n      >\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </button>\n    </ng-container>\n    <ng-template #link>\n      <a mat-button\n         [routerLink]=\"action.routerLink.link\"\n         [queryParams]=\"action.routerLink.queryParams\">\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </a>\n    </ng-template>\n  </ng-container>\n\n  <!-- Raised button -->\n  <ng-container *ngSwitchCase=\"'raised'\">\n    <ng-container *ngIf=\"!action.routerLink; else link\">\n      <button type=\"button\"\n              mat-raised-button\n              (click)=\"actionClick($event)\"\n              [ngClass]=\"action.classArray\"\n      >\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </button>\n    </ng-container>\n    <ng-template #link>\n      <a mat-raised-button\n         [routerLink]=\"action.routerLink.link\"\n         [queryParams]=\"action.routerLink.queryParams\">\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </a>\n    </ng-template>\n  </ng-container>\n\n  <!-- Icon button -->\n  <ng-container *ngSwitchCase=\"'icon'\">\n    <ng-container *ngIf=\"!action.routerLink; else link\">\n      <button type=\"button\"\n              mat-icon-button\n              (click)=\"actionClick($event)\"\n              [ngClass]=\"action.classArray\"\n      >\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </button>\n    </ng-container>\n    <ng-template #link>\n      <a mat-icon-button\n         [routerLink]=\"action.routerLink.link\"\n         [queryParams]=\"action.routerLink.queryParams\">\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </a>\n    </ng-template>\n  </ng-container>\n\n  <!-- Fab button -->\n  <ng-container *ngSwitchCase=\"'fab'\">\n    <ng-container *ngIf=\"!action.routerLink; else link\">\n      <button type=\"button\"\n              mat-fab\n              (click)=\"actionClick($event)\"\n              [ngClass]=\"action.classArray\"\n      >\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </button>\n    </ng-container>\n    <ng-template #link>\n      <a mat-fab\n         [routerLink]=\"action.routerLink.link\"\n         [queryParams]=\"action.routerLink.queryParams\">\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </a>\n    </ng-template>\n  </ng-container>\n\n  <!-- Mini Fab button -->\n  <ng-container *ngSwitchCase=\"'mini-fab'\">\n    <ng-container *ngIf=\"!action.routerLink; else link\">\n      <button type=\"button\"\n              mat-mini-fab\n              (click)=\"actionClick($event)\"\n              [ngClass]=\"action.classArray\"\n      >\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </button>\n    </ng-container>\n    <ng-template #link>\n      <a mat-mini-fab\n         [routerLink]=\"action.routerLink.link\"\n         [queryParams]=\"action.routerLink.queryParams\">\n        <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n      </a>\n    </ng-template>\n  </ng-container>\n\n  <ng-template #buttonContent>\n    <ng-container *ngIf=\"!action.fileConfig else uploadFileBtn\">\n      <mat-icon *ngIf=\"action.icon\">{{action.icon}}</mat-icon>\n      {{action.label}}\n    </ng-container>\n\n    <ng-template #uploadFileBtn>\n      <fs-file\n        class=\"action-button\"\n        [accept]=\"action.fileConfig.accept || '*'\"\n        [multiple]=\"action.fileConfig.multiple\"\n        [minWidth]=\"action.fileConfig.minWidth\"\n        [minHeight]=\"action.fileConfig.minHeight\"\n        [imageWidth]=\"action.fileConfig.maxWidth\"\n        [imageHeight]=\"action.fileConfig.maxHeight\"\n        (select)=\"fileSelected($event)\"\n        (error)=\"action.fileConfig.error && action.fileConfig.error($event)\">\n        <mat-icon *ngIf=\"action.icon\">{{action.icon}}</mat-icon>\n        {{action.label}}\n      </fs-file>\n    </ng-template>\n  </ng-template>\n</ng-container>\n", components: [{ type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i1.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i3$1.FsFileComponent, selector: "fs-file", inputs: ["minHeight", "minWidth", "orientate", "multiple", "capture", "allowClick", "allowDrop", "accept", "disabled", "imageWidth", "imageHeight", "imageQuality"], outputs: ["select", "selectPreviews", "error", "clicked", "declined"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { type: i3.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i7.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["routerLink", "target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowInlineActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-row-inline-action',
                    templateUrl: './inline-action.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { action: [{
                type: Input
            }], clicked: [{
                type: Output
            }], fileSelect: [{
                type: Output
            }] } });

class ActionLabelPipe {
    transform(label, row) {
        if (typeof label === 'function') {
            return label(row.data);
        }
        return label;
    }
}
ActionLabelPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ActionLabelPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ActionLabelPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ActionLabelPipe, name: "actionLabel" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: ActionLabelPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'actionLabel' }]
        }] });

class FsRowMenuActionComponent {
    constructor(_menu) {
        this._menu = _menu;
        this.fileSelect = new EventEmitter();
        this.fileError = new EventEmitter();
    }
    selectFile(event) {
        this.fileSelect.emit(event);
        this.closeMenu();
    }
    errorFile(event) {
        this.fileError.emit(event);
        this.closeMenu();
    }
    closeMenu() {
        this._menu.closeMenu();
    }
}
FsRowMenuActionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowMenuActionComponent, deps: [{ token: i2$1.FsMenuComponent }], target: i0.ɵɵFactoryTarget.Component });
FsRowMenuActionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsRowMenuActionComponent, selector: "fs-list-row-menu-action", inputs: { row: "row", icon: "icon", label: "label", file: "file" }, outputs: { fileSelect: "fileSelect", fileError: "fileError" }, ngImport: i0, template: "<ng-container *ngIf=\"!file else withFile\">\n  <mat-icon *ngIf=\"icon\">{{icon}}</mat-icon>\n  {{label| actionLabel:row}}\n</ng-container>\n\n<ng-template #withFile>\n  <fs-file\n      class=\"action-button\"\n      [accept]=\"file.accept || '*'\"\n      [multiple]=\"file.multiple\"\n      [minWidth]=\"file.minWidth\"\n      [minHeight]=\"file.minHeight\"\n      [imageWidth]=\"file.maxWidth\"\n      [imageHeight]=\"file.maxHeight\"\n      (click)=\"$event.stopPropagation();\"\n      (select)=\"selectFile($event)\"\n      (error)=\"errorFile($event)\"\n      (cancel)=\"closeMenu()\">\n    <mat-icon *ngIf=\"icon\">{{icon}}</mat-icon>\n    {{label| actionLabel:row}}\n  </fs-file>\n</ng-template>\n", components: [{ type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i3$1.FsFileComponent, selector: "fs-file", inputs: ["minHeight", "minWidth", "orientate", "multiple", "capture", "allowClick", "allowDrop", "accept", "disabled", "imageWidth", "imageHeight", "imageQuality"], outputs: ["select", "selectPreviews", "error", "clicked", "declined"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "actionLabel": ActionLabelPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowMenuActionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-row-menu-action',
                    templateUrl: './menu-action.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i2$1.FsMenuComponent }]; }, propDecorators: { row: [{
                type: Input
            }], icon: [{
                type: Input
            }], label: [{
                type: Input
            }], file: [{
                type: Input
            }], fileSelect: [{
                type: Output
            }], fileError: [{
                type: Output
            }] } });

class FsRowActionsComponent {
    constructor(_prompt) {
        this._prompt = _prompt;
        this.restoreMode = false;
        this.rowActions = [];
        this.menuRowActions = [];
        this.inlineRowActions = [];
        this._destroy$ = new Subject();
    }
    actionClick(action, row, event, menuRef) {
        if (action.remove) {
            if (typeof action.remove === 'boolean') {
                this._removeAction(action, row.data, event, this.index);
            }
            else {
                this._prompt.confirm({
                    title: action.remove.title,
                    template: action.remove.template,
                    autofocus: true,
                })
                    .pipe(take(1), takeUntil(this._destroy$))
                    .subscribe(() => {
                    this._removeAction(action, row.data, event, this.index);
                });
            }
        }
        else {
            action.click(row.data, event, this.index, menuRef);
        }
    }
    /**
     * Track By for improve change detection
     *
     * @param index
     */
    trackByFn(index) {
        return index;
    }
    /**
     * Emit that some row must be removed
     *
     * @param action
     * @param row
     * @param event
     * @param index
     */
    _removeAction(action, row, event, index) {
        const removeObservable = action.click(row, event, index);
        if (removeObservable && removeObservable instanceof Observable) {
            removeObservable
                .pipe(take(1), takeUntil(this._destroy$))
                .subscribe(() => {
                this.rowRemoved.emit(row);
            });
        }
        else {
            this.rowRemoved.emit(row);
        }
    }
}
FsRowActionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowActionsComponent, deps: [{ token: i1$1.FsPrompt }], target: i0.ɵɵFactoryTarget.Component });
FsRowActionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsRowActionsComponent, selector: "fs-list-row-actions", inputs: { row: "row", index: "index", restoreMode: "restoreMode", rowActions: "rowActions", rowRemoved: "rowRemoved", menuRowActions: "menuRowActions", inlineRowActions: "inlineRowActions", restoreAction: "restoreAction" }, ngImport: i0, template: "<ng-container *ngIf=\"!restoreMode || (restoreMode && !restoreAction)\">\n  <ng-container *ngFor=\"let action of inlineRowActions; trackBy: trackByFn\">\n    <fs-list-row-inline-action \n      class=\"row-inline-action row-inline-action-{{action.type}}\"\n      [action]=\"action\"\n      [ngClass]=\"{'mobile-hide': action.menu === undefined}\"\n      (clicked)=\"actionClick(action, row, $event)\"\n      (fileSelect)=\"action.fileConfig.select($event, row, index)\">\n    </fs-list-row-inline-action>\n  </ng-container>\n</ng-container>\n\n<!-- Menu -->\n<fs-menu \n    class=\"row-menu-action\"\n    *ngIf=\"menuRowActions.length || (restoreMode && restoreAction)\"\n    #menuRef>\n  <!-- Case when we have usual menu actions -->\n  <ng-container *ngIf=\"!restoreMode || (restoreMode && !restoreAction)\">\n    <ng-container *ngFor=\"let action of inlineRowActions; trackBy: trackByFn\">\n      <ng-template \n          class=\"hidden-mobile-menu-action\"\n          fs-menu-item\n          [link]=\"action.routerLink?.link\"\n          [queryParams]=\"action.routerLink?.queryParams\"\n          [target]=\"action.routerLink?.target\"\n          [hidden]=\"action.menu !== undefined && !action.isShown\">\n        <fs-list-row-menu-action\n          [icon]=\"action.icon\"\n          [label]=\"action.label\"\n          [row]=\"row\"\n          [file]=\"action.fileConfig\"\n          (fileSelect)=\"action.fileConfig.select($event, row, index)\"\n          (fileError)=\"action.fileConfig.error && action.fileConfig.error($event)\">\n        </fs-list-row-menu-action>\n      </ng-template>\n    </ng-container>\n\n    <ng-container *ngFor=\"let action of menuRowActions; trackBy: trackByFn\">\n      <ng-container *ngIf=\"action.isGroup else simpleMenuItem\">\n        <fs-menu-group [label]=\"action.label | actionLabel:row\">\n          <ng-container *ngFor=\"let subAction of action.rowActions\">\n            <ng-template \n                fs-menu-item\n                [link]=\"subAction.routerLink?.link\"\n                [queryParams]=\"subAction.routerLink?.queryParams\"\n                [target]=\"subAction.routerLink?.target\"\n                [hidden]=\"!subAction.isShown\"\n                (click)=\"actionClick(subAction, row, $event, menuRef);\">\n              <fs-list-row-menu-action\n                [icon]=\"subAction.icon\"\n                [label]=\"subAction.label\"\n                [row]=\"row\"\n                [file]=\"subAction.fileConfig\"\n                (fileSelect)=\"subAction.fileConfig.select($event, row, index)\"\n                (fileError)=\"subAction.fileConfig.error && subAction.fileConfig.error($event)\">\n              </fs-list-row-menu-action>\n            </ng-template>\n          </ng-container>\n        </fs-menu-group>\n      </ng-container>\n      <ng-template #simpleMenuItem>\n        <ng-template \n            fs-menu-item\n            [link]=\"action.routerLink?.link\"\n            [queryParams]=\"action.routerLink?.queryParams\"\n            [target]=\"action.routerLink?.target\"\n            [hidden]=\"!action.isShown\"\n            (click)=\"actionClick(action, row, $event, menuRef);\">\n          <fs-list-row-menu-action\n            [icon]=\"action.icon\"\n            [label]=\"action.label\"\n            [row]=\"row\"\n            [file]=\"action.fileConfig\"\n            (fileSelect)=\"action.fileConfig.select($event, row, index)\"\n            (fileError)=\"action.fileConfig.error && action.fileConfig.error($event)\">\n          </fs-list-row-menu-action>\n        </ng-template>\n      </ng-template>\n    </ng-container>\n  </ng-container>\n\n  <!-- Case when we have restore mode enabled and must hide menu actions -->\n  <ng-container *ngIf=\"restoreMode && restoreAction\">\n    <ng-template fs-menu-item (click)=\"actionClick(restoreAction, row, $event, menuRef)\">\n      <mat-icon *ngIf=\"restoreAction.icon\">{{restoreAction.icon}}</mat-icon>\n      {{restoreAction.label | actionLabel:row}}\n    </ng-template>\n  </ng-container>\n</fs-menu>\n", styles: [":host ::ng-deep fs-list-row-inline-action{margin-left:5px}:host ::ng-deep fs-list-row-inline-action:first-child{margin-left:0}::ng-deep .hidden-mobile-menu-action{display:none!important}\n"], components: [{ type: FsRowInlineActionComponent, selector: "fs-list-row-inline-action", inputs: ["action"], outputs: ["clicked", "fileSelect"] }, { type: i2$1.FsMenuComponent, selector: "fs-menu", inputs: ["class", "buttonClass", "buttonType", "buttonColor"], outputs: ["opened", "closed"] }, { type: FsRowMenuActionComponent, selector: "fs-list-row-menu-action", inputs: ["row", "icon", "label", "file"], outputs: ["fileSelect", "fileError"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2$1.FsMenuItemDirective, selector: "fs-menu-group,[fs-menu-item]" }], pipes: { "actionLabel": ActionLabelPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-row-actions',
                    templateUrl: './actions.component.html',
                    styleUrls: ['./actions.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1$1.FsPrompt }]; }, propDecorators: { row: [{
                type: Input
            }], index: [{
                type: Input
            }], restoreMode: [{
                type: Input
            }], rowActions: [{
                type: Input
            }], rowRemoved: [{
                type: Input
            }], menuRowActions: [{
                type: Input
            }], inlineRowActions: [{
                type: Input
            }], restoreAction: [{
                type: Input
            }] } });

class FsRowComponent {
    constructor(el, _cdRef, _differs, _renderer, _draggableList) {
        this.el = el;
        this._cdRef = _cdRef;
        this._differs = _differs;
        this._renderer = _renderer;
        this._draggableList = _draggableList;
        this.role = 'row';
        this.rowActionsRaw = [];
        this.groupActionsRaw = [];
        this.hasRowActions = false;
        this.rowEvents = {};
        this.restoreMode = false;
        this.ReorderPosition = ReorderPosition;
        this.ReorderStrategy = ReorderStrategy;
        this.rowActions = [];
        this.menuRowActions = [];
        this.inlineRowActions = [];
        this.selected = false;
        this.indeterminateSelected = false;
        this._eventListeners = [];
        this._destroy$ = new Subject();
        this._rowDiffer = _differs.find({}).create();
    }
    get isGroupRow() {
        return this.row.isGroup;
    }
    get isGroupChildRow() {
        return this.row.isGroupChild;
    }
    get isGroupFooterRow() {
        return this.row.isGroupFooter;
    }
    get isDragDisabled() {
        return !this.selected && this.reorderMultiple && !!this.selection.selectedRows.size;
    }
    get isMultipleSelection() {
        const multiple = this.reorderMultiple;
        return multiple && this.selected;
    }
    get rowCssClass() {
        var _a, _b, _c;
        let classes = ['fs-list-row'];
        if (this.rowIndex % 2 !== 0) {
            classes.push('fs-list-row-odd');
        }
        if (this.rowIndex % 2 === 0) {
            classes.push('fs-list-row-even');
        }
        if ((_a = this.row) === null || _a === void 0 ? void 0 : _a.isGroup) {
            classes.push('fs-list-row-group');
        }
        else if ((_b = this.row) === null || _b === void 0 ? void 0 : _b.isChild) { // TODO fix isChild & all
            classes.push('fs-list-row-group-child');
        }
        else if ((_c = this.row) === null || _c === void 0 ? void 0 : _c.isGroupFooter) {
            classes.push('fs-list-row-group-footer');
        }
        else {
            classes.push('fs-list-row-body');
        }
        if (this.rowClass) {
            classes = [
                ...classes,
                ...this._getRowClasses(this.rowClass),
            ];
        }
        return classes.join(' ');
    }
    get dragCellVisible() {
        return !this.row.isGroup;
    }
    get leftDragDropEnabled() {
        return this.reorderEnabled
            && this.reorderPosition === ReorderPosition.Left
            && this.activeFiltersCount === 0;
    }
    get rightDragDropEnabled() {
        return this.reorderEnabled
            && this.reorderPosition === ReorderPosition.Right
            && this.activeFiltersCount === 0;
    }
    ngOnInit() {
        this._initRowEvents();
        this._initSelection();
        if (this.row) {
            this._initRowActionsUpdate();
        }
        if (this.row && this.row.isGroup) {
            if (this.row && this.row.isGroup && this.groupActionsRaw) {
                this.rowActions = this.groupActionsRaw.map((action) => new RowAction(action));
                this._filterActionsByCategories();
            }
        }
        else if (this.rowActionsRaw) {
            this.rowActions = this.rowActionsRaw.map((action) => new RowAction(action));
            this._filterActionsByCategories();
        }
    }
    ngDoCheck() {
        if (this._rowDiffer.diff(this.row)) {
            this.updateRowActions();
        }
    }
    updateRowActions() {
        if (this.rowActions) {
            this.rowActions.forEach((action) => {
                action.checkShowStatus(this.row.data, this.rowIndex);
                action.updateLink(this.row.data);
            });
            this._filterActionsByCategories();
        }
        this._cdRef.markForCheck();
    }
    ngOnDestroy() {
        this._eventListeners.forEach((listener) => {
            listener();
        });
        this._destroy$.next();
        this._destroy$.complete();
    }
    /**
     * Select row by checkbox
     *
     * @param event
     */
    selectRow(event) {
        this.selection.rowSelectionChange(this.row, event.checked);
        this._cdRef.markForCheck();
    }
    /**
     * Track By for improve change detection
     *
     * @param index
     */
    trackByFn(index) {
        return index;
    }
    dragStart(event) {
        if (this.reorderEnabled && !this.isDragDisabled) {
            event.preventDefault();
            event.stopPropagation();
            this._draggableList.dragStart(this.el.nativeElement);
        }
    }
    /**
     * Set event listeners for row
     */
    _initRowEvents() {
        Object.keys(this.rowEvents || {})
            .forEach((event) => {
            if (this.rowEvents.hasOwnProperty(event)) {
                const listener = this._renderer
                    .listen(this.el.nativeElement, event, (evt) => {
                    if (!this.reorderEnabled) {
                        this.rowEvents[event]({
                            event: evt,
                            row: this.row.data,
                            rowIndex: this.rowIndex,
                        });
                    }
                });
                this._eventListeners.push(listener);
            }
        });
    }
    _initRowActionsUpdate() {
        if (this.row.actionsUpdate$) {
            this.row.actionsUpdate$
                .pipe(takeUntil(this._destroy$))
                .subscribe(() => {
                this.updateRowActions();
            });
        }
    }
    _getRowClasses(rowClass) {
        const classes = [];
        const options = {
            index: this.rowIndex,
            type: this.row.type,
        };
        if (this.row.isGroup) {
            options.groupIndex = this.row.index;
        }
        else if (this.row.isChild || this.row.isGroupFooter) { // TODO fix isChild & all
            options.groupIndex = this.row.parent.index;
        }
        const resultClass = rowClass(this.row.data, options);
        if (resultClass) {
            if (typeof resultClass === 'string') {
                classes.push(resultClass);
            }
            else if (typeof resultClass === 'object') {
                const keys = Object.keys(resultClass);
                for (const k of keys) {
                    if (resultClass[k] === true) {
                        classes.push(k);
                    }
                }
            }
        }
        return classes;
    }
    /**
     * Subscribe to selection change events
     */
    _initSelection() {
        if (this.selection) {
            this.selected = this.row && this.selection.isRowSelected(this.row.data);
            this.selection.selectionChange$
                .pipe(
            // // Would like to respond only when checkbox on top is changed
            // // or was clicked "Select All" in selection dialog
            // filter(({type}) => {
            //   return type === SelectionChangeType.AllVisibleSelectionChange
            //     || type === SelectionChangeType.SelectedAll;
            // }),
            takeUntil(this._destroy$))
                .subscribe(() => {
                var _a;
                this.selected = this.row && this.selection.isRowSelected(this.row.data);
                if ((_a = this.row) === null || _a === void 0 ? void 0 : _a.isGroup) {
                    const groupSelection = this.selection.isGroupSelected(this.row);
                    if (groupSelection === 'indeterminate') {
                        this.selected = true;
                        this.indeterminateSelected = true;
                    }
                    else {
                        this.selected = groupSelection;
                        this.indeterminateSelected = false;
                    }
                }
                this._cdRef.markForCheck();
            });
        }
    }
    _filterActionsByCategories() {
        this.menuRowActions = [];
        this.inlineRowActions = [];
        this.restoreAction = null;
        this.rowActions.forEach((action) => {
            if (!action.isShown) {
                return;
            }
            if (action.menu && !action.restore) {
                this.menuRowActions.push(action);
            }
            else if (!action.menu && !action.restore) {
                this.inlineRowActions.push(action);
            }
            else if (action.restore) {
                this.restoreAction = action;
            }
        });
    }
}
FsRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.KeyValueDiffers }, { token: i0.Renderer2 }, { token: FsListDraggableListDirective }], target: i0.ɵɵFactoryTarget.Component });
FsRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsRowComponent, selector: "[fs-list-row]", inputs: { row: "row", rowActionsRaw: "rowActionsRaw", groupActionsRaw: "groupActionsRaw", hasRowActions: "hasRowActions", rowEvents: "rowEvents", rowClass: "rowClass", restoreMode: "restoreMode", rowIndex: "rowIndex", columns: "columns", selection: "selection", rowRemoved: "rowRemoved", activeFiltersCount: "activeFiltersCount", reorderEnabled: "reorderEnabled", reorderPosition: "reorderPosition", reorderStrategy: "reorderStrategy", reorderMultiple: "reorderMultiple" }, host: { properties: { "attr.role": "this.role", "class.drag-row": "this.reorderEnabled", "class.multiple-selection": "this.isMultipleSelection", "class": "this.rowCssClass" } }, viewQueries: [{ propertyName: "cellRefs", predicate: ["td"], descendants: true }], ngImport: i0, template: "<!-- Drag -->\n<ng-container *ngIf=\"leftDragDropEnabled\">\n  <ng-container *ngTemplateOutlet=\"dragCell\"></ng-container>\n</ng-container>\n\n<!-- Selection -->\n<ng-container *ngIf=\"selection && !(selection.disabled$ | async)\">\n  <td class=\"fs-list-col fs-list-col-selection\">\n    <ng-container *ngIf=\"!row.isGroupFooter\">\n      <mat-checkbox \n        (change)=\"selectRow($event)\"\n        [checked]=\"selected\"\n        [indeterminate]=\"indeterminateSelected\">\n      </mat-checkbox>\n    </ng-container>\n  </td>\n</ng-container>\n\n<!-- Content -->\n<ng-container *ngFor=\"let column of columns; trackBy: trackByFn; let isFirst = first\">\n  <td fs-cell\n      *ngIf=\"(isGroupRow && !column.groupHeaderColspanned)\n        || (isGroupFooterRow && !column.groupFooterColspanned)\n        || (!isGroupRow && !isGroupFooterRow && !column.cellColspanned)\"\n      [column]=\"column\"\n      [row]=\"row\"\n      [rowIndex]=\"rowIndex\"\n      [class]=\"(isGroupRow && column.groupHeaderConfigs.classesString)\n        || (isGroupFooterRow && column.groupFooterConfigs.classesString)\n        || (!isGroupFooterRow && column.cellConfigs.classesString)\"\n      [ngClass]=\"{ 'primary-col': isFirst }\"\n      [attr.colspan]=\"(isGroupRow && column.groupHeaderConfigs.colspan)\n        || (isGroupFooterRow && column.groupFooterConfigs.colspan)\n        || column.cellConfigs.colspan\"\n      [attr.width]=\"column.width\"\n  >\n  </td>\n</ng-container>\n\n<!-- Drag -->\n<ng-container *ngIf=\"rightDragDropEnabled\">\n  <ng-container *ngTemplateOutlet=\"dragCell\"></ng-container>\n</ng-container>\n\n<!-- Row Actions -->\n<td *ngIf=\"hasRowActions && !(reorderEnabled && reorderStrategy === ReorderStrategy.Manual)\" class=\"fs-list-col row-actions\">\n  <ng-container *ngIf=\"!isGroupFooterRow\">\n    <fs-list-row-actions\n      [row]=\"row\"\n      [index]=\"rowIndex\"\n      [rowActions]=\"rowActions\"\n      [menuRowActions]=\"menuRowActions\"\n      [inlineRowActions]=\"inlineRowActions\"\n      [restoreAction]=\"restoreAction\"\n      [restoreMode]=\"restoreMode\"\n      [rowRemoved]=\"rowRemoved\">\n    </fs-list-row-actions>\n  </ng-container>\n</td>\n\n<ng-template #dragCell>\n  <ng-container *ngIf=\"dragCellVisible && !isGroupFooterRow; else emptyCell\">\n    <td \n        class=\"fs-list-col drag-col\"\n        [class.drag-disabled]=\"isDragDisabled\"\n        (mousedown)=\"dragStart($event)\"\n        (touchstart)=\"dragStart($event)\">\n      <mat-icon>drag_handle</mat-icon>\n    </td>\n  </ng-container>\n  <ng-template #emptyCell>\n    <td class=\"fs-list-col drag-col\"></td>\n  </ng-template>\n</ng-template>\n", styles: [":host.drag-hidden{display:none}:host.draggable{opacity:.8;position:fixed;z-index:9999;box-shadow:2px 2px 2px #9e9e9ea6;border-radius:5px}:host.draggable td{border:none;background-color:#fff}:host.draggable-elem td{background-color:#c3c3c3}td.drag-col{width:24px;text-align:center;cursor:grab}td.drag-col.drag-disabled{opacity:.4;cursor:no-drop}td.drag-col mat-icon{display:flex}td.fs-list-col-selection{padding:10px}td.row-actions{width:1%;white-space:nowrap;overflow:hidden}td.row-actions .row-inline-action{margin-left:12px;display:inline-block}td.row-actions .row-inline-action:first-child{margin-left:0}td.row-actions .row-inline-action-icon,td.row-actions .row-inline-action-fab,td.row-actions .row-inline-action-mini-fab,td.row-actions .row-menu-action{width:35px;justify-content:center;align-items:center}td.left{text-align:left}td.center{text-align:center}td.right{text-align:right}\n"], components: [{ type: i2$2.MatCheckbox, selector: "mat-checkbox", inputs: ["disableRipple", "color", "tabIndex", "aria-label", "aria-labelledby", "id", "labelPosition", "name", "required", "checked", "disabled", "indeterminate", "aria-describedby", "value"], outputs: ["change", "indeterminateChange"], exportAs: ["matCheckbox"] }, { type: FsCellComponent, selector: "[fs-cell]", inputs: ["column", "row", "rowIndex"] }, { type: FsRowActionsComponent, selector: "fs-list-row-actions", inputs: ["row", "index", "restoreMode", "rowActions", "rowRemoved", "menuRowActions", "inlineRowActions", "restoreAction"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-row]',
                    templateUrl: './row.component.html',
                    styleUrls: ['./row.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.KeyValueDiffers }, { type: i0.Renderer2 }, { type: FsListDraggableListDirective }]; }, propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], row: [{
                type: Input
            }], rowActionsRaw: [{
                type: Input
            }], groupActionsRaw: [{
                type: Input
            }], hasRowActions: [{
                type: Input
            }], rowEvents: [{
                type: Input
            }], rowClass: [{
                type: Input
            }], restoreMode: [{
                type: Input
            }], rowIndex: [{
                type: Input
            }], columns: [{
                type: Input
            }], selection: [{
                type: Input
            }], rowRemoved: [{
                type: Input
            }], activeFiltersCount: [{
                type: Input
            }], reorderEnabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.drag-row']
            }], reorderPosition: [{
                type: Input
            }], reorderStrategy: [{
                type: Input
            }], reorderMultiple: [{
                type: Input
            }], cellRefs: [{
                type: ViewChildren,
                args: ['td']
            }], isMultipleSelection: [{
                type: HostBinding,
                args: ['class.multiple-selection']
            }], rowCssClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class FsListDraggableRowDirective {
    constructor(_el, _renderer, _reorderController, _draggableList) {
        this._el = _el;
        this._renderer = _renderer;
        this._reorderController = _reorderController;
        this._draggableList = _draggableList;
        this._destroy$ = new Subject();
    }
    get elRef() {
        return this._el;
    }
    ngOnInit() {
        if (this._reorderController.moveDropCallback) {
            this._listenDragEvents();
        }
        this._draggableList.addDraggableDirective(this);
    }
    ngOnDestroy() {
        this._draggableList.removeDraggableDirective(this);
        this._destroy$.next();
        this._destroy$.complete();
    }
    dragHide() {
        this._el.nativeElement.classList.add('drag-hidden');
    }
    _listenDragEvents() {
        this._draggableList
            .dragStart$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._markReadyToSwapRows();
        });
        this._draggableList
            .dragEnd$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._unmarkRows();
        });
    }
    _markReadyToSwapRows() {
        var _a, _b;
        const currentEl = this.row;
        const targetEl = this._draggableList.draggableItem;
        this.row.readyToSwap = this._reorderController.moveDropCallback({
            row1: currentEl === null || currentEl === void 0 ? void 0 : currentEl.data,
            row2: targetEl === null || targetEl === void 0 ? void 0 : targetEl.data,
            group1: (_a = currentEl === null || currentEl === void 0 ? void 0 : currentEl.parent) === null || _a === void 0 ? void 0 : _a.data,
            group2: (_b = targetEl === null || targetEl === void 0 ? void 0 : targetEl.parent) === null || _b === void 0 ? void 0 : _b.data,
        });
        if (!this.row.readyToSwap) {
            this._renderer.addClass(this._el.nativeElement, 'fs-list-swap-restricted');
        }
    }
    _unmarkRows() {
        if (!this.row.readyToSwap) {
            this._renderer.removeClass(this._el.nativeElement, 'fs-list-swap-restricted');
        }
    }
}
FsListDraggableRowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListDraggableRowDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: ReorderController }, { token: FsListDraggableListDirective }], target: i0.ɵɵFactoryTarget.Directive });
FsListDraggableRowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListDraggableRowDirective, selector: "[fsListDraggableRow]", inputs: { row: "row" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListDraggableRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsListDraggableRow]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: ReorderController }, { type: FsListDraggableListDirective }]; }, propDecorators: { row: [{
                type: Input
            }] } });

class FsBodyComponent {
    constructor() {
        this.columns = [];
        this.hasFooter = false;
        this.rowActionsRaw = [];
        this.groupActionsRaw = [];
        this.rowEvents = {};
        this.hasRowActions = false;
        this.restoreMode = false;
    }
}
FsBodyComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsBodyComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsBodyComponent, selector: "[fs-list-body]", inputs: { rows: "rows", columns: "columns", hasFooter: "hasFooter", rowActionsRaw: "rowActionsRaw", groupActionsRaw: "groupActionsRaw", rowEvents: "rowEvents", rowClass: "rowClass", hasRowActions: "hasRowActions", selection: "selection", restoreMode: "restoreMode", rowRemoved: "rowRemoved", activeFiltersCount: "activeFiltersCount", reorderEnabled: "reorderEnabled", reorderPosition: "reorderPosition", reorderStrategy: "reorderStrategy", reorderMultiple: "reorderMultiple" }, queries: [{ propertyName: "headerTemplate", first: true, predicate: FsRowComponent, descendants: true, read: TemplateRef, static: true }], viewQueries: [{ propertyName: "rowsContainer", first: true, predicate: ["rowsContainer"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<ng-container *ngFor=\"let row of rows; let i = index;\">\n  <tr\n    fs-list-row\n    fsListDraggableRow\n    [row]=\"row\"\n    [rowIndex]=\"i\"\n    [columns]=\"columns\"\n    [rowActionsRaw]=\"rowActionsRaw\"\n    [groupActionsRaw]=\"groupActionsRaw\"\n    [hasRowActions]=\"hasRowActions\"\n    [rowEvents]=\"rowEvents\"\n    [rowClass]=\"rowClass\"\n    [selection]=\"selection\"\n    [restoreMode]=\"restoreMode\"\n    [rowRemoved]=\"rowRemoved\"\n    [activeFiltersCount]=\"activeFiltersCount\"\n    [reorderEnabled]=\"reorderEnabled\"\n    [reorderPosition]=\"reorderPosition\"\n    [reorderStrategy]=\"reorderStrategy\"\n    [reorderMultiple]=\"reorderMultiple\">\n  </tr>\n</ng-container>\n", styles: [":host.drag-hidden .drag-col{opacity:0!important;cursor:default}:host.disabled{opacity:.4;pointer-events:none}\n"], components: [{ type: FsRowComponent, selector: "[fs-list-row]", inputs: ["row", "rowActionsRaw", "groupActionsRaw", "hasRowActions", "rowEvents", "rowClass", "restoreMode", "rowIndex", "columns", "selection", "rowRemoved", "activeFiltersCount", "reorderEnabled", "reorderPosition", "reorderStrategy", "reorderMultiple"] }], directives: [{ type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: FsListDraggableRowDirective, selector: "[fsListDraggableRow]", inputs: ["row"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-body]',
                    templateUrl: './body.component.html',
                    styleUrls: ['./body.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { rows: [{
                type: Input
            }], columns: [{
                type: Input
            }], hasFooter: [{
                type: Input
            }], rowActionsRaw: [{
                type: Input
            }], groupActionsRaw: [{
                type: Input
            }], rowEvents: [{
                type: Input
            }], rowClass: [{
                type: Input
            }], hasRowActions: [{
                type: Input
            }], selection: [{
                type: Input
            }], restoreMode: [{
                type: Input
            }], rowRemoved: [{
                type: Input
            }], activeFiltersCount: [{
                type: Input
            }], reorderEnabled: [{
                type: Input
            }], reorderPosition: [{
                type: Input
            }], reorderStrategy: [{
                type: Input
            }], reorderMultiple: [{
                type: Input
            }], rowsContainer: [{
                type: ViewChild,
                args: ['rowsContainer', { read: ViewContainerRef, static: true }]
            }], headerTemplate: [{
                type: ContentChild,
                args: [FsRowComponent, { read: TemplateRef, static: true }]
            }] } });

class CustomizeColsDialogComponent {
    constructor(data, _dialog) {
        this._dialog = _dialog;
        this.columns = [];
        this.saveDisabled = false;
        this._destroy$ = new Subject();
        this.columns = data.columns;
        this._changeFn = data.changeFn;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    visibilityChange(event, column) {
        column.show = event.checked;
    }
    save() {
        const data = this.columns
            .filter((column) => !!column.name)
            .map((column) => {
            return {
                name: column.name,
                show: column.show,
            };
        });
        this.saveDisabled = true;
        const changed = this._changeFn(data);
        if (changed instanceof Observable) {
            changed
                .pipe(takeUntil(this._destroy$))
                .subscribe({
                next: () => {
                    this._dialog.close(this.columns);
                },
                error: () => {
                    this.saveDisabled = false;
                },
            });
        }
        else {
            this._dialog.close(this.columns);
        }
    }
    cancel() {
        this._dialog.close();
    }
}
CustomizeColsDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: CustomizeColsDialogComponent, deps: [{ token: MAT_DIALOG_DATA }, { token: i1$2.MatDialogRef }], target: i0.ɵɵFactoryTarget.Component });
CustomizeColsDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: CustomizeColsDialogComponent, selector: "ng-component", ngImport: i0, template: "<h1 matDialogTitle>Customize Columns</h1>\n<mat-dialog-content>\n  <div *ngFor=\"let column of columns\">\n    <mat-checkbox [checked]=\"column.show\" (change)=\"visibilityChange($event, column)\" [disabled]=\"column.disabled\">\n      <ng-container *ngIf=\"!column.title; else titleLable\">\n        <ng-template [ngTemplateOutlet]=\"column.template\"></ng-template>\n      </ng-container>\n      <ng-template #titleLable>\n        {{ column.title }}\n      </ng-template>\n\n      <ng-container *ngIf=\"column.tooltip\">\n        <mat-icon [matTooltip]=\"column.tooltip\">\n          help\n        </mat-icon>\n      </ng-container>\n    </mat-checkbox>\n  </div>\n</mat-dialog-content>\n<mat-dialog-actions>\n  <button type=\"button\" mat-button color=\"primary\" (click)=\"save()\" [disabled]=\"saveDisabled\">Save</button>\n  <button type=\"button\" mat-button (click)=\"cancel()\">Cancel</button>\n</mat-dialog-actions>\n", styles: [".mat-checkbox{min-height:32px;display:block}.mat-icon{font-size:14px;height:14px;width:14px;vertical-align:middle}\n"], components: [{ type: i2$2.MatCheckbox, selector: "mat-checkbox", inputs: ["disableRipple", "color", "tabIndex", "aria-label", "aria-labelledby", "id", "labelPosition", "name", "required", "checked", "disabled", "indeterminate", "aria-describedby", "value"], outputs: ["change", "indeterminateChange"], exportAs: ["matCheckbox"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1$2.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i1$2.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i6.MatTooltip, selector: "[matTooltip]", exportAs: ["matTooltip"] }, { type: i1$2.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: CustomizeColsDialogComponent, decorators: [{
            type: Component,
            args: [{
                    templateUrl: './customize-cols.component.html',
                    styleUrls: ['./customize-cols.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: i1$2.MatDialogRef }]; } });

class FsFooterCellComponent extends FsCellComponent {
    constructor() {
        super();
    }
}
FsFooterCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterCellComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsFooterCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsFooterCellComponent, selector: "[fs-list-footer-cell]", usesInheritance: true, ngImport: i0, template: "<ng-template [ngTemplateOutlet]=\"column.footerTemplate || cell\" [ngTemplateOutletContext]=\"cellContext\"></ng-template>\n\n<ng-template #cell let-value=\"value\">\n  {{value}}\n</ng-template>\n", directives: [{ type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterCellComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-footer-cell]',
                    templateUrl: './footer-cell.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return []; } });

class FsFooterRowComponent extends FsRowComponent {
    constructor(el, cdRef, differs, renderer) {
        super(el, cdRef, differs, renderer, null);
    }
}
FsFooterRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterRowComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.KeyValueDiffers }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
FsFooterRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsFooterRowComponent, selector: "[fs-list-footer-row]", inputs: { hasRowActions: "hasRowActions", activeFiltersCount: "activeFiltersCount", reorderEnabled: "reorderEnabled", reorderPosition: "reorderPosition", reorderStrategy: "reorderStrategy" }, usesInheritance: true, ngImport: i0, template: "<td *ngIf=\"leftDragDropEnabled\" class=\"fs-list-col drag-col\"></td>\n\n<td *ngIf=\"selection\" class=\"fs-list-col fs-list-col-selection\"></td>\n<ng-container *ngFor=\"let column of columns; trackBy: trackByFn\">\n  <td \n    *ngIf=\"!column.footerColspanned\"\n    fs-list-footer-cell\n    [column]=\"column\"\n    [row]=\"row\"\n    [rowIndex]=\"rowIndex\"\n    [ngClass]=\"column.footerConfigs.classesArray\"\n    [attr.colspan]=\"column.footerConfigs.colspan\"\n    [attr.width]=\"column.width\">\n  </td>\n</ng-container>\n\n<!-- Drag -->\n<td *ngIf=\"rightDragDropEnabled\" class=\"fs-list-col row-actions\"></td>\n\n<!-- Row Actions -->\n<td *ngIf=\"hasRowActions && !(reorderEnabled && reorderStrategy === ReorderStrategy.Manual)\" class=\"fs-list-col drag-col\"></td>\n", styles: ["td.left{text-align:left}td.center{text-align:center}td.right{text-align:right}\n"], components: [{ type: FsFooterCellComponent, selector: "[fs-list-footer-cell]" }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-footer-row]',
                    templateUrl: './footer-row.component.html',
                    styleUrls: ['./footer-row.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.KeyValueDiffers }, { type: i0.Renderer2 }]; }, propDecorators: { hasRowActions: [{
                type: Input
            }], activeFiltersCount: [{
                type: Input
            }], reorderEnabled: [{
                type: Input
            }], reorderPosition: [{
                type: Input
            }], reorderStrategy: [{
                type: Input
            }] } });

class FsFooterComponent {
    constructor() {
        this.columns = [];
    }
}
FsFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsFooterComponent, selector: "[fs-list-footer]", inputs: { hasRowActions: "hasRowActions", columns: "columns", selection: "selection", activeFiltersCount: "activeFiltersCount", reorderEnabled: "reorderEnabled", reorderPosition: "reorderPosition", reorderStrategy: "reorderStrategy" }, ngImport: i0, template: "<tr \n    fs-list-footer-row\n    [columns]=\"columns\"\n    [hasRowActions]=\"hasRowActions\"\n    [selection]=\"selection\"\n    [activeFiltersCount]=\"activeFiltersCount\"\n    [reorderEnabled]=\"reorderEnabled\"\n    [reorderPosition]=\"reorderPosition\"\n    [reorderStrategy]=\"reorderStrategy\">\n</tr>\n", styles: [""], components: [{ type: FsFooterRowComponent, selector: "[fs-list-footer-row]", inputs: ["hasRowActions", "activeFiltersCount", "reorderEnabled", "reorderPosition", "reorderStrategy"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-footer]',
                    templateUrl: './footer.component.html',
                    styleUrls: ['./footer.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { hasRowActions: [{
                type: Input
            }], columns: [{
                type: Input
            }], selection: [{
                type: Input
            }], activeFiltersCount: [{
                type: Input
            }], reorderEnabled: [{
                type: Input
            }], reorderPosition: [{
                type: Input
            }], reorderStrategy: [{
                type: Input
            }] } });

class FsHeadCellComponent extends FsCellComponent {
    constructor(_cdRef, _differs) {
        super();
        this._cdRef = _cdRef;
        this._differs = _differs;
        this.cellContext = {};
        this._columnDiffer = this._differs.find({}).create();
    }
    ngDoCheck() {
        if (this._columnDiffer.diff(this.column)) {
            this._cdRef.markForCheck();
        }
    }
    initCellContext() {
        this.cellContext.value = this.column.title;
    }
}
FsHeadCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsHeadCellComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.KeyValueDiffers }], target: i0.ɵɵFactoryTarget.Component });
FsHeadCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsHeadCellComponent, selector: "[fs-head-cell]", usesInheritance: true, ngImport: i0, template: "<div class=\"wrap\">\n    <span class=\"title\">\n      <ng-template [ngIf]=\"!column.headerTemplate\">{{column.title}}</ng-template>\n      <ng-template\n        [ngIf]=\"column.headerTemplate\"\n        [ngTemplateOutlet]=\"column.headerTemplate\"\n        [ngTemplateOutletContext]=\"cellContext\">\n      </ng-template>\n    </span>\n  <div class=\"direction\" *ngIf=\"column.ordered\" [ngSwitch]=\"column.sortingDirection$ | async\">\n    <mat-icon class=\"material-icons\" role=\"img\" aria-label=\"arrow_downward\" *ngSwitchCase=\"'asc'\">arrow_downward</mat-icon>\n    <mat-icon class=\"material-icons\" role=\"img\" aria-label=\"arrow_upward\" *ngSwitchCase=\"'desc'\">arrow_upward</mat-icon>\n  </div>\n</div>\n", styles: [".wrap{display:inline-flex;align-items:center}.wrap mat-icon{font-size:14px;display:block;height:14px;width:14px}.wrap .direction{margin-left:5px}\n"], components: [{ type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i3.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { type: i3.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsHeadCellComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-head-cell]',
                    templateUrl: './head-cell.component.html',
                    styleUrls: ['./head-cell.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.KeyValueDiffers }]; } });

class ColumnAsyncAttribute extends BehaviorSubject {
    constructor(value) {
        super(Object.freeze(value));
    }
    next(value) {
        const newValue = value;
        const oldValue = this.getValue();
        if (newValue !== oldValue) {
            super.next(Object.freeze(value));
        }
    }
}

class ColumnAttributes {
    constructor(attrs = {}) {
        this._customize = true;
        this._direction$ = new ColumnAsyncAttribute(null);
        this._visible$ = new ColumnAsyncAttribute(true);
        this._init(attrs);
    }
    // title
    set title(value) {
        this._title = value;
    }
    get title() {
        return this._title;
    }
    // name
    set name(value) {
        this._name = value;
    }
    get name() {
        return this._name;
    }
    // customize
    set customize(value) {
        this._customize = value;
    }
    get customize() {
        return this._customize;
    }
    // sortable
    set sortable(value) {
        this._sortable = value;
    }
    get sortable() {
        return this._sortable;
    }
    // sortableDefault
    set sortableDefault(value) {
        this._sortableDefault = value;
        if (this.sortableDefault) {
            this.sortable = true;
        }
    }
    get sortableDefault() {
        return this._sortableDefault;
    }
    // direction
    set direction(value) {
        this._direction$.next(value);
    }
    get direction() {
        return this._direction$.value;
    }
    get direction$() {
        return this._direction$.asObservable();
    }
    // align
    set align(value) {
        this._align = value;
    }
    get align() {
        return this._align;
    }
    // width
    set width(value) {
        this._width = value;
    }
    get width() {
        return this._width;
    }
    // className
    set className(value) {
        this._className = value;
    }
    get className() {
        return this._className;
    }
    // visibility
    set visible(value) {
        this._visible$.next(value);
    }
    get visible() {
        return this._visible$.getValue();
    }
    get visible$() {
        return this._visible$.asObservable();
    }
    _init(attrs) {
        Object.keys(attrs)
            .forEach((key) => {
            switch (key) {
                case 'title':
                    {
                        this.title = attrs[key];
                    }
                    break;
                case 'name':
                    {
                        this.name = attrs[key];
                    }
                    break;
                case 'align':
                    {
                        this.align = attrs[key];
                    }
                    break;
                case 'direction':
                    {
                        this.direction = attrs[key];
                    }
                    break;
                case 'sortable':
                    {
                        this.sortable = attrs[key];
                    }
                    break;
                case 'show':
                    {
                        this.visible = attrs[key];
                    }
                    break;
                case 'visible':
                    {
                        this.visible = attrs[key];
                    }
                    break;
            }
        });
    }
}

class SortingController {
    constructor() {
        this.sortingColumns = [];
        this.fakeSortingColumns = [];
        this._initialized = new BehaviorSubject(false);
        this._sortingChanged$ = new Subject();
        this._onDestroy = new Subject();
    }
    get sortingChanged$() {
        return this._sortingChanged$.pipe(takeUntil(this._onDestroy));
    }
    get initialized$() {
        return this._initialized.pipe(takeUntil(this._onDestroy));
    }
    get value() {
        if (this.sortingColumn) {
            return {
                value: this.sortingColumn.name,
                direction: this.sortingColumn.direction,
            };
        }
        return undefined;
    }
    get initialization() {
        return !this._initialized.getValue();
    }
    get isDefined() {
        return !!this.sortingColumn;
    }
    set _initialization(value) {
        this._initialized.next(!value);
    }
    addSortableColumn(column) {
        this.sortingColumns.push(column);
    }
    clearSortableColumns() {
        this.sortingColumns = [];
    }
    /**
     * Set Sortable Direction
     *
     * @param direction
     */
    sortDirection(direction) {
        if (this.sortingColumn && this.sortingColumn.sortingDirection !== direction) {
            this._setSortingDirection(direction);
            this._notifySortChanged();
        }
    }
    /**
     * Sort By
     *
     * @param column
     */
    sortBy(column) {
        if (column.sortable) {
            this._setSortingColumn(column);
            this._notifySortChanged();
        }
    }
    /**
     * Same as sortBy, but need only column name as parameter for sort
     *
     * @param name
     */
    sortByColumnWithName(name) {
        const column = this.getColumn(name);
        if (!column) {
            return;
        }
        this._setSortingColumn(column);
        this._notifySortChanged();
    }
    /**
     * Init fake columns for sorting
     *
     * @param columns
     */
    initFakeColumns(columns) {
        columns.forEach((column) => {
            const attributes = new ColumnAttributes({
                title: column.name,
                name: column.value,
                sortable: true,
                direction: column.direction,
            });
            const fakeColumn = new Column({
                attributes,
            });
            this.fakeSortingColumns.push(fakeColumn);
        });
    }
    /**
     * Set initial sorting
     *
     * @param sort
     */
    initialSortBy(sort) {
        this._initialization = true;
        if (!sort) {
            this._trySortByDefaultSortableColumn();
            if (!this.sortingColumn) {
                this.sortByFirstSortbale();
            }
            this._initialization = false;
            return;
        }
        if (!this.getColumn(sort.value)) {
            this.sortByFirstSortbale();
            console.warn(`
        Not possible to do initial sort by "${sort.value}" column.
        Column with name "${sort.value}" does not exists.
        Check your lister configuration.
      `);
        }
        else {
            this.sortByColumnWithName(sort.value);
            const direction = (sort.direction === undefined || sort.direction === 'asc')
                ? SortingDirection.asc
                : SortingDirection.desc;
            this._setSortingDirection(direction);
        }
        this._initialization = false;
    }
    /**
     * Sort by first of available sorting columns
     */
    sortByFirstSortbale() {
        const column = this.sortingColumns.find((col) => col.sortable);
        if (!column) {
            return;
        }
        this.sortBy(column);
        this.sortDirection(column.direction || SortingDirection.asc);
    }
    getColumn(name) {
        return [...this.sortingColumns, ...this.fakeSortingColumns]
            .find((col) => col.name === name && col.sortable);
    }
    /**
     * Destroy
     */
    destroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }
    _setSortingColumn(column) {
        // Can't do sort by non sortable column
        if (!column.sortable) {
            return false;
        }
        // Column was ordered before
        if (column.ordered) {
            column.changeDirection();
        }
        else {
            [...this.fakeSortingColumns, ...this.sortingColumns]
                .filter((col) => col.ordered)
                .forEach((col) => col.ordered = false);
            column.ordered = true;
        }
        this.sortingColumn = column;
    }
    _setSortingDirection(direction) {
        this.sortingColumn.sortingDirection = direction;
    }
    _notifySortChanged() {
        if (this.initialization) {
            return;
        }
        this._sortingChanged$.next({
            sortBy: this.sortingColumn.name,
            sortDirection: this.sortingColumn.direction,
        });
    }
    _trySortByDefaultSortableColumn() {
        const sortableDefault = this.sortingColumns
            .find((column) => column.sortableDefault);
        if (sortableDefault) {
            this.sortByColumnWithName(sortableDefault.name);
        }
    }
}

class FsHeadComponent {
    constructor(_cdRef) {
        this._cdRef = _cdRef;
        this.selectedAll = false;
        this.ReorderStrategyEnum = ReorderStrategy;
        this._destroy$ = new Subject();
    }
    get leftDragDropEnabled() {
        return this.reorderEnabled
            && this.reorderPosition === ReorderPosition.Left
            && this.activeFiltersCount === 0;
    }
    get rightDragDropEnabled() {
        return this.reorderEnabled
            && this.reorderPosition === ReorderPosition.Right
            && this.activeFiltersCount === 0;
    }
    ngOnInit() {
        this._initSorting();
        this._initSelection();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    /**
     * Select All Visible Rows
     *
     * @param event
     */
    selectAll(event) {
        this.selection.selectAllVisibleRows(event.checked);
    }
    /**
     * Track By for improve change detection
     *
     * @param index
     */
    trackByFn(index) {
        return index;
    }
    /**
     * Subscribe to sorting change
     */
    _initSorting() {
        this.sorting.sortingChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._cdRef.detectChanges();
        });
    }
    /**
     * Subscribe to selection change
     */
    _initSelection() {
        if (this.selection) {
            this.selection.selectionChange$
                .pipe(filter(({ type }) => (type === SelectionChangeType.AllVisibleSelectionChange
                || type === SelectionChangeType.SelectedAll
                || type === SelectionChangeType.RowSelectionChange)), takeUntil(this._destroy$))
                .subscribe(({ type, payload: status }) => {
                this.selectedAll = status;
                this._cdRef.markForCheck();
            });
        }
    }
}
FsHeadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsHeadComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsHeadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsHeadComponent, selector: "[fs-list-head]", inputs: { sorting: "sorting", columns: "columns", hasRowActions: "hasRowActions", selection: "selection", activeFiltersCount: "activeFiltersCount", reorderEnabled: "reorderEnabled", reorderPosition: "reorderPosition", reorderStrategy: "reorderStrategy" }, viewQueries: [{ propertyName: "rowsContainer", first: true, predicate: ["rowsContainer"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<tr class=\"fs-list-row\">\n  <!-- Drag -->\n  <th *ngIf=\"leftDragDropEnabled\"\n      class=\"fs-list-col drag-col\">\n  </th>\n\n  <!-- Selection -->\n  <th *ngIf=\"selection && !(selection.disabled$ | async)\" class=\"fs-list-col fs-list-col-selection\">\n    <mat-checkbox (change)=\"selectAll($event)\" [checked]=\"selectedAll\"></mat-checkbox>\n  </th>\n\n  <!-- Content -->\n  <th \n    fs-head-cell *ngFor=\"let column of columns; trackBy: trackByFn\"\n    (click)=\"$event.stopPropagation(); sorting.sortBy(column)\"\n    [column]=\"column\"\n    [class.sorting]=\"column.sortable\"\n    [ngClass]=\"column.headerConfigs.classesArray\"\n    [attr.colspan]=\"column.headerConfigs.colspan\"\n    [attr.width]=\"column.width\">\n  </th>\n\n  <!-- Drag -->\n  <th *ngIf=\"rightDragDropEnabled\"\n      class=\"fs-list-col drag-col\">\n  </th>\n\n  <!-- Row Actions -->\n  <th *ngIf=\"hasRowActions && !(reorderEnabled && reorderStrategy === ReorderStrategyEnum.Manual)\" class=\"fs-list-col row-actions\"></th>\n</tr>\n", styles: ["th.fs-list-col-selection{width:1%;text-align:left}th.sorting{cursor:pointer}th.sorting:hover{background-color:#f6f6f6}th.sorting{background-image:none}th.left{text-align:left}th.center{text-align:center}th.right{text-align:right}\n"], components: [{ type: i2$2.MatCheckbox, selector: "mat-checkbox", inputs: ["disableRipple", "color", "tabIndex", "aria-label", "aria-labelledby", "id", "labelPosition", "name", "required", "checked", "disabled", "indeterminate", "aria-describedby", "value"], outputs: ["change", "indeterminateChange"], exportAs: ["matCheckbox"] }, { type: FsHeadCellComponent, selector: "[fs-head-cell]" }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsHeadComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fs-list-head]',
                    templateUrl: './head.component.html',
                    styleUrls: ['./head.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { sorting: [{
                type: Input
            }], columns: [{
                type: Input
            }], hasRowActions: [{
                type: Input
            }], selection: [{
                type: Input
            }], activeFiltersCount: [{
                type: Input
            }], reorderEnabled: [{
                type: Input
            }], reorderPosition: [{
                type: Input
            }], reorderStrategy: [{
                type: Input
            }], rowsContainer: [{
                type: ViewChild,
                args: ['rowsContainer', { read: ViewContainerRef, static: true }]
            }] } });

var PageChangeType;
(function (PageChangeType) {
    PageChangeType[PageChangeType["Default"] = 0] = "Default";
    PageChangeType[PageChangeType["LimitChanged"] = 1] = "LimitChanged";
})(PageChangeType || (PageChangeType = {}));

var FsListState;
(function (FsListState) {
    FsListState[FsListState["Idle"] = 0] = "Idle";
    FsListState[FsListState["Load"] = 1] = "Load";
    FsListState[FsListState["Reload"] = 2] = "Reload";
    FsListState[FsListState["Filter"] = 3] = "Filter";
    FsListState[FsListState["Sort"] = 4] = "Sort";
    FsListState[FsListState["PageChange"] = 5] = "PageChange";
    FsListState[FsListState["LoadMore"] = 6] = "LoadMore";
})(FsListState || (FsListState = {}));

class ColumnsController {
    constructor() {
        this._visibleColumns$ = new BehaviorSubject([]);
        this._visibleColumnsShared$ = this._visibleColumns$.pipe();
        this._theadClass = '';
        this._isConfigured = false;
        this._loadFnConfigured = false;
        this._changeFnConfigured = false;
        this._columnsFetched = false;
        this._hasHeader = false;
        this._hasFooter = false;
        this._columns = [];
        this._columnsUpdated$ = new Subject();
        this._destroy$ = new Subject();
    }
    get columns() {
        return this._columns.slice();
    }
    get visibleColumns() {
        return this._visibleColumns$.getValue();
    }
    get visibleColumns$() {
        return this._visibleColumnsShared$;
    }
    get columnsForDialog() {
        const hasCustomTitle = !!this._customizeFieldTitleFn;
        const hasCustomDisabledStatus = !!this._customizeFieldDisabledFn;
        const hasCustomTooltip = !!this._columnTooltipFn;
        return this._columns
            .filter((column) => column.customize && !!column.name)
            .map((column) => {
            const title = hasCustomTitle
                ? this._customizeFieldTitleFn(column.name, column.title)
                : column.name;
            const disabled = hasCustomDisabledStatus
                ? this._customizeFieldDisabledFn(column.name)
                : false;
            const tooltip = hasCustomTooltip
                ? this._columnTooltipFn(column.name, column.visible, disabled)
                : void 0;
            return {
                template: column.headerTemplate,
                name: column.name,
                show: column.visible,
                title: title,
                disabled: disabled,
                tooltip: tooltip,
            };
        });
    }
    get columnsFetched() {
        return this._columnsFetched;
    }
    get hasHeader() {
        return this._hasHeader;
    }
    get hasFooter() {
        return this._hasFooter;
    }
    get theadClass() {
        return this._theadClass;
    }
    get configured() {
        return this._isConfigured;
    }
    get loadFnConfigured() {
        return this._loadFnConfigured;
    }
    get changeFnConfigured() {
        return this._changeFnConfigured;
    }
    get changeFn() {
        return this._changeFn;
    }
    get visibleColumnsNames() {
        return this.visibleColumns
            .map((column) => column.name)
            .filter((name) => !!name);
    }
    setDefaults(defaults) {
        this._defaultConfigs = defaults;
    }
    /**
     * Set data from list config
     * @param config
     */
    initConfig(config) {
        if (config) {
            if (config.load) {
                this._loadFn = config.load;
                this._loadFnConfigured = true;
            }
            if (config.change) {
                this._changeFn = config.change;
                this._changeFnConfigured = true;
            }
            if (config.title) {
                this._customizeFieldTitleFn = config.title;
            }
            if (config.disabled) {
                this._customizeFieldDisabledFn = config.disabled;
            }
            if (config.tooltip) {
                this._columnTooltipFn = config.tooltip;
            }
            this._isConfigured = true;
        }
    }
    /**
     * Base initialization for columns
     * @param templates
     */
    initializeColumns(templates) {
        this._columns = [];
        templates.forEach((column) => {
            const col = new Column(column, this._defaultConfigs);
            if (col.headerTemplate || col.title) {
                this._hasHeader = true;
            }
            if (col.footerTemplate) {
                this._hasFooter = true;
            }
            this._columns.push(col);
        });
        this._theadClass = this.hasHeader ? 'has-header' : '';
        this._updateColspans('headerConfigs', 'headerColspanned');
        this._updateColspans('groupHeaderConfigs', 'groupHeaderColspanned');
        this._updateColspans('groupFooterConfigs', 'groupFooterColspanned');
        this._updateColspans('cellConfigs', 'cellColspanned');
        this._updateColspans('footerConfigs', 'footerColspanned');
        this.updateVisibleColumns();
        this._listenColumnVisibilityUpdates();
    }
    /**
     * Load visiblity config for columns from remote
     */
    loadRemoteColumnConfigs() {
        return this._loadFn()
            .pipe(takeUntil(this._destroy$), tap((columnConfigs) => {
            this._columnsFetched = true;
            this.updateVisibilityForCols(columnConfigs);
        }));
    }
    /**
     * Set visible columns based on current columns show status
     */
    updateVisibleColumns() {
        this._visibleColumns$.next(this._columns.filter((column) => column.visible) || []);
    }
    /**
     * Update visibility based on passed config
     * @param columnsConfig
     */
    updateVisibilityForCols(columnsConfig) {
        columnsConfig.forEach((columnConfig) => {
            const col = this._columns
                .find((column) => column.name === columnConfig.name);
            if (col) {
                col.updateVisibility(columnConfig.show);
            }
        });
        this.updateVisibleColumns();
    }
    destroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this._columnsUpdated$.complete();
        this._columns = void 0;
        this._visibleColumns$ = void 0;
        this._defaultConfigs = void 0;
        this._loadFn = void 0;
        this._changeFn = void 0;
    }
    _listenColumnVisibilityUpdates() {
        this._columnsUpdated$.next();
        const columnsVisibility = this._columns.map((column) => {
            return column.visible$.pipe(skip(1));
        });
        merge(...columnsVisibility)
            .pipe(takeUntil(this._columnsUpdated$), takeUntil(this._destroy$))
            .subscribe(() => {
            this.updateVisibleColumns();
        });
    }
    _updateColspans(config, updateFlag) {
        this._columns.forEach((col, index) => {
            if (col[config].colspan !== void 0) {
                const spanTo = index + +col[config].colspan;
                if (!isNumber(spanTo)) {
                    return;
                }
                this._columns[index][updateFlag] = false;
                for (let i = index + 1; i < spanTo; i++) {
                    if (this._columns[i]) {
                        this._columns[i][updateFlag] = true;
                    }
                }
            }
        });
    }
}

class DataController {
    constructor() {
        this._visibleRows$ = new BehaviorSubject([]);
        this._rowsRemoved$ = new Subject();
        this._remoteRowsChange$ = new Subject();
        this._store = new Map();
        this._rowsStack = [];
        this._initialExpand = true;
        this._groupEnabled = false;
        this._hasData = false;
        this._destroy$ = new Subject();
    }
    get visibleRows$() {
        return this._visibleRows$;
    }
    get visibleRows() {
        return this._visibleRows$.getValue();
    }
    get visibleRowsData() {
        return this.visibleRows.map((row) => row.data);
    }
    get rowsStack() {
        return this._rowsStack.slice();
    }
    get groupEnabled() {
        return this._groupEnabled;
    }
    set groupEnabled(value) {
        this._groupEnabled = value;
    }
    get remoteRowsChange$() {
        return this._remoteRowsChange$.pipe(takeUntil(this._destroy$));
    }
    get rowsRemoved$() {
        return this._rowsRemoved$.pipe(takeUntil(this._destroy$));
    }
    get hasData() {
        return this._hasData;
    }
    get operation() {
        return this._operation;
    }
    set visibleRows(value) {
        this._visibleRows$.next(value);
        this._hasData = this.visibleRows.length > 0;
    }
    get visibleRowsCount() {
        return this.visibleRows.length;
    }
    get hasGroups() {
        return this._compareByFn && this._groupByFn;
    }
    get reorderData() {
        return this._rowsStack
            .map((row) => row.getReorderData());
    }
    setGroupConfig(group) {
        var _a;
        if (group) {
            this._groupByFn = group.groupBy;
            this._compareByFn = group.compareBy;
            this._footerRowFn = group.footer || (() => false);
            this._initialExpand = (_a = group.initialExpand) !== null && _a !== void 0 ? _a : true;
            // group mode enabled by default
            this._groupEnabled = (group.enabled !== void 0)
                ? group.enabled
                : true;
        }
    }
    setAdditionalConfigs(configs) {
        this._infinityScrollEnabled = configs.scrollable;
        this._loadMoreEnabled = configs.loadMoreEnabled;
    }
    /**
     * Rows what was received from fetch function
     * @param rows
     */
    setRowsFromResponse(rows) {
        if (this._infinityScrollEnabled) {
            switch (this._operation) {
                case FsListState.Filter:
                case FsListState.Reload:
                case FsListState.Sort:
                    {
                        this._updateRowsStack(rows);
                    }
                    break;
                default: {
                    this._extendRowsStack(rows);
                }
            }
            this._remoteRowsChange$.next();
        }
        else {
            if (this._operation === FsListState.LoadMore ||
                (this._operation === FsListState.PageChange && this._loadMoreEnabled)) {
                this._extendRowsStack(rows);
            }
            else {
                this._updateRowsStack(rows);
            }
        }
        this._operation = FsListState.Idle;
        this._updateVisibleRows();
    }
    /**
     * Set current operation for list data flow
     * @param value
     */
    setOperation(value) {
        this._operation = value;
    }
    /**
     * Remove rows from visible
     */
    clearRows() {
        this.visibleRows = [];
    }
    /**
     * Replace data for specified row
     * @param targetRow
     * @param trackBy
     */
    replaceData(targetRow, trackBy) {
        const rowIndex = this._rowsStack.findIndex((listRow) => {
            return trackBy(listRow.data, targetRow);
        });
        if (rowIndex > -1) {
            this._rowsStack[rowIndex] = new Row(targetRow, RowType.Simple, { initialExpand: this._initialExpand });
            this._updateVisibleRows();
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * Update data for specified row
     * @param rows
     * @param trackBy
     */
    updateData(rows, trackBy) {
        if (Array.isArray(rows)) {
            let updateSuccess = false;
            rows.forEach((item) => {
                if (this.updateRow(item, trackBy)) {
                    updateSuccess = true;
                }
            });
            this._updateVisibleRows();
            return updateSuccess;
        }
        else {
            const updated = this.updateRow(rows, trackBy);
            this._updateVisibleRows();
            return updated;
        }
    }
    /**
     * Remove data for specified row
     * @param data
     */
    removeData(data) {
        const removedRows = [];
        const defaultTrackBy = (row, target) => {
            return row === target;
        };
        if (Array.isArray(data)) {
            //
            data.forEach((item) => {
                removedRows.push(...this.removeRow(item, defaultTrackBy));
            });
        }
        else if (isFunction(data)) {
            removedRows.push(...this.removeRow(null, data));
        }
        else if (isObject(data)) {
            removedRows.push(...this.removeRow(data, defaultTrackBy));
        }
        if (removedRows.length > 0) {
            this._updateVisibleRows();
            this._rowsRemoved$.next(removedRows);
        }
        return !!removedRows.length;
    }
    swapRows(row1, row2, selectedRows, isMultipleDrag = false) {
        let tmpEl;
        const rowsStack = this._rowsStack;
        const row1GlobalIndex = rowsStack.indexOf(row1);
        const row2GlobalIndex = rowsStack.indexOf(row2);
        tmpEl = rowsStack[row1GlobalIndex];
        rowsStack[row1GlobalIndex] = rowsStack[row2GlobalIndex];
        rowsStack[row2GlobalIndex] = tmpEl;
        if (isMultipleDrag && Array.isArray(selectedRows)) {
            if (!selectedRows.includes(row1)) {
                selectedRows = [row1, ...selectedRows];
            }
            selectedRows
                .filter((selectedRow) => {
                return selectedRow !== row1;
            })
                .forEach((selectedRow) => {
                const idx = rowsStack.indexOf(selectedRow);
                rowsStack.splice(idx, 1);
            });
            const indexToInsertAfter = rowsStack.indexOf(tmpEl);
            rowsStack.splice(indexToInsertAfter, 1);
            selectedRows.forEach((selectedRow, offset) => {
                rowsStack.splice(indexToInsertAfter + offset, 0, selectedRow);
            });
        }
        this._rowsStack = [...rowsStack];
    }
    destroy() {
        this._destroyRowsStack();
        this._store.clear();
        this._destroy$.next();
        this._destroy$.complete();
    }
    toggleRowGroup(rowData) {
        const row = this.visibleRows.find((visibleRow) => visibleRow.data === rowData);
        row.toggleRowExpandStatus();
        this._updateVisibleRows();
    }
    finishReorder() {
        // TODO fixme remove or improve
        if (this.groupEnabled) {
            let group;
            this._rowsStack
                .forEach((row, index) => {
                if (row.isGroup && row !== group) {
                    group = row;
                }
                else {
                    row.index = index - this._rowsStack.indexOf(group) - 1;
                }
            });
        }
        this._updateVisibleRows();
    }
    _updateRowsStack(rows) {
        this._destroyRowsStack();
        if (this.groupEnabled) {
            this._store.clear();
            this._rowsStack = [...this.groupRowsBy(rows)];
        }
        else {
            rows = rows.map((row) => {
                return new Row(row, RowType.Simple, { initialExpand: this._initialExpand });
            });
            this._rowsStack = [...rows];
        }
    }
    _extendRowsStack(rows) {
        if (this.groupEnabled) {
            this._rowsStack = [...this.groupRowsBy(rows)];
        }
        else {
            rows = rows.map((row) => {
                return new Row(row, RowType.Simple, { initialExpand: this._initialExpand });
            });
            this._rowsStack = [...this._rowsStack, ...rows];
        }
    }
    _destroyRowsStack() {
        this._rowsStack.forEach((row) => row.destroy());
    }
    _updateVisibleRows() {
        this.visibleRows = this._rowsStack
            .filter((row, index) => {
            return (!row.isChild && !row.isGroupFooter) || row.visible;
        });
    }
    updateRow(targetRow, trackBy) {
        if (trackBy === void 0) {
            trackBy = (row, target) => {
                return row === target;
            };
        }
        const targetIndex = this._rowsStack.findIndex((listRow) => trackBy(listRow.data, targetRow));
        if (targetIndex !== -1) {
            const updateTarget = this._rowsStack[targetIndex];
            const updatedData = Object.assign({}, updateTarget.data, targetRow);
            this._rowsStack[targetIndex] = new Row(updatedData, updateTarget.type, {
                parent: updateTarget.parent,
                initialExpand: updateTarget.expanded,
            });
            return true;
        }
        return false;
    }
    /**
     * Remove row from
     * @param targetRow
     * @param trackBy
     */
    removeRow(targetRow, trackBy) {
        const rows = this._rowsStack;
        const removedRows = [];
        rows.forEach((listRow, index) => {
            if (trackBy(listRow.data, targetRow)) {
                const removedRow = rows.splice(index, 1);
                removedRows.push(removedRow);
            }
        });
        return removedRows;
    }
    /**
     * Split existing rows by groups and store them for future use
     */
    groupRowsBy(rows) {
        if (!this._groupByFn || !this._compareByFn) {
            return rows;
        }
        let groupRows = [];
        const footerRows = new Map();
        rows.forEach((row) => {
            const mainGroup = this._groupByFn(row);
            const mainGroupKey = this._compareByFn(mainGroup);
            if (!this._store.has(mainGroupKey)) {
                const group = new GroupRow(mainGroup, this._initialExpand);
                group.index = groupRows.length;
                groupRows.push(group);
                const childRow = new ChildRow(row, group);
                this._store.set(mainGroupKey, group);
                group.children.push(childRow);
            }
            else {
                const group = this._store.get(mainGroupKey);
                const childRow = new ChildRow(row, group);
                group.children.push(childRow);
            }
        });
        groupRows.forEach((groupRow) => {
            const footerIndex = groupRow.children
                .findIndex((row) => {
                return this._footerRowFn(row.data, Object.assign(Object.assign({}, groupRow.data), { children: groupRow.childrenData }));
            });
            if (footerIndex !== -1) {
                const footerRow = groupRow.children.splice(footerIndex, footerIndex + 1)[0];
                groupRow.children.push(new GroupFooterRow(footerRow.data, groupRow));
            }
        });
        return Array.from(this._store.values())
            .reduce((acc, item) => {
            if (item.isGroup) {
                acc.push(item, ...item.children);
            }
            else {
                acc.push(item);
            }
            return acc;
        }, []);
    }
}

class ExternalParamsController {
    constructor(_router, _route, _persistance, _paginator, _sorting, _queryParamsEnabled) {
        this._router = _router;
        this._route = _route;
        this._persistance = _persistance;
        this._paginator = _paginator;
        this._sorting = _sorting;
        this._queryParamsEnabled = _queryParamsEnabled;
        this._externalParams = {
            page: null,
            limit: null,
            sortName: null,
            sortDirection: null,
        };
        this._destroy$ = new Subject();
        this.initialize();
    }
    get externalSorting() {
        if (!this._enabled || !this._sortName || !this._sortDirection) {
            return null;
        }
        return {
            value: this._sortName,
            direction: this._sortDirection,
        };
    }
    get _enabled() {
        return this._queryParamsEnabled || this._persistance.enabled;
    }
    get _page() {
        return this._externalParams.page;
    }
    get _limit() {
        return this._externalParams.limit;
    }
    get _sortName() {
        return this._externalParams.sortName;
    }
    get _sortDirection() {
        return this._externalParams.sortDirection;
    }
    initialize() {
        // Restore from localStorage parameters if persistance enabled
        if (this._persistance.enabled) {
            this._restorePaginationParams(this._persistance.getDataFromScope('paging'));
            this._restoreSortingParams(this._persistance.getDataFromScope('sorting'));
        }
        // Restore from queryParams and override if persistance already had values for those params
        if (this._queryParamsEnabled) {
            this._restorePaginationParams(this._route.snapshot.queryParams);
            this._restoreSortingParams(this._route.snapshot.queryParams);
        }
        // Set initial pagination params
        // The same initial, but for sorting placed in list-controller "tranformTemplatesToColumns" method
        if (this._paginator.enabled && !this._paginator.infinityScrollEnabled) {
            if (this._limit) {
                this._paginator.setLimit(this._limit);
            }
            if (this._page) {
                this._paginator.goToPage(this._page);
            }
        }
        // Update pagination in stores
        this._updatePaginationParams(this._paginator.queryPageStrategy);
        this._listenPaginatorChanges();
        this._listenSortingChanges();
    }
    clearSortingParams() {
        this._updateSortingParams(null, null);
    }
    destroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _restorePaginationParams(values) {
        if (values === null || values === void 0 ? void 0 : values.page) {
            this._externalParams.page = +values.page;
        }
        if (values === null || values === void 0 ? void 0 : values.limit) {
            this._externalParams.limit = +values.limit;
        }
    }
    _restoreSortingParams(values) {
        if (values === null || values === void 0 ? void 0 : values.sortName) {
            this._externalParams.sortName = values.sortName;
        }
        if (values === null || values === void 0 ? void 0 : values.sortDirection) {
            this._externalParams.sortDirection = values.sortDirection;
        }
    }
    _listenPaginatorChanges() {
        merge(this._paginator.pageChanged$, this._paginator.pages$, this._paginator.pageReset$)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._updatePaginationParams(this._paginator.queryPageStrategy);
        });
    }
    _listenSortingChanges() {
        merge(this._sorting.initialized$, this._sorting.sortingChanged$)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            const sorting = this._sorting.value;
            if (sorting) {
                this._updateSortingParams(sorting.value, sorting.direction);
            }
            else {
                this.clearSortingParams();
            }
        });
    }
    _updateSortingParams(name, direction) {
        if (this._queryParamsEnabled) {
            this._replaceState({
                sortName: name,
                sortDirection: direction,
            });
        }
        if (this._persistance.enabled) {
            if (name && direction) {
                this._persistance.saveDataToScope('sorting', { sortName: name, sortDirection: direction });
            }
            else {
                this._persistance.saveDataToScope('sorting', null);
            }
        }
    }
    _updatePaginationParams(params) {
        if (this._queryParamsEnabled) {
            this._replaceState(params);
        }
        if (this._persistance.enabled) {
            this._persistance.saveDataToScope('paging', params);
        }
    }
    _replaceState(data) {
        const url = new URL(window.location.href);
        Object.keys(data)
            .filter((name) => data[name] !== undefined && data[name] !== null)
            .forEach((name) => {
            url.searchParams.set(name, data[name]);
        });
        history.replaceState({}, null, url.pathname + url.search);
    }
}

class ActionsController {
    constructor() {
        this._actions = [];
        this._destroy$ = new Subject();
    }
    get destroy$() {
        return this._destroy$.asObservable();
    }
    get actions() {
        return this._actions;
    }
    get hasActions() {
        return this._actions.length > 0;
    }
    setFilterRef(ref) {
        this._filterRef = ref;
    }
    setActions(actions) {
        this.clearActions();
        this._actions = actions;
        if (this._reorderAction) {
            this._actions.unshift(this._reorderAction);
        }
    }
    addReorderAction(action) {
        const actionClickFn = action.click;
        this._actions.unshift(action);
        action.click = () => {
            this._filterRef.updateActions([this._doneAction]);
            this._filterRef.hideKeywordField();
            this._filterRef.hideFiltersBtn();
            actionClickFn(null);
        };
        this._reorderAction = action;
    }
    addReorderDoneAction(action) {
        this._doneAction = action;
        const actionClickFn = action.click;
        this._doneAction.click = () => {
            this._filterRef.updateActions(this._actions);
            this._filterRef.showKeywordField();
            this._filterRef.showFiltersBtn();
            actionClickFn(null);
        };
    }
    clearActions() {
        this._actions = [];
        this._destroy$.next();
    }
    updateDisabledState() {
        this._filterRef.updateDisabledState();
    }
}

var PaginationStrategy;
(function (PaginationStrategy) {
    PaginationStrategy["Page"] = "page";
    PaginationStrategy["Offset"] = "offset";
    PaginationStrategy["None"] = "none";
})(PaginationStrategy || (PaginationStrategy = {}));

class PaginationController {
    constructor() {
        this.limit = 25;
        this.manual = false;
        this.page = 1; // Active page
        this.offset = 0;
        // public pagesArray = [];
        this.displayed = 0;
        this._pages$ = new BehaviorSubject(0); // Total pages
        this._strategy = PaginationStrategy.None;
        this._removedRows = 0;
        this._pageChanged$ = new Subject();
        this._pageReset$ = new Subject();
        this._onDestroy$ = new Subject();
        this._loadMoreEnabled = false;
        this._infinityScrollEnabled = false;
        this._loadMoreText = 'Load More';
        this._limits = [10, 25, 50, 100, 200];
    }
    // Total pages
    set pages(value) {
        this._pages$.next(value);
    }
    get pages() {
        return this._pages$.getValue();
    }
    get pages$() {
        return this._pages$
            .pipe(distinctUntilChanged());
    }
    /**
     * Fire if page was changed
     */
    get pageChanged$() {
        return this._pageChanged$.pipe(takeUntil(this._onDestroy$));
    }
    get pageReset$() {
        return this._pageReset$.asObservable();
    }
    /**
     * Get enabled
     */
    get enabled() {
        return !this.hasNoneStrategy;
    }
    /**
     * Get Limits
     */
    get limits() {
        return this._limits;
    }
    /**
     * Set limits, update pages array and set new limit per page
     *
     * @param value
     */
    set limits(value) {
        this._limits = value;
        if (this.limits.length > 0 && this.limits.indexOf(this.limit) === -1) {
            this.limit = this.limits[0];
        }
        else if (this.limits.length === 0) {
            this.limit = this.records;
        }
    }
    /**
     * Get query for request
     */
    get query() {
        switch (this.strategy) {
            case PaginationStrategy.Page:
                return this.queryPageStrategy;
            case PaginationStrategy.Offset:
                return this.queryOffsetStrategy;
        }
        return {};
    }
    get loadMoreQuery() {
        switch (this.strategy) {
            case PaginationStrategy.Page:
                return this.query;
            case PaginationStrategy.Offset:
                const query = this.queryOffsetStrategy;
                query.limit = query.offset + query.limit;
                query.offset = 0;
                return query;
        }
        return {};
    }
    /**
     * Query for Page Strategy
     */
    get queryPageStrategy() {
        return this.hasNoneStrategy
            ? {}
            : {
                page: this.page || 1,
                limit: this.limit || 10,
            };
    }
    /**
     * Query for Offset Strategy
     */
    get queryOffsetStrategy() {
        const page = this.page - 1 || 0;
        const limit = this.limit || 5;
        return {
            offset: page * limit,
            limit,
        };
    }
    /**
     * Get query for load only count of deleted rows
     */
    get loadDeletedOffsetQuery() {
        const paginationOffset = this.limit * this.page;
        const actualOffset = Math.min(this.records, paginationOffset);
        const offset = Math.max(0, actualOffset - this._removedRows);
        return {
            offset,
            limit: this._removedRows,
        };
    }
    get initialized() {
        return !!this.pages;
    }
    get strategy() {
        return this._strategy;
    }
    set strategy(strategy) {
        this._strategy = (strategy === undefined) ? PaginationStrategy.Page : strategy;
    }
    /**
     * Check if pagination in Page Strategy Mode
     */
    get hasPageStrategy() {
        return this.strategy === PaginationStrategy.Page;
    }
    /**
     * Check if pagination in Offset Strategy Mode
     */
    get hasOffsetStrategy() {
        return this.strategy === PaginationStrategy.Offset;
    }
    /**
     * Check if pagination in None Strategy Mode
     */
    get hasNoneStrategy() {
        return this.strategy === PaginationStrategy.None;
    }
    /**
     * If prev page can be activated
     *
     */
    get hasPrevPage() {
        switch (this.strategy) {
            case PaginationStrategy.Page:
                return this._hasPrevPagePageStrategy;
            case PaginationStrategy.Offset:
                return this._hasPrevPageOffsetStrategy;
        }
        return false;
    }
    /**
     * If next page can be activated
     */
    get hasNextPage() {
        switch (this.strategy) {
            case PaginationStrategy.Page:
                return this._hasNextPagePageStrategy;
            case PaginationStrategy.Offset:
                return this._hasNextPageOffsetStrategy;
        }
        return false;
    }
    get loadMoreEnabled() {
        return this._loadMoreEnabled;
    }
    get loadMoreText() {
        return this._loadMoreText;
    }
    get infinityScrollEnabled() {
        return this._infinityScrollEnabled;
    }
    /**
     * SP-T1974
     * Showing 26-50 of 333 results sorted by Name, Ascending
     * Showing 0 results sorted by Name, Ascending
     */
    get statusLabel() {
        const current = (this.page - 1) * this.limit;
        const from = current + 1;
        const to = Math.min(this.records, current + this.limit);
        return `${from}-${to}`;
    }
    get state() {
        return {
            enabled: this.enabled,
            strategy: this.strategy,
            page: this.page,
            offset: this.offset,
            limit: this.limit,
            records: this.records,
            displayed: this.displayed,
        };
    }
    initWithConfig(config, loadMore, infinityScrollEnabled = false) {
        if (config) {
            if (config.limits) {
                this.limits = config.limits;
            }
            if (config.limit) {
                this.limit = config.limit;
            }
            this.strategy = config.strategy;
        }
        if (loadMore) {
            this.setLoadMore(loadMore);
        }
        this._infinityScrollEnabled = infinityScrollEnabled;
    }
    /**
     * If pagination has prev page when Page Strategy
     */
    get _hasPrevPagePageStrategy() {
        return this.page > 1 && this.pages > 1;
    }
    /**
     * If pagination has prev page when Offset Strategy
     */
    get _hasPrevPageOffsetStrategy() {
        return this.offset >= this.limit && this.records > 1;
    }
    /**
     * If pagination has next page when Page Strategy
     */
    get _hasNextPagePageStrategy() {
        return this.page < this.pages && this.pages > 1;
    }
    /**
     * If pagination has next page when Offset Strategy
     */
    get _hasNextPageOffsetStrategy() {
        return (this.offset + this.limit) < this.records && this.records > 1;
    }
    /**
     * Update paging config and all related fields
     *
     * @param config
     * @param displayedRecords
     * @param loadMoreOperation
     */
    updatePaging(config, displayedRecords = 0, loadMoreOperation = false) {
        if (!loadMoreOperation) {
            this._fromParams(config);
            this.displayed = displayedRecords;
        }
        else {
            this.records = config.records;
            if (this.records < this.displayed) {
                this.displayed = this.records;
            }
            this._removedRows = 0;
        }
        this._updateTotalPages();
    }
    // /**
    //  * Update paging when data source not remove
    //  * @param {any[]} rows
    //  */
    /*public updatePagingManual(rows: any[]) {
      if (Array.isArray(rows) && rows.length > 0) {
        this.records = rows.length;
        this.pages = Math.ceil(rows.length / this.limit);
      }
  
      this.updatePagesArray();
      this.updateDisplayed();
    }*/
    /**
     * Update pages array with new pages count
     */
    // public updatePagesArray() {
    //   const MIDDLE = 2;
    //   const pagesArr = [];
    //   let from = 0;
    //   let to = 0;
    //   if (this.page < MIDDLE) {
    //     from = MIDDLE - 1;
    //     to = MIDDLE + 1;
    //   } else if (this.page >= MIDDLE && this.page <= this.pages - MIDDLE + 1) {
    //     from = this.page - 1;
    //     to = this.page + 1;
    //   } else if (this.page > this.pages - MIDDLE + 1) {
    //     from = this.pages - MIDDLE - 1;
    //     to = this.pages;
    //   }
    //   if (!this.pages || this.pages < 5) {
    //     from = 1;
    //     to = this.pages || 0;
    //   }
    //   for (let i = from; i <= to; i++) {
    //     pagesArr.push(i);
    //   }
    //   this.pagesArray = Object.assign([], pagesArr);
    // }
    setLoadMore(config) {
        this._loadMoreEnabled = !!config;
        if (this._loadMoreEnabled && isObject(config) && config.label) {
            this._loadMoreText = config.label;
        }
    }
    /**
     * Update dispayed records counter
     */
    // public updateDisplayed() {
    //   this.displayed = this.getVisibleRecords();
    // }
    /**
     * Return count of records that could be shown on page
     */
    // public getVisibleRecords() {
    //   const diff = this.hasOffsetStrategy
    //     ? this.records - this.offset
    //     : this.limit;
    //
    //   return diff < this.limit
    //     ? diff
    //     : this.displayed;
    // }
    getVisibleRecords() {
        return this.displayed;
    }
    /**
     * Set new limit
     *
     * @param limit
     */
    setLimit(limit) {
        this.limit = limit;
        this.resetPaging();
        this._pageChanged$.next({
            type: PageChangeType.LimitChanged,
            payload: limit,
        });
    }
    /**
     * If page is activate page
     *
     * @param page
     */
    isActive(page) {
        return page === this.page;
    }
    /**
     * Go to page
     *
     * @param page
     */
    goToPage(page) {
        if (page >= 1 && this.page !== page) {
            this.page = page;
            this._updateOffset();
            this._pageChanged$.next({
                type: PageChangeType.Default,
                payload: page,
            });
        }
    }
    /**
     * Reset paging like it was just initialized
     */
    resetPaging() {
        this.page = 1;
        this.offset = 0;
    }
    /**
     * Go to next page
     */
    goNext() {
        if (this.hasNextPage) {
            this.page++;
            this._updateOffset();
            this._pageChanged$.next({
                type: PageChangeType.Default,
                payload: this.page,
            });
        }
    }
    /**
     * Go to first page
     */
    goFirst() {
        if (this.page > 1) {
            this.page = 1;
            this._updateOffset();
            this._pageChanged$.next({
                type: PageChangeType.Default,
                payload: this.page,
            });
        }
    }
    /**
     * Go to prev page
     */
    goPrev() {
        if (this.page > 1) {
            this.page--;
            this._updateOffset();
            this._pageChanged$.next({
                type: PageChangeType.Default,
                payload: this.page,
            });
        }
    }
    /**
     * Go to last page
     */
    goLast() {
        if (this.page !== this.pages) {
            this.page = this.pages;
            this._updateOffset();
            this._pageChanged$.next({
                type: PageChangeType.Default,
                payload: this.page,
            });
        }
    }
    /**
     * Update count of deleted rows. This count will be applied for offset calc
     *
     * @param count
     */
    removeRows(count) {
        this._removedRows += count;
    }
    updatePagination() {
        this._updateTotalPages();
    }
    /**
     * Destroy
     */
    destroy() {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }
    /**
     * Update paging state
     *
     * @param params
     */
    _fromParams(params) {
        var _a;
        if (!this.loadMoreEnabled) {
            this.limit = (_a = params.limit) !== null && _a !== void 0 ? _a : 25;
        }
        this.records = params.records;
        this.manual = params.manual;
        this.pages = params.pages || 0;
    }
    /**
     * Calc and update offset
     */
    _updateOffset() {
        this.offset = this.limit * (this.page - 1);
    }
    /**
     * Calc and update total count of pages
     */
    _updateTotalPages() {
        this.pages = Math.ceil(this.records / this.limit);
    }
}

const SHOW_DELETED_FILTERS_KEY = '_showDeleted_';
class List {
    constructor(_el, config = {}, _fsScroll, _selectionDialog, _router, _route, _persistance, _inDialog) {
        this._el = _el;
        this.config = config;
        this._fsScroll = _fsScroll;
        this._selectionDialog = _selectionDialog;
        this._router = _router;
        this._route = _route;
        this._persistance = _persistance;
        this._inDialog = _inDialog;
        this.filters = [];
        this.initialized$ = new BehaviorSubject(false);
        this.loading$ = new BehaviorSubject(false);
        this.paging = new PaginationController();
        this.columns = new ColumnsController();
        this.actions = new ActionsController();
        this.dataController = new DataController();
        this.sorting = new SortingController();
        this.filterConfig = null;
        this.fetchComplete$ = new Subject();
        this.filtersReady$ = new Subject();
        this.status = true;
        this.chips = false;
        this.filterInput = true;
        this.queryParam = false;
        this.restoreMode = false;
        this.initialFetch = true;
        // Empty state
        this.emptyStateEnabled = false;
        this.onDestroy$ = new Subject();
        this._fetch$ = new Subject();
        this._filtersQuery = new BehaviorSubject(null);
        this._activeFiltersCount$ = this._filtersQuery
            .pipe(map((v) => Object.keys(v).length), shareReplay());
        this._initialize(config);
        this._headerConfig = new StyleConfig(config.header);
        this._groupCellConfig = new StyleConfig(config.cell);
        this._cellConfig = new StyleConfig(config.cell);
        this._footerConfig = new StyleConfig(config.footer);
        this.initialized$.next(true);
        this.subscribe();
        if (this.initialFetch) {
            this.dataController.setOperation(FsListState.Load);
            this._fetch$.next();
        }
    }
    get fetch$() {
        return this._fetch$.asObservable();
    }
    get hasSavedFilters() {
        return !!this.filterConfig.savedFilters;
    }
    get filtersQuery() {
        return this._filtersQuery.getValue();
    }
    get activeFiltersCount$() {
        return this._activeFiltersCount$;
    }
    fetchRemote(query) {
        const options = {
            state: this.dataController.operation,
        };
        const result = this.fetchFn(query, options);
        return result instanceof Promise ? from(result) : result;
    }
    // public loadLocal() {
    //   this.paging.updatePagingManual(this._rows);
    //   const from = (this.paging.page - 1) * this.paging.limit;
    //   const to = (this.paging.page === 1) ? this.paging.limit : this.paging.limit * this.paging.page;
    //   const sliceOfRows = this._rows.slice(from, to);
    //   this.data$.next(sliceOfRows);
    // }
    /**
     * Transform templates for using
     *
     * @param templates
     */
    tranformTemplatesToColumns(templates) {
        const defaultConfigs = {
            header: this._headerConfig,
            groupCell: this._groupCellConfig,
            cell: this._cellConfig,
            footer: this._footerConfig,
        };
        this.columns.setDefaults(defaultConfigs);
        this.columns.initializeColumns(templates);
        // Set sortBy default column
        this._updateSortingColumns();
        // Default sort by
        const externalSorting = this.externalParams.externalSorting;
        const initialSortConfig = externalSorting || this.config.sort;
        this.sorting.initialSortBy(initialSortConfig);
        if (externalSorting && !this.sorting.isDefined) {
            this.externalParams.clearSortingParams();
            console.warn('Not able to restore persisted sorting params.', externalSorting);
        }
        this._initFilters();
        this._initInfinityScroll();
    }
    reload() {
        this.loading$.next(true);
        this.dataController.setOperation(FsListState.Reload);
        if (this.fsScrollInstance) {
            this.paging.resetPaging();
            this.dataController.clearRows();
            this.fsScrollInstance.reload();
        }
        else {
            this._fetch$.next();
        }
    }
    /**
     * Watch page changes
     */
    subscribe() {
        this.paging.pageChanged$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((event) => {
            this.dataController.setOperation(FsListState.PageChange);
            // Remove all rows if limits was changed
            if (event.type === PageChangeType.LimitChanged && this.paging.hasPageStrategy) {
                this.dataController.clearRows();
            }
            if (this.paging.hasOffsetStrategy) {
                this.paging.updatePagination();
                if (this.selection) {
                    this.selection.updateVisibleRecordsCount(this.paging.getVisibleRecords());
                    this.selection.updateTotalRecordsCount(this.paging.records);
                    this.selection.pageChanged(this.scrollable);
                }
            }
            if (!this.scrollable && !this.paging.loadMoreEnabled) {
                const contains = [].slice.call(document.querySelectorAll('.cdk-overlay-container')).some((overlay) => {
                    return this._el.nativeElement.contains(overlay);
                });
                let el = this._el.nativeElement;
                if (!contains) {
                    const rect = this._el.nativeElement.getBoundingClientRect();
                    if ((rect.top + window.pageYOffset) < window.innerHeight) {
                        el = document.body;
                    }
                }
                this.fetchComplete$.asObservable()
                    .pipe(take(1), takeUntil(this.onDestroy$))
                    .subscribe((event) => {
                    var _a;
                    if ((_a = event === null || event === void 0 ? void 0 : event.scrollIntoView) !== null && _a !== void 0 ? _a : true) {
                        el.scrollIntoView({ behavior: 'smooth' });
                    }
                });
            }
            this._fetch$.next();
        });
        this.sorting.sortingChanged$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
            this.dataController.setOperation(FsListState.Sort);
            this.paging.page = 1;
            if (this.fsScrollInstance) {
                this.dataController.clearRows();
                this.fsScrollInstance.reload();
            }
            else {
                this._fetch$.next();
            }
        });
        this._listenVisibleColumnChanges();
        this._listenRowsRemove();
        this._listenFetch();
    }
    getData(trackBy) {
        let rowsData = this.dataController
            .visibleRowsData;
        if (trackBy) {
            rowsData = rowsData
                .filter(trackBy);
        }
        return rowsData
            .map((data) => {
            return cloneDeep(data);
        });
    }
    hasData(trackBy) {
        return this.dataController.visibleRowsData.some(trackBy);
    }
    /**
     * Toggle group mode status
     *
     * @param value
     */
    groupEnabled(value) {
        if (this.dataController.groupEnabled !== value) {
            this.dataController.groupEnabled = value;
            // Commented out because Ray wants to change group status in fetch map pipe
            // and it triggers extra requests
            // this.reload();
        }
    }
    destroy() {
        if (this._fsScrollSubscription) {
            this._fsScrollSubscription.unsubscribe();
        }
        if (this.paging) {
            this.paging.destroy();
        }
        if (this.sorting) {
            this.sorting.destroy();
        }
        if (this.selection) {
            this.selection.destroy();
        }
        if (this.filterConfig) {
            this.filterConfig = null;
        }
        if (this.externalParams) {
            this.externalParams.destroy();
        }
        this.columns.destroy();
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.dataController.destroy();
    }
    /**
     * Do initialization of table
     *
     * @param config
     */
    _initialize(config) {
        this._initVariables(config);
        this._initDefaultOptions(config);
        this._initRestore();
        this._initActions(config.actions);
        this._initPaging(config.paging, config.loadMore);
        this._initSelection(config.selection, this._selectionDialog);
        this._initGroups(config.group);
        this._initExternalParamsController();
        this._initializeData();
    }
    _initVariables(config) {
        var _a, _b;
        this.autoFocus = config.autoFocus;
        this.rowHighlight = (_a = config.rowHighlight) !== null && _a !== void 0 ? _a : true;
        this.heading = config.heading;
        this.trackBy = config.trackBy;
        this.subheading = config.subheading;
        this.rowActionsRaw = config.rowActions;
        this.rowClass = config.rowClass;
        this.rowEvents = config.rowEvents;
        this.restore = config.restore;
        this.persist = config.persist;
        this.filters = (_b = config.filters) !== null && _b !== void 0 ? _b : [];
        this.filterInitCb = config.filterInit;
        this.filterChangeCb = config.filterChange;
        this.savedFilters = config.savedFilters;
        this.scrollable = config.scrollable;
        this.noResults = config.noResults;
        this.emptyState = config.emptyState;
        this.fetchFn = config.fetch;
        this.afterFetchFn = config.afterFetch;
        this.beforeFetchFn = config.beforeFetch;
        this.afterInit = config.afterInit;
        this.style = config.style;
        this.columns.initConfig(config.column);
    }
    /**
     * Just init options by default it it wasn't specified
     *
     * @param config
     */
    _initDefaultOptions(config) {
        // We should prevent initial fetch in cases when it will be fetched in any case
        // As ex. scrollable or filter will do fetch in any cases
        if (config.initialFetch === false || config.scrollable) { // TODO fixme after tsmodel version update
            this.initialFetch = false;
        }
        if (config.status === false) {
            this.status = false;
        }
        if (config.chips) {
            this.chips = config.chips;
        }
        if (config.filterInput === false) {
            this.filterInput = false;
        }
        if (this._inDialog) {
            this.queryParam = false;
        }
        else {
            this.queryParam = (config.queryParam === void 0)
                ? true
                : config.queryParam;
        }
        if (config.sorts) {
            this.sorting.initFakeColumns(config.sorts);
        }
        if (!config.trackBy) {
            this.trackBy = 'id';
        }
        if (config.afterContentInit) {
            this.afterContentInit = () => {
                config.afterContentInit(this.paging.query, this.dataController.visibleRows);
            };
        }
    }
    /**
     * Init restore row action and append Show Deleted option into filters
     */
    _initRestore() {
        if (this.restore) {
            const restoreAction = new RowAction({
                label: this.restore.menuLabel || 'Restore',
                menu: true,
                click: (row) => {
                    this._restoreClick(this.restore.click, row);
                },
                restore: true,
            });
            if (!this.rowActionsRaw) {
                this.rowActionsRaw = [];
            }
            this.rowActionsRaw.push(restoreAction);
            if (!this.filters) {
                this.filters = [];
            }
            if (this.restore.filter !== false) {
                this.filters.push({
                    name: SHOW_DELETED_FILTERS_KEY,
                    type: ItemType.Checkbox,
                    label: this.restore.filterLabel || 'Show Deleted',
                });
            }
        }
    }
    /**
     * Init paging
     *
     * @param pagingConfig
     * @param loadMore
     */
    _initPaging(pagingConfig, loadMore) {
        this.paging.initWithConfig(pagingConfig, loadMore, !!this.scrollable);
    }
    /**
     * Split actions by categories
     */
    _initActions(actions) {
        if (actions) {
            this.actions.setActions(actions);
        }
        this.hasRowActions =
            (this.rowActionsRaw && this.rowActionsRaw.length > 0)
                || (this.groupActionsRaw && this.groupActionsRaw.length > 0);
    }
    _initSelection(selectionConfig, selectionDialog) {
        if (selectionConfig) {
            this.selection = new SelectionController(selectionConfig, this.trackBy, selectionDialog);
            this.selection.setRowsCallback(() => this.dataController.visibleRows);
        }
    }
    _initializeData() {
        this.dataController.setAdditionalConfigs({
            scrollable: !!this.scrollable,
            loadMoreEnabled: this.paging.loadMoreEnabled,
        });
    }
    _initGroups(groupConfig) {
        if (groupConfig) {
            this.dataController.setGroupConfig(groupConfig);
            this.groupActionsRaw = groupConfig.actions;
        }
    }
    _initExternalParamsController() {
        this.externalParams = new ExternalParamsController(this._router, this._route, this._persistance, this.paging, this.sorting, this.queryParam);
    }
    /**
     * Subscribe to fetch$ event with debounce
     */
    _listenFetch() {
        let fetch$ = this.fetch$;
        // Should wait until saved filters not loaded
        if (!!this.filters) {
            fetch$ = combineLatest([fetch$, this.filtersReady$])
                .pipe(map(([params]) => params));
        }
        fetch$
            .pipe(debounceTime(50), tap(() => {
            this.loading$.next(true);
        }), tap(() => {
            var _a;
            (_a = this.selection) === null || _a === void 0 ? void 0 : _a.closeSelectionDialog();
        }), map((params) => {
            let query = Object.assign({}, this.filtersQuery);
            if (this.paging.hasOffsetStrategy && (params === null || params === void 0 ? void 0 : params.loadOffset)) {
                query = Object.assign(query, this.paging.loadDeletedOffsetQuery);
            }
            else {
                const allRecordsRangeNeeded = (this.initialFetch
                    || this.dataController.operation === FsListState.Reload) && this.paging.loadMoreEnabled;
                query = allRecordsRangeNeeded ? Object.assign(query, this.paging.loadMoreQuery) : Object.assign(query, this.paging.query);
            }
            if (this.sorting.sortingColumn) {
                Object.assign(query, {
                    order: `${this.sorting.sortingColumn.name},${this.sorting.sortingColumn.direction}`,
                });
            }
            if (this.columns.configured) {
                query = Object.assign(Object.assign({}, query), { columns: this.columns.visibleColumnsNames });
            }
            return { params, query };
        }), switchMap(({ params, query }) => {
            if (this.columns.loadFnConfigured && !this.columns.columnsFetched) {
                return this.columns.loadRemoteColumnConfigs()
                    .pipe(mapTo({ params, query }));
            }
            return of({ params, query });
        }), switchMap(({ params, query }) => {
            if (this.beforeFetchFn) {
                return this.beforeFetchFn(query)
                    .pipe(map((beforeFetchQuery) => ({ params, query: beforeFetchQuery })), catchError((error) => {
                    console.error(error);
                    return EMPTY;
                }));
            }
            return of({ params, query });
        }), switchMap(({ params, query }) => {
            const remoteFetch = this.fetchRemote(query)
                .pipe(catchError((error) => {
                console.error(error);
                return EMPTY;
            }));
            return combineLatest([of({ params, query }), remoteFetch]);
        }), catchError((error) => {
            console.error(error);
            return EMPTY;
        }), takeUntil(this.onDestroy$))
            .subscribe(([paramsQuery, response]) => {
            this.initialFetch = false;
            this._completeFetch(paramsQuery.params, paramsQuery.query, response);
        });
    }
    _listenRowsRemove() {
        this.dataController.rowsRemoved$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((rows) => {
            if (this.paging.enabled) {
                const removedCount = rows.length;
                if (this.paging.hasPageStrategy) {
                    this._noDataPaginationUpdate(removedCount);
                }
                else {
                    // Fetch more if has something for fetch
                    if (this.dataController.hasData || this.paging.hasNextPage) {
                        this.dataController.setOperation(FsListState.LoadMore);
                        this.paging.removeRows(removedCount);
                        this._fetch$.next({ loadOffset: true });
                    }
                    else {
                        this._noDataPaginationUpdate(removedCount);
                    }
                }
            }
            // Remove from selection
            if (this.selection) {
                rows.forEach((row) => {
                    this.selection.removeRow(row.data);
                });
            }
        });
    }
    /**
     * Lister may have originally hidden columns, but visibility of those columns
     * can be changed programmaticaly in any time
     */
    _listenVisibleColumnChanges() {
        this.columns.visibleColumns$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
            this._updateSortingColumns();
        });
    }
    _initInfinityScroll() {
        if (this.fsScrollInstance) {
            return;
        }
        if (this.scrollable) {
            // Scrollable status by default
            if (this.scrollable.status === void 0) {
                this.scrollable.status = true;
            }
            this._fsScroll
                .component(this.scrollable.name)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((fsScrollInstance) => {
                this.fsScrollInstance = fsScrollInstance;
                this._fsScrollSubscription = fsScrollInstance
                    .subscribe(() => {
                    let startLoading = false;
                    const operation = this.dataController.operation;
                    // Initial loading if initialFetch equals false
                    if (!this.initialFetch
                        && !this.paging.initialized
                        && operation !== FsListState.Reload) {
                        this.dataController.setOperation(FsListState.Load);
                        startLoading = true;
                    }
                    else if (operation === FsListState.Reload ||
                        operation === FsListState.Filter ||
                        operation === FsListState.Sort) {
                        startLoading = true;
                    }
                    else if (this.paging.initialized && this.paging.hasNextPage) {
                        // Loading if content has been scrolled
                        startLoading = true;
                        this.dataController.setOperation(FsListState.Load);
                        this.paging.goNext();
                    }
                    if (startLoading) {
                        this._fetch$.next();
                        fsScrollInstance.loading();
                    }
                });
                this.dataController.remoteRowsChange$
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(() => {
                    fsScrollInstance.loaded();
                });
            });
        }
    }
    /**
     * Update and watch filter changes
     */
    _initFilters() {
        var _a;
        if (this.filterConfig) {
            return;
        }
        // Merge sorting and fake sorting cols
        // Fake sorting cols it's cols which don't represented in table cols, like abstract cols
        const sortValues = [
            ...this.sorting.sortingColumns,
            ...this.sorting.fakeSortingColumns,
        ].reduce((acc, column) => {
            const sortingItem = {
                name: column.title,
                value: column.name,
                default: this.sorting.sortingColumn && this.sorting.sortingColumn.name === column.name,
            };
            acc.push(sortingItem);
            return acc;
        }, []);
        const sortConfig = this.sorting.sortingColumn
            ? { value: this.sorting.sortingColumn.name, direction: this.sorting.sortingColumn.direction }
            : null;
        // Config
        this.filterConfig = {
            persist: this.persist,
            items: this.filters || [],
            savedFilters: this.savedFilters,
            // inline: this.inlineFilters,
            actions: this.actions.actions,
            queryParam: this.queryParam,
            autofocus: this.autoFocus,
            sorts: sortValues,
            sort: sortConfig,
            chips: this.chips,
            init: this._filterInit.bind(this),
            change: this._filterChange.bind(this),
            reload: ((_a = this.config.reload) !== null && _a !== void 0 ? _a : true) ? this.reload.bind(this) : null,
            sortChange: this._filterSort.bind(this),
        };
    }
    /**
     * Callback when Filter has been initialized
     *
     * @param filters
     */
    _filterInit(filters) {
        if (this.filterInitCb) {
            this.filterInitCb(filters);
        }
        this._filtersQuery.next(filters);
        this._checkRestoreFilter();
    }
    /**
     * Callback when Filter has been changed
     *
     * @param filterQuery
     * @param filterSort
     */
    _filterChange(filterQuery, filterSort) {
        if (this.filterChangeCb) {
            this.filterChangeCb(filterQuery, filterSort);
        }
        this._filtersQuery.next(filterQuery);
        this.restoreMode = false;
        // Restore option
        this._checkRestoreFilter();
        if (this.restore && this.restore.reload) {
            this.reload();
        }
        this.dataController.setOperation(FsListState.Filter);
        // Reset paging for request with correct offset
        this.paging.resetPaging();
        if (this.fsScrollInstance) {
            this.dataController.clearRows();
            this.fsScrollInstance.reload();
        }
        else {
            this._fetch$.next();
        }
    }
    _checkRestoreFilter() {
        // Restore option
        if (this.restore && this.filtersQuery[SHOW_DELETED_FILTERS_KEY]) {
            delete this.filtersQuery[SHOW_DELETED_FILTERS_KEY];
            Object.assign(this.filtersQuery, this.restore.query);
            this.restoreMode = true;
        }
    }
    // Callback when Filter sort has been changed
    _filterSort(filterQuery, filterSort) {
        if (filterSort) {
            this.sorting.sortByColumnWithName(filterSort.value);
            const sortDirection = filterSort.direction === 'asc' ? SortingDirection.asc : SortingDirection.desc;
            this.sorting.sortDirection(sortDirection);
        }
        else {
            // FIXME need to be refactored...
            this.sorting.sortingColumn = undefined;
            this.reload();
        }
    }
    _completeFetch(params, query, response) {
        var _a;
        if (!this.paging.page) {
            this.paging.page = 1;
        }
        if (response.paging) {
            const displayed = (Array.isArray(response.data) && response.data.length) || 0;
            this.paging.updatePaging(response.paging, displayed, this.dataController.operation === FsListState.LoadMore);
        }
        else if (this.paging.enabled) {
            console.log('%c FsList Warning ', 'color: white; background-color: #ffcc0b', 'Pagination does not configured properly. ' +
                'Pagination is enabled, but http response does not contain "paging" field. ' +
                'You have to set "paging: false" in config or add "paging" field to response.');
        }
        /// must be before selection, because seletion use records
        this.dataController.setRowsFromResponse(response.data);
        ///
        this._completeFetchUpdateSelecton(response);
        if (((_a = this.emptyState) === null || _a === void 0 ? void 0 : _a.validate) && this.emptyStateTemplate) {
            this.emptyStateEnabled = this.emptyState.validate(query, cloneDeep(this.dataController.visibleRowsData));
        }
        if (this.afterFetchFn) {
            this.afterFetchFn(query, this.dataController.visibleRowsData);
        }
        // case when have removed all results from last page
        if (this.dataController.visibleRows.length === 0
            && this.paging.page > 1
            && this.paging.page > this.paging.pages) {
            this.paging.goLast();
        }
        this.fetchComplete$.next({ scrollIntoView: params === null || params === void 0 ? void 0 : params.scrollIntoView });
        this.loading$.next(false);
    }
    _completeFetchUpdateSelecton(response) {
        // Update selection params
        if (this.selection) {
            if (this.paging.enabled) {
                this.selection.pageChanged(this.scrollable);
                this.selection.updateVisibleRecordsCount(this.paging.getVisibleRecords());
                this.selection.updateTotalRecordsCount(this.paging.records);
            }
            else {
                const count = response.paging && response.paging.records
                    || Array.isArray(response.data) && response.data.length;
                this.selection.updateVisibleRecordsCount(count);
                this.selection.updateTotalRecordsCount(count);
            }
            this.selection.selectedRowsIntersection(this.dataController.visibleRowsData);
        }
    }
    /**
     * Will do some actions if you removed item and item was last on his own page
     *
     * Ex: if list has 3 pages and on third page you have only one item. And you just deleted this item.
     * You must go to second page, but if it was last page and you can't go back -> just reload
     *
     * @param deletedCount
     */
    _noDataPaginationUpdate(deletedCount) {
        if (!this.dataController.hasData) {
            if (this.paging.page > 1) {
                this.paging.goToPage(this.paging.page - 1 || 1);
            }
            else {
                this.reload();
            }
        }
        this.paging.records -= deletedCount;
        this.paging.updatePagination();
        if (this.dataController.hasData && this.selection) {
            this.selection.updateVisibleRecordsCount(this.dataController.visibleRowsCount);
            this.selection.updateTotalRecordsCount(this.paging.records);
        }
    }
    /**
     * Temporary solution, will do auto subscribe if was returned Observable.
     *
     * TODO: MOVE THIS PEACE OF CODE TO SPECIAL PLACE
     *
     * @param restoreClickCallback
     * @param row
     */
    _restoreClick(restoreClickCallback, row) {
        const restoreClickResult = restoreClickCallback(row);
        if (restoreClickResult instanceof Observable) {
            restoreClickResult
                .pipe(take(1), takeUntil(this.onDestroy$))
                .subscribe({
                next: () => this.reload(),
                error: () => { },
            });
        }
    }
    _updateSortingColumns() {
        this.sorting.clearSortableColumns();
        this.columns.columns
            .filter((column) => column.sortable && column.visible)
            .forEach((column) => {
            this.sorting.addSortableColumn(column);
        });
    }
}

const FILTER_STORE_KEY = 'fs-list-persist';
class PersistanceController extends FsPersistanceStore {
    constructor(_store, _route) {
        super(_store, _route);
        this.STORE_KEY = FILTER_STORE_KEY;
    }
}
PersistanceController.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PersistanceController, deps: [{ token: i1$3.FsStore }, { token: i7.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable });
PersistanceController.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PersistanceController });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: PersistanceController, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.FsStore }, { type: i7.ActivatedRoute }]; } });

class FsListHeadingDirective {
}
FsListHeadingDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeadingDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListHeadingDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListHeadingDirective, selector: "[fs-list-heading],[fsListHeading]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-heading],[fsListHeading]'
                }]
        }] });

class FsListSubheadingDirective {
}
FsListSubheadingDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListSubheadingDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListSubheadingDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListSubheadingDirective, selector: "[fs-list-subheading],[fsListSubheading]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListSubheadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-subheading],[fsListSubheading]'
                }]
        }] });

class FsListHeadingContainerDirective {
}
FsListHeadingContainerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeadingContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListHeadingContainerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListHeadingContainerDirective, selector: "[fs-list-heading-container],[fsListHeadingContainer]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeadingContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-heading-container],[fsListHeadingContainer]'
                }]
        }] });

class FsListHeaderDirective {
}
FsListHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeaderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListHeaderDirective, selector: "[fs-list-header]", inputs: { colspan: "colspan", align: "align", className: ["class", "className"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListHeaderDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fs-list-header]' }]
        }], propDecorators: { colspan: [{
                type: Input
            }], align: [{
                type: Input
            }], className: [{
                type: Input,
                args: ['class']
            }] } });

class FsListCellDirective {
}
FsListCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListCellDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListCellDirective, selector: "[fs-list-cell]", inputs: { colspan: "colspan", align: "align", className: ["class", "className"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListCellDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fs-list-cell]' }]
        }], propDecorators: { colspan: [{
                type: Input
            }], align: [{
                type: Input
            }], className: [{
                type: Input,
                args: ['class']
            }] } });

class FsListFooterDirective {
}
FsListFooterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListFooterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListFooterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListFooterDirective, selector: "[fs-list-footer]", inputs: { colspan: "colspan", align: "align", className: ["class", "className"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListFooterDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fs-list-footer]' }]
        }], propDecorators: { colspan: [{
                type: Input
            }], align: [{
                type: Input
            }], className: [{
                type: Input,
                args: ['class']
            }] } });

class FsListGroupHeaderDirective extends FsListCellDirective {
}
FsListGroupHeaderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupHeaderDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FsListGroupHeaderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListGroupHeaderDirective, selector: "[fs-list-group-cell],[fs-list-group-header]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-group-cell],[fs-list-group-header]'
                }]
        }] });

class FsListGroupFooterDirective extends FsListCellDirective {
}
FsListGroupFooterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupFooterDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FsListGroupFooterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListGroupFooterDirective, selector: "[fs-list-group-footer]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupFooterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-group-footer]'
                }]
        }] });

class GroupExpandNotifierService {
    constructor() {
        this._expandStatusChanged$ = new Subject();
        this._destroy$ = new Subject();
    }
    get expandStatusChange$() {
        return this._expandStatusChanged$.pipe(takeUntil(this._destroy$));
    }
    toggleExpandStatus(rowData) {
        this._expandStatusChanged$.next(rowData);
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
GroupExpandNotifierService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GroupExpandNotifierService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
GroupExpandNotifierService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GroupExpandNotifierService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: GroupExpandNotifierService, decorators: [{
            type: Injectable
        }] });

class FsListGroupExpandTriggerDirective {
    constructor(_expandNotifier) {
        this._expandNotifier = _expandNotifier;
    }
    click(event) {
        event.preventDefault();
        event.stopPropagation();
        this._expandNotifier.toggleExpandStatus(this.row);
    }
}
FsListGroupExpandTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupExpandTriggerDirective, deps: [{ token: GroupExpandNotifierService }], target: i0.ɵɵFactoryTarget.Directive });
FsListGroupExpandTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListGroupExpandTriggerDirective, selector: "[fsListGroupExpandTrigger]", inputs: { row: "row" }, host: { listeners: { "click": "click($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListGroupExpandTriggerDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fsListGroupExpandTrigger]' }]
        }], ctorParameters: function () { return [{ type: GroupExpandNotifierService }]; }, propDecorators: { click: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], row: [{
                type: Input
            }] } });

class FsListColumnDirective {
    constructor() {
        this._columnAttributes = new ColumnAttributes();
    }
    get attributes() {
        return this._columnAttributes;
    }
    set visible(value) {
        this._columnAttributes.visible = value;
    }
    set title(value) {
        this._columnAttributes.title = value;
    }
    set name(value) {
        this._columnAttributes.name = value;
    }
    set customize(value) {
        this._columnAttributes.customize = value;
    }
    set sortable(value) {
        this._columnAttributes.sortable = value;
    }
    set sortableDefault(value) {
        this._columnAttributes.sortableDefault = value;
    }
    set direction(value) {
        this._columnAttributes.direction = value;
    }
    set align(value) {
        this._columnAttributes.align = value;
    }
    set width(value) {
        this._columnAttributes.width = value;
    }
    set className(value) {
        this._columnAttributes.className = value;
    }
}
FsListColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListColumnDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListColumnDirective, selector: "fs-list-column", inputs: { visible: ["show", "visible"], title: "title", name: "name", customize: "customize", sortable: "sortable", sortableDefault: "sortableDefault", direction: "direction", align: "align", width: "width", className: ["class", "className"] }, queries: [{ propertyName: "headerTemplate", first: true, predicate: FsListHeaderDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "headerConfigs", first: true, predicate: FsListHeaderDirective, descendants: true, static: true }, { propertyName: "groupHeaderTemplate", first: true, predicate: FsListGroupHeaderDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "groupHeaderConfigs", first: true, predicate: FsListGroupHeaderDirective, descendants: true, static: true }, { propertyName: "groupFooterTemplate", first: true, predicate: FsListGroupFooterDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "groupFooterConfigs", first: true, predicate: FsListGroupFooterDirective, descendants: true, static: true }, { propertyName: "cellTemplate", first: true, predicate: FsListCellDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "cellConfigs", first: true, predicate: FsListCellDirective, descendants: true, static: true }, { propertyName: "footerTemplate", first: true, predicate: FsListFooterDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "footerConfigs", first: true, predicate: FsListFooterDirective, descendants: true, static: true }, { propertyName: "expandTrigger", predicate: FsListGroupExpandTriggerDirective, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListColumnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'fs-list-column'
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { headerTemplate: [{
                type: ContentChild,
                args: [FsListHeaderDirective, { read: TemplateRef, static: true }]
            }], headerConfigs: [{
                type: ContentChild,
                args: [FsListHeaderDirective, { static: true }]
            }], groupHeaderTemplate: [{
                type: ContentChild,
                args: [FsListGroupHeaderDirective, { read: TemplateRef, static: true }]
            }], groupHeaderConfigs: [{
                type: ContentChild,
                args: [FsListGroupHeaderDirective, { static: true }]
            }], groupFooterTemplate: [{
                type: ContentChild,
                args: [FsListGroupFooterDirective, { read: TemplateRef, static: true }]
            }], groupFooterConfigs: [{
                type: ContentChild,
                args: [FsListGroupFooterDirective, { static: true }]
            }], expandTrigger: [{
                type: ContentChildren,
                args: [FsListGroupExpandTriggerDirective, { descendants: true }]
            }], cellTemplate: [{
                type: ContentChild,
                args: [FsListCellDirective, { read: TemplateRef, static: true }]
            }], cellConfigs: [{
                type: ContentChild,
                args: [FsListCellDirective, { static: true }]
            }], footerTemplate: [{
                type: ContentChild,
                args: [FsListFooterDirective, { read: TemplateRef, static: true }]
            }], footerConfigs: [{
                type: ContentChild,
                args: [FsListFooterDirective, { static: true }]
            }], visible: [{
                type: Input,
                args: ['show']
            }], title: [{
                type: Input
            }], name: [{
                type: Input
            }], customize: [{
                type: Input
            }], sortable: [{
                type: Input
            }], sortableDefault: [{
                type: Input
            }], direction: [{
                type: Input
            }], align: [{
                type: Input
            }], width: [{
                type: Input
            }], className: [{
                type: Input,
                args: ['class']
            }] } });

class FsListEmptyStateDirective {
}
FsListEmptyStateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListEmptyStateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListEmptyStateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListEmptyStateDirective, selector: "[fs-list-empty-state]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListEmptyStateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-empty-state]',
                }]
        }] });

const FS_LIST_DEFAULT_CONFIG = new InjectionToken('fs-list.default-config');
const FS_LIST_CONFIG = new InjectionToken('fs-list.config');

class FsListSavedFiltersComponent {
    constructor(_dialog, _externalParams, _vcRef) {
        this._dialog = _dialog;
        this._externalParams = _externalParams;
        this._vcRef = _vcRef;
    }
    get savedFiltersEnabled$() {
        return this._externalParams.savedFiltersController.enabled$;
    }
    showManageDialog() {
        // this._dialog.open(FsListManageSavedFiltersComponent, {
        //   viewContainerRef: this._vcRef,
        // });
    }
}
FsListSavedFiltersComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListSavedFiltersComponent, deps: [{ token: i1$2.MatDialog }, { token: i2$3.ExternalParamsController }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component });
FsListSavedFiltersComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsListSavedFiltersComponent, selector: "fs-list-saved-filters", ngImport: i0, template: "<ng-container *ngIf=\"savedFiltersEnabled$ | async\"><!--\n  -->, saved filter\n  <fs-filter-saved-filters-menu (manage)=\"showManageDialog()\"></fs-filter-saved-filters-menu>\n</ng-container>\n", components: [{ type: i2$3.FsSavedFiltersMenuComponent, selector: "fs-filter-saved-filters-menu", outputs: ["select", "clear", "manage"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i3.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListSavedFiltersComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-saved-filters',
                    templateUrl: './saved-filters.component.html',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.MatDialog }, { type: i2$3.ExternalParamsController }, { type: i0.ViewContainerRef }]; } });

class FsStatusComponent {
    constructor(_cdRef) {
        this._cdRef = _cdRef;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this.sorting.sortingChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._cdRef.markForCheck();
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    toggleDirection() {
        if (this.sorting.sortingColumn.direction === 'asc') {
            this.sorting.sortDirection(SortingDirection.desc);
        }
        else {
            this.sorting.sortDirection(SortingDirection.asc);
        }
    }
    setSortableColumn(column) {
        if (this.sorting.sortingColumn !== column) {
            this.sorting.sortBy(column);
        }
    }
    setLimit(limit) {
        this.paging.setLimit(limit);
    }
}
FsStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsStatusComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsStatusComponent, selector: "fs-list-status", inputs: { paging: "paging", sorting: "sorting", rows: "rows", scrollable: "scrollable", firstLoad: "firstLoad" }, host: { properties: { "class.first-load": "this.firstLoad", "class.fs-skeleton-placeholder": "this.firstLoad" } }, ngImport: i0, template: "<div class=\"status\">\n  <small>\n    <ng-container *ngIf=\"paging.enabled && !scrollable\">\n      <ng-container *ngIf=\"paging.records > 0; else emptyResults\">\n        Showing <a [fsMenuTriggerFor]=\"limitsMenu\">{{ paging.statusLabel }}</a> of {{ paging.records | number:'1.0':'en-US' }} results\n      </ng-container>\n      <ng-template #emptyResults>\n        Showing <a [fsMenuTriggerFor]=\"limitsMenu\">0</a> results\n      </ng-template>\n      <ng-container *ngTemplateOutlet=\"sortedBy\"></ng-container><!--\n  --></ng-container><!--\n\n  --><ng-container *ngIf=\"(!paging.enabled || scrollable) && paging.displayed > 0\">\n    <ng-container *ngIf=\"!scrollable; else scrollable\">\n      Showing\n      <span *ngIf=\"paging.displayed == 1\">{{ paging.displayed }} result </span>\n      <span *ngIf=\"paging.displayed > 1\">{{ paging.displayed | number:'1.0':'en-US' }} results </span>\n    </ng-container>\n\n    <ng-template #scrollable>\n      <span *ngIf=\"paging.records == 1\">{{ paging.records }} result </span>\n      <span *ngIf=\"paging.records > 1\">{{ paging.records | number:'1.0':'en-US' }} results </span>\n    </ng-template>\n    <ng-container *ngTemplateOutlet=\"sortedBy\"></ng-container><!--\n  --></ng-container><!--\n\n  --><fs-list-saved-filters class=\"saved-filters\"></fs-list-saved-filters>\n\n    <fs-menu [hidden]=\"!paging.enabled || scrollable\" #limitsMenu>\n      <ng-template\n        ngFor\n        let-limit\n        [ngForOf]=\"paging.limits\">\n        <ng-template fs-menu-item (click)=\"setLimit(limit)\">\n          {{ limit }}\n        </ng-template>\n      </ng-template>\n    </fs-menu>\n\n    <fs-menu [hidden]=\"!sorting.sortingColumn || paging.displayed === 0\" #orderColumnsMenu>\n      <!-- Real sorting columns -->\n      <ng-template\n        ngFor\n        let-column\n        [ngForOf]=\"sorting.sortingColumns\">\n        <ng-template fs-menu-item (click)=\"setSortableColumn(column)\">\n          <ng-container *ngIf=\"column.title; else sortByTemplate\">\n            {{ column.title }}\n          </ng-container>\n          <ng-template #sortByTemplate>\n            <ng-template [ngTemplateOutlet]=\"column.headerTemplate\"></ng-template>\n          </ng-template>\n        </ng-template>\n      </ng-template>\n\n      <!-- Fake sorting columns -->\n      <ng-template\n        ngFor\n        let-column\n        [ngForOf]=\"sorting.fakeSortingColumns\">\n        <ng-template fs-menu-item (click)=\"setSortableColumn(column)\">\n          {{ column.title }}\n        </ng-template>\n      </ng-template>\n    </fs-menu>\n\n    <ng-template #sortedBy>\n      <ng-container *ngIf=\"sorting.sortingColumn\">\n        sorted by\n        <ng-container *ngIf=\"sorting.sortingColumn.title; else sortByTemplate\">\n          <a class=\"order-toggle\" [fsMenuTriggerFor]=\"orderColumnsMenu\">{{ sorting.sortingColumn.title }}</a>,\n        </ng-container>\n        <ng-template #sortByTemplate>\n          <a class=\"order-toggle\" [fsMenuTriggerFor]=\"orderColumnsMenu\">\n            <ng-template [ngTemplateOutlet]=\"sorting.sortingColumn.headerTemplate\"></ng-template>\n          </a>,\n        </ng-template>\n        <a class=\"order-toggle\" (click)=\"toggleDirection()\">{{ sorting.sortingColumn.fullNameDirection }}</a><!--\n    --></ng-container><!--\n  --></ng-template>\n  </small>  \n</div>\n", styles: [":host.hidden-mobile{display:none!important}:host.first-load .status{visibility:hidden}.order-toggle{white-space:nowrap}a{cursor:pointer}\n"], components: [{ type: FsListSavedFiltersComponent, selector: "fs-list-saved-filters" }, { type: i2$1.FsMenuComponent, selector: "fs-menu", inputs: ["class", "buttonClass", "buttonType", "buttonColor"], outputs: ["opened", "closed"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2$1.FsMenuTriggerDirective, selector: "[fsMenuTriggerFor]", inputs: ["fsMenuTriggerFor"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2$1.FsMenuItemDirective, selector: "fs-menu-group,[fs-menu-item]" }], pipes: { "number": i3.DecimalPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush, preserveWhitespaces: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsStatusComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-status',
                    templateUrl: './status.component.html',
                    styleUrls: ['./status.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    preserveWhitespaces: true,
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { paging: [{
                type: Input
            }], sorting: [{
                type: Input
            }], rows: [{
                type: Input
            }], scrollable: [{
                type: Input
            }], firstLoad: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.first-load']
            }, {
                type: HostBinding,
                args: ['class.fs-skeleton-placeholder']
            }] } });

class FsListLoaderComponent {
    constructor() {
        this.placeholderWidths = [[], [], []];
        this.widths = [];
        this.cols = [];
        this.rows = [];
    }
    ngOnInit() {
        this.cols = this.columns > 5 ? this.columns.splice(0, 5) : this.columns;
        const length = this.cols.length;
        for (let w = length; w >= 0; w--) {
            this.widths[w] = w ? random(15, 100 / length, false) : 0;
        }
        for (let r = 0; r < 3; r++) {
            for (let c = 0; c < length; c++) {
                this.placeholderWidths[r].push(random(50, 90));
            }
        }
    }
    ngOnChanges(changes) {
        if (changes.loaderLines) {
            this.rows = new Array(this.loaderLines);
        }
    }
}
FsListLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsListLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsListLoaderComponent, selector: "fs-list-loader", inputs: { columns: "columns", loaderLines: "loaderLines" }, usesOnChanges: true, ngImport: i0, template: "<table>\n  <thead>\n    <tr>\n      <th *ngFor=\"let col of cols; let column = index\" [width]=\"widths[column] + '%'\">\n        <div class=\"fs-skeleton-placeholder\" [style.width]=\"(placeholderWidths[0][column]/2)  + '%'\"></div>\n      </th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr *ngFor=\"let row of rows; let index=index\">\n      <td *ngFor=\"let col of cols; let column = index\">\n        <div class=\"fs-skeleton-placeholder\" [style.width]=\"placeholderWidths[index][column] + '%'\"></div>\n      </td>\n    </tr>\n  </tbody>\n</table>\n", styles: ["table{width:100%;border-spacing:0}.fs-skeleton-placeholder{opacity:.5}:host ::ng-deep tbody tr:hover td{background:transparent}\n"], directives: [{ type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListLoaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-loader',
                    templateUrl: './loader.component.html',
                    styleUrls: ['./loader.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { columns: [{
                type: Input
            }], loaderLines: [{
                type: Input
            }] } });

class FsPaginationComponent {
    constructor(_cdRef) {
        this._cdRef = _cdRef;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        merge(this.pagination.pageChanged$, this.pagination.pages$)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._cdRef.markForCheck();
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
FsPaginationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsPaginationComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsPaginationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsPaginationComponent, selector: "fs-list-pagination", inputs: { pagination: "pagination", rows: "rows" }, ngImport: i0, template: "<ng-container *ngIf=\"!pagination.loadMoreEnabled; else loadMoreButton\">\n  <ng-container *ngIf=\"pagination?.pages\">\n    <a matRipple [matRippleRadius]=\"15\" [matRippleCentered]=\"true\" class=\"first\" [class.disabled]=\"!pagination.hasPrevPage\" (click)=\"pagination.goFirst()\">\n      <i class=\"material-icons\">first_page</i>\n    </a>\n\n    <a matRipple [matRippleRadius]=\"15\" [matRippleCentered]=\"true\" class=\"previous\" [class.disabled]=\"!pagination.hasPrevPage\" (click)=\"pagination.goPrev()\">\n      <i class=\"material-icons\">keyboard_arrow_left</i>\n    </a>\n\n    <div class=\"number\">\n      {{ pagination.page | fsFormatNumber }} of {{ pagination.pages | fsFormatNumber }}\n    </div>\n\n    <a matRipple [matRippleRadius]=\"15\" [matRippleCentered]=\"true\" class=\"next\" [class.disabled]=\"!pagination.hasNextPage\" (click)=\"pagination.goNext()\">\n      <i class=\"material-icons\">keyboard_arrow_right</i>\n    </a>\n\n    <a matRipple [matRippleRadius]=\"15\" [matRippleCentered]=\"true\" class=\"last\" [class.disabled]=\"!pagination.hasNextPage\" (click)=\"pagination.goLast()\">\n      <i class=\"material-icons\">last_page</i>\n    </a>\n  </ng-container>\n</ng-container>\n\n<ng-template #loadMoreButton>\n  <div class=\"fs-list-load-more\" *ngIf=\"pagination.hasNextPage\">\n    <button mat-button (click)=\"pagination.goNext()\">{{ pagination.loadMoreText }}</button>\n  </div>\n</ng-template>\n", styles: [":host{display:flex;justify-content:center;align-items:center;-webkit-user-select:none;user-select:none;width:100%}a{text-align:center;color:#000000de;text-decoration:none;font-size:15px;cursor:pointer;display:flex}a:not(.page){padding:10px}a.disabled{pointer-events:none;cursor:default;color:#d8d8d8}.number{font-size:90%}.fs-list-load-more button{width:100%;margin-top:10px}\n"], components: [{ type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3$2.MatRipple, selector: "[mat-ripple], [matRipple]", inputs: ["matRippleRadius", "matRippleDisabled", "matRippleTrigger", "matRippleColor", "matRippleUnbounded", "matRippleCentered", "matRippleAnimation"], exportAs: ["matRipple"] }], pipes: { "fsFormatNumber": i4.FsFormatNumberPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsPaginationComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list-pagination',
                    templateUrl: './pagination.component.html',
                    styleUrls: ['./pagination.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { pagination: [{
                type: Input
            }], rows: [{
                type: Input
            }] } });

class FsListContentInitDirective {
    constructor() { }
    ngAfterViewInit() {
        if (this.contentInitCallback) {
            this.contentInitCallback();
        }
    }
}
FsListContentInitDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListContentInitDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListContentInitDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListContentInitDirective, selector: "[fsListContentInit]", inputs: { contentInitCallback: ["fsListContentInit", "contentInitCallback"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListContentInitDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsListContentInit]'
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { contentInitCallback: [{
                type: Input,
                args: ['fsListContentInit']
            }] } });

class FsListComponent {
    constructor(reorderController, _defaultOptions, _scroll, _dialogRef, _drawerRef, _el, _selectionDialog, _dialog, _cdRef, _groupExpandNotifier, _router, _route, _persistance, _location) {
        this.reorderController = reorderController;
        this._defaultOptions = _defaultOptions;
        this._scroll = _scroll;
        this._dialogRef = _dialogRef;
        this._drawerRef = _drawerRef;
        this._el = _el;
        this._selectionDialog = _selectionDialog;
        this._dialog = _dialog;
        this._cdRef = _cdRef;
        this._groupExpandNotifier = _groupExpandNotifier;
        this._router = _router;
        this._route = _route;
        this._persistance = _persistance;
        this._location = _location;
        this.classFsList = true;
        this.loaderLines = 3;
        this.filtersReady = new EventEmitter();
        this.keywordVisible = true;
        // Event will fired if action remove: true will clicked
        this.rowRemoved = new EventEmitter();
        this.firstLoad = true;
        this._filterParamsReady = false;
        this._inDialog = !!this._dialogRef || !!this._drawerRef;
        this._destroy = new Subject();
    }
    set config(config) {
        this._initWithConfig(config);
    }
    set filterReference(component) {
        this._filterRef = component;
        this.list.actions.setFilterRef(component);
        this._emitFiltersReadyEvent();
    }
    /**
     * Set columns to config
     * Create Column Model instances
     */
    set columnTemplates(listColumnDirectives) {
        this._listColumnDirectives = listColumnDirectives;
        if (this.list) {
            this.list.tranformTemplatesToColumns(listColumnDirectives);
        }
    }
    set _emptyStateTemplate(template) {
        if (this.list) {
            this.list.emptyStateTemplate = template;
        }
    }
    /**
     * Return reference for filter
     */
    get filterRef() {
        return this._filterRef;
    }
    set groupEnabled(value) {
        this.list.groupEnabled(value);
    }
    get groupEnabled() {
        return this.list.dataController.groupEnabled;
    }
    get hasFilterKeyword() {
        return this.list.filterInput && this.keywordVisible;
    }
    get hasStatus() {
        return this.list.status &&
            (this.list.sorting.isDefined || this.list.paging.enabled) &&
            (!this.reorderController.enabled ||
                (this.reorderController.enabled && this.reorderController.status)) &&
            ((this.list.scrollable && this.list.scrollable.status) || !this.list.scrollable);
    }
    get paginatorVisible() {
        return this.list.paging.enabled
            && !this.firstLoad
            && !this.list.scrollable
            && !this.list.emptyStateEnabled
            && this.list.dataController.visibleRowsCount > 0
            && this.list.paging.pages > 1;
    }
    get filtersQuery() {
        return this.list.filtersQuery;
    }
    get pagingState() {
        return this.list.paging.state;
    }
    ngAfterContentInit() {
        if (this.list.afterInit) {
            this.list.afterInit(this);
        }
    }
    ngOnInit() {
        this._subscribeToRemoveRow();
        this._subscribeToGroupExpandStatusChange();
    }
    ngOnDestroy() {
        if (this.list) {
            this.list.destroy();
        }
        this._destroy.next();
        this._destroy.complete();
    }
    nextPage() {
        this.list.paging.goNext();
    }
    prevPage() {
        this.list.paging.goPrev();
    }
    firstPage() {
        this.list.paging.goFirst();
    }
    lastPage() {
        this.list.paging.goLast();
    }
    reload() {
        this.list.reload();
    }
    getData(trackBy) {
        return this.list.getData(trackBy);
    }
    hasData(trackBy) {
        return this.list.hasData(trackBy);
    }
    updateData(rows, trackBy) {
        return this.list.dataController.updateData(rows, trackBy);
    }
    replaceRow(row, trackBy) {
        return this.list.dataController.replaceData(row, trackBy);
    }
    updateSelectionConfig(config) {
        this.list.selection.updateConfig(config);
    }
    resetSelectionActions() {
        this.list.selection.resetActions();
    }
    enableSelection() {
        this.list.selection.enableSelection();
    }
    disableSelection() {
        this.list.selection.disableSelection();
    }
    removeData(data) {
        return this.list.dataController.removeData(data);
    }
    setHeading(heading) {
        this.list.heading = heading;
    }
    setSubheading(subheading) {
        this.list.subheading = subheading;
    }
    reorderStart() {
        this.reorderController.enableReorder();
    }
    reorderFinish() {
        this.reorderController.disableReorder();
    }
    setActions(actions) {
        if (actions) {
            this.list.actions.clearActions();
            this.list.actions.setActions(actions);
        }
    }
    filterReady() {
        this.list.filtersReady$.next();
        this._filterParamsReady = true;
        this._emitFiltersReadyEvent();
    }
    /**
     * Update visibility for specific column
     */
    columnVisibility(name, show) {
        this.columnsVisibility([{ name, show }]);
    }
    /**
     * Update visibility for list of specific columns
     */
    columnsVisibility(columns) {
        this.list.columns.updateVisibilityForCols(columns);
    }
    _emitFiltersReadyEvent() {
        if (!!this.filterRef && this._filterParamsReady) {
            this.filtersReady.emit();
            this.keywordVisible = this.filterRef.hasKeyword;
            this._cdRef.markForCheck();
        }
    }
    /**
     * Initialize config for list
     *
     * @param config
     */
    _initWithConfig(config) {
        if (this.list) {
            this.list.destroy();
        }
        const defaultOpts = this._defaultOptions
            ? cloneDeep(this._defaultOptions)
            : {};
        const listConfig = mergeWith(defaultOpts, config, this._configMergeCustomizer);
        if (listConfig.persist !== false) {
            this._restorePersistance(listConfig.persist);
        }
        this._updateCustomizeAction(listConfig.actions);
        this.list = new List(this._el, listConfig, this._scroll, this._selectionDialog, this._router, this._route, this._persistance, this._inDialog);
        this.noRowsHighlight = !this.list.rowHighlight;
        this._waitFirstLoad();
        this.reorderController.initWithConfig(config.reorder, this.list.dataController, this.list.actions, this.list.selection);
        if (this._listColumnDirectives) {
            this.list.tranformTemplatesToColumns(this._listColumnDirectives);
        }
        this._listenSortingChange();
    }
    /**
     * Find action with customize flag and re-declare click function for CustomizeColsDialog
     *
     * @param actions
     */
    _updateCustomizeAction(actions) {
        const customizeAction = actions === null || actions === void 0 ? void 0 : actions.find((action) => action.customize);
        if (customizeAction) {
            const actionClickFn = customizeAction.click;
            customizeAction.click = () => {
                if (actionClickFn) {
                    actionClickFn(null);
                }
                this._dialog.open(CustomizeColsDialogComponent, {
                    data: {
                        columns: this.list.columns.columnsForDialog,
                        changeFn: this.list.columns.changeFn,
                    },
                })
                    .afterClosed()
                    .pipe(takeUntil(this.list.onDestroy$), takeUntil(this._destroy))
                    .subscribe((data) => {
                    if (data) {
                        this.list.columns.updateVisibilityForCols(data);
                        this._cdRef.markForCheck();
                    }
                });
            };
        }
    }
    /**
     * Update sorting in filter
     */
    _listenSortingChange() {
        this.list.sorting
            .sortingChanged$
            .pipe(takeUntil(this.list.onDestroy$), takeUntil(this._destroy))
            .subscribe((sort) => {
            this._filterRef.updateSort(sort);
        });
    }
    _subscribeToRemoveRow() {
        this.rowRemoved
            .pipe(takeUntil(this._destroy))
            .subscribe((row) => {
            this.list.dataController.removeData(row);
            this.body.rows
                .forEach((row) => row.actionsUpdate());
        });
    }
    _subscribeToGroupExpandStatusChange() {
        if (this.list.dataController.hasGroups) {
            this._groupExpandNotifier.expandStatusChange$
                .pipe(takeUntil(this._destroy))
                .subscribe((row) => {
                this.list.dataController.toggleRowGroup(row);
            });
        }
    }
    _waitFirstLoad() {
        this.list.loading$
            .pipe(skip(1), filter((value) => !value), take(1), takeUntil(this.list.onDestroy$), takeUntil(this._destroy))
            .subscribe(() => {
            this.firstLoad = false;
            this._cdRef.markForCheck();
        });
    }
    _configMergeCustomizer(objValue, srcValue) {
        if (Array.isArray(objValue)) {
            return objValue;
        }
    }
    _restorePersistance(persistConfig) {
        const namespace = getNormalizedPath(this._location);
        this._persistance.setConfig(persistConfig, namespace, this._inDialog);
    }
}
FsListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListComponent, deps: [{ token: ReorderController }, { token: FS_LIST_DEFAULT_CONFIG, optional: true }, { token: i2$4.FsScrollService, optional: true }, { token: i1$2.MatDialogRef, optional: true }, { token: i4$1.DrawerRef, optional: true }, { token: i0.ElementRef }, { token: i5.SelectionDialog }, { token: i1$2.MatDialog }, { token: i0.ChangeDetectorRef }, { token: GroupExpandNotifierService }, { token: i7.Router }, { token: i7.ActivatedRoute }, { token: PersistanceController }, { token: i3.Location }], target: i0.ɵɵFactoryTarget.Component });
FsListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsListComponent, selector: "fs-list", inputs: { config: "config", loaderLines: "loaderLines" }, outputs: { filtersReady: "filtersReady" }, host: { properties: { "class.fs-list": "this.classFsList", "class.fs-list-no-highlight": "this.noRowsHighlight" } }, providers: [
        GroupExpandNotifierService,
        PersistanceController,
        ReorderController,
    ], queries: [{ propertyName: "headingTemplate", first: true, predicate: FsListHeadingDirective, descendants: true, read: TemplateRef }, { propertyName: "headingContainerTemplate", first: true, predicate: FsListHeadingContainerDirective, descendants: true, read: TemplateRef }, { propertyName: "subheadingTemplate", first: true, predicate: FsListSubheadingDirective, descendants: true, read: TemplateRef }, { propertyName: "_emptyStateTemplate", first: true, predicate: FsListEmptyStateDirective, descendants: true, read: TemplateRef }, { propertyName: "columnTemplates", predicate: FsListColumnDirective }], viewQueries: [{ propertyName: "body", first: true, predicate: FsBodyComponent, descendants: true }, { propertyName: "filterReference", first: true, predicate: FilterComponent, descendants: true }], ngImport: i0, template: "<div class=\"fs-list-container\"\n    [ngClass]=\"{\n      'has-filter-keyword': hasFilterKeyword,\n      'has-filters': list.filterConfig?.items.length,\n      'has-heading': list.heading || headingTemplate,\n      'has-status': hasStatus,\n      'has-chips': list.chips,\n      'has-actions': list.actions.hasActions,\n      'first-load': firstLoad,\n      'loading': list.loading$ | async\n    }\">\n  <ng-template [ngTemplateOutlet]=\"listContainerContent\"></ng-template>\n</div>\n\n<ng-template #listContainerContent>\n  <ng-container *ngIf=\"list.dataController.visibleRows$ | async as listData\">\n    <div class=\"fs-list-header-container\">\n      <div\n          class=\"fs-list-header\"\n          [ngClass]=\"{ 'no-wrap': reorderController.manualReorderActivated || !list.filterConfig?.items.length }\">\n        <ng-container\n            *ngIf=\"hasFilterKeyword\"\n            [ngTemplateOutlet]=\"heading\">\n        </ng-container>\n        <fs-filter\n            class=\"fs-list-filter\"\n            *ngIf=\"list.filterConfig\"\n            [filter]=\"list.filterConfig\"\n            [showSortBy]=\"!list.status\"\n            [showFilterInput]=\"list.filterInput\"\n            (ready)=\"filterReady()\">\n          <ng-template fsFilterStatusBar>\n            <ng-container\n                *ngIf=\"!hasFilterKeyword\"\n                [ngTemplateOutlet]=\"heading\">\n            </ng-container>\n            <ng-container *ngIf=\"hasStatus\">\n              <fs-list-status\n                class=\"fs-list-status\"\n                [ngClass]=\"{ 'hidden-mobile': !list.status }\"\n                [rows]=\"listData\"\n                [sorting]=\"list.sorting\"\n                [paging]=\"list.paging\"\n                [firstLoad]=\"firstLoad\"\n                [scrollable]=\"list.scrollable || list.paging.loadMoreEnabled\">\n              </fs-list-status>\n            </ng-container>\n          </ng-template>\n        </fs-filter>\n      </div>\n    </div>\n\n    <ng-content select=\"[fs-list-content]\"></ng-content>\n\n    <!-- Table implementation -->\n    <div class=\"fs-list-table-container\">\n      <ng-container *ngIf=\"!firstLoad && listData.length > 0 && !list.emptyStateEnabled\">\n        <table \n            class=\"fs-list-table\" \n            role=\"grid\" \n            [fsListContentInit]=\"list.afterContentInit\" \n            [ngClass]=\"{\n              'style-card': list.style === 'card',\n              'style-basic': list.style === 'basic',\n              'style-line': (list.style || 'line') === 'line'\n            }\">\n          <thead\n            fs-list-head\n            class=\"fs-list-head\"\n            role=\"rowgroup\"\n            *ngIf=\"list.columns.hasHeader\"\n            [ngClass]=\"list.columns.theadClass\"\n            [columns]=\"list.columns.visibleColumns$ | async\"\n            [sorting]=\"list.sorting\"\n            [selection]=\"list.selection\"\n            [hasRowActions]=\"list.hasRowActions\"\n            [activeFiltersCount]=\"list.activeFiltersCount$ | async\"\n            [reorderEnabled]=\"reorderController.enabled$ | async\"\n            [reorderPosition]=\"reorderController.position$ | async\"\n            [reorderStrategy]=\"reorderController.strategy$ | async\">\n          </thead>\n\n          <tbody\n            fs-list-body\n            fsListDraggableList\n            class=\"fs-list-body\"\n            role=\"rowgroup\"\n            [class.disabled]=\"!!(reorderController.reorderDisabled$ | async)\"\n            [rows]=\"listData\"\n            [rowActionsRaw]=\"list.rowActionsRaw\"\n            [groupActionsRaw]=\"list.groupActionsRaw\"\n            [hasRowActions]=\"list.hasRowActions\"\n            [rowEvents]=\"list.rowEvents\"\n            [rowClass]=\"list.rowClass\"\n            [columns]=\"list.columns.visibleColumns$ | async\"\n            [restoreMode]=\"list.restoreMode\"\n            [selection]=\"list.selection\"\n            [rowRemoved]=\"rowRemoved\"\n            [activeFiltersCount]=\"list.activeFiltersCount$ | async\"\n            [reorderEnabled]=\"reorderController.enabled$ | async\"\n            [reorderPosition]=\"reorderController.position$ | async\"\n            [reorderStrategy]=\"reorderController.strategy$ | async\"\n            [reorderMultiple]=\"reorderController.multiple\">\n          </tbody>\n\n          <tfoot\n            fs-list-footer\n            class=\"fs-list-footer\"\n            *ngIf=\"list.columns.hasFooter\"\n            [columns]=\"list.columns.visibleColumns$ | async\"\n            [selection]=\"list.selection\"\n            [hasRowActions]=\"list.hasRowActions\"\n            [activeFiltersCount]=\"list.activeFiltersCount$ | async\"\n            [reorderEnabled]=\"reorderController.enabled$ | async\"\n            [reorderPosition]=\"reorderController.position$ | async\"\n            [reorderStrategy]=\"reorderController.strategy$ | async\">\n          </tfoot>\n        </table>\n      </ng-container>\n      <fs-list-loader\n        *ngIf=\"firstLoad\"\n        [columns]=\"list.columns.columns\"\n        [loaderLines]=\"loaderLines\">\n      </fs-list-loader>\n    </div>\n\n    <fs-list-pagination\n      *ngIf=\"paginatorVisible\"\n      class=\"fs-list-pagination\"\n      [rows]=\"listData\"\n      [pagination]=\"list.paging\">\n    </fs-list-pagination>\n\n    <ng-container *ngIf=\"!firstLoad\">\n      <div\n          *ngIf=\"listData.length === 0\"\n          class=\"fs-list-no-results-container\">\n        <div\n            *ngIf=\"list.noResults?.message && !list.emptyStateEnabled\"\n            class=\"fs-list-no-results\">\n          {{ list.noResults?.message }}\n        </div>\n        <ng-container *ngIf=\"list.emptyStateEnabled\">\n          <ng-template [ngTemplateOutlet]=\"list.emptyStateTemplate\"></ng-template>\n        </ng-container>\n      </div>\n    </ng-container>\n  </ng-container>\n</ng-template>\n\n<ng-template #heading>\n  <div class=\"heading-container\" *ngIf=\"headingContainerTemplate || list.heading || list.subheading || headingTemplate\">\n    <ng-container *ngIf=\"headingContainerTemplate; else headingContainer\">\n      <ng-container\n        [ngTemplateOutlet]=\"headingContainerTemplate\"\n        [ngTemplateOutletContext]=\"{ template: headingContainer }\">\n      </ng-container>\n    </ng-container>\n    <ng-template #headingContainer>\n      <h2 class=\"heading\" *ngIf=\"list.heading || headingTemplate\">\n        {{list.heading}}\n        <ng-container [ngTemplateOutlet]=\"headingTemplate\"></ng-container>\n      </h2>\n      <div class=\"small subheading\" *ngIf=\"list.subheading || subheadingTemplate\">\n        {{list.subheading}}\n        <ng-container [ngTemplateOutlet]=\"subheadingTemplate\"></ng-container>\n      </div>\n    </ng-template>\n  </div>\n</ng-template>\n", styles: [":host{display:block;width:100%}:host ::ng-deep .fs-list-swap-restricted{opacity:.5}:host ::ng-deep .fs-list-no-drop{cursor:no-drop}:host ::ng-deep .hidden{display:none}:host ::ng-deep .multiple-selection{background-color:#2196f380}:host ::ng-deep .multiple-selection ::ng-deep .fs-list-col{background-color:#2196f380}@media print{:host ::ng-deep .fs-list-row-group{page-break-after:avoid}:host ::ng-deep .fs-list-row-group-child{page-break-before:avoid}:host ::ng-deep .fs-list-row-group-footer{page-break-before:avoid}}.fs-list-container{width:100%}.fs-list-container:not(.has-filters):not(.has-actions):not(.has-heading) .fs-list-header-container{display:none}.fs-list-container:not(.has-filter-keyword) .fs-list-header{display:flex;margin-bottom:4px}.fs-list-container:not(.has-filter-keyword) .fs-list-header .heading-container{flex:1}.fs-list-container:not(.has-filter-keyword) .fs-list-header fs-filter{display:flex}.fs-list-container.has-filters .heading-container{margin-bottom:4px}.fs-list-container.loading .fs-list-status,.fs-list-container.loading .fs-list-body,.fs-list-container.loading .fs-list-head,.fs-list-container.loading .fs-list-footer,.fs-list-container.loading fs-list-pagination,.fs-list-container.loading ::ng-deep fs-filter-chips,.fs-list-container.loading .fs-list-no-results-container,.fs-list-container.first-load .fs-list-status,.fs-list-container.first-load .fs-list-body,.fs-list-container.first-load .fs-list-head,.fs-list-container.first-load .fs-list-footer,.fs-list-container.first-load fs-list-pagination,.fs-list-container.first-load ::ng-deep fs-filter-chips,.fs-list-container.first-load .fs-list-no-results-container{opacity:.4;pointer-events:none}.fs-list-container.first-load ::ng-deep fs-filter-chips .fs-chip{color:transparent}.fs-list-container.first-load ::ng-deep fs-filter-chips .fs-chip .remove{visibility:hidden}.fs-list-container.has-actions .fs-list-actions{margin-left:5px}.fs-list-container .fs-list-header .heading-container{display:flex;flex-direction:column;justify-content:center}.fs-list-container .fs-list-header .heading{margin:0}.fs-list-container .fs-list-header h2+.subheading{margin:2px 0 0}.fs-list-container .fs-list-filter{margin-bottom:0;position:initial!important;display:block;width:100%}.fs-list-container .fs-list-no-results-container .fs-list-no-results{text-align:center;color:#999;padding:10px 0}.fs-list-container .fs-list-table-container{width:100%;overflow:auto}::ng-deep .fs-list-table{border-spacing:0;display:table;width:100%;border-collapse:collapse}::ng-deep .fs-list-table thead{display:table-header-group}::ng-deep .fs-list-table thead th{color:#999;padding:8px;font-weight:normal;color:#8f8f8f;font-size:85%;text-align:left}::ng-deep .fs-list-table tbody{display:table-row-group;position:relative}::ng-deep .fs-list-table tbody tr,::ng-deep .fs-list-table thead tr,::ng-deep .fs-list-table tfoot tr{display:table-row}::ng-deep .fs-list-table tbody tr td,::ng-deep .fs-list-table thead tr td,::ng-deep .fs-list-table tfoot tr td{display:table-cell;padding:8px;vertical-align:middle;outline:none;text-align:left}::ng-deep .fs-list-table tfoot td{padding:8px}::ng-deep .fs-list-table.style-line tbody .fs-list-col,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) tbody .fs-list-col{box-sizing:border-box;border-top:1px solid #ddd}::ng-deep .fs-list-table.style-line tbody .fs-list-row:first-child .fs-list-col,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) tbody .fs-list-row:first-child .fs-list-col{border-top:2px solid #ddd}::ng-deep .fs-list-table.style-line tbody .fs-list-row:last-child .fs-list-col,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) tbody .fs-list-row:last-child .fs-list-col{border-bottom:2px solid #ddd}::ng-deep .fs-list-table.style-line .fs-list-row:hover .fs-list-col,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) .fs-list-row:hover .fs-list-col{background-color:#f6f6f6}::ng-deep .fs-list-table.style-line .fs-list-container.has-dragging thead th,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) .fs-list-container.has-dragging thead th{border-bottom:2px solid #ddd}::ng-deep .fs-list-table.style-line .fs-list-container.has-dragging tbody tr:nth-child(2) td,::ng-deep .fs-list-table:not(.style-card):not(.style-basic) .fs-list-container.has-dragging tbody tr:nth-child(2) td{border-top:none}::ng-deep .fs-list-table.style-line.style-card,::ng-deep .fs-list-table:not(.style-card):not(.style-basic).style-card{border-spacing:0 8px;border-collapse:separate}::ng-deep .fs-list-table.style-line.style-card tbody tr:not(.fs-list-row-group-footer):not(.fs-list-row-group),::ng-deep .fs-list-table:not(.style-card):not(.style-basic).style-card tbody tr:not(.fs-list-row-group-footer):not(.fs-list-row-group){background-color:#fafafa;border-radius:10px}::ng-deep .fs-list-table.style-line.style-card tbody tr td,::ng-deep .fs-list-table:not(.style-card):not(.style-basic).style-card tbody tr td{border:none!important;padding:8px 16px}::ng-deep .fs-list-table.style-line.style-card tbody tr td:first-child,::ng-deep .fs-list-table:not(.style-card):not(.style-basic).style-card tbody tr td:first-child{border-top-left-radius:10px;border-bottom-left-radius:10px}::ng-deep .fs-list-table.style-line.style-card tbody tr td:last-child,::ng-deep .fs-list-table:not(.style-card):not(.style-basic).style-card tbody tr td:last-child{border-top-right-radius:10px;border-bottom-right-radius:10px}::ng-deep .fs-list-preview-block{position:fixed;height:30px;width:200px;background:grey;display:flex;justify-content:center;align-items:center;z-index:1000;box-shadow:2px 2px 2px #9e9e9ea6;border-radius:6px}::ng-deep .reorder-in-progress{-webkit-user-select:none;user-select:none}@media only screen and (max-width: 600px){::ng-deep .fs-list-filter .inline-actions{top:initial!important;position:initial!important}::ng-deep .fs-list-filter .inline-actions .action-filter{margin-bottom:0!important}::ng-deep .fs-list-header .filter-input-field .mat-form-field-wrapper{padding:0!important}::ng-deep .fs-list-header.has-filters{flex-flow:row wrap}}@media only screen and (max-width: 768px){::ng-deep .show-mobile{display:inline-block!important}::ng-deep .fs-list-actions .action-button{display:none}::ng-deep .row-inline-action.mobile-hide{display:none}::ng-deep .hidden-mobile-menu-action{display:block!important}}:host(.fs-list-no-highlight) ::ng-deep .fs-list-row:hover .fs-list-col{background-color:initial}\n"], components: [{ type: i2$3.FilterComponent, selector: "fs-filter", inputs: ["config", "filter", "showSortBy", "showFilterInput"], outputs: ["closed", "opened", "ready"] }, { type: FsStatusComponent, selector: "fs-list-status", inputs: ["paging", "sorting", "rows", "scrollable", "firstLoad"] }, { type: FsHeadComponent, selector: "[fs-list-head]", inputs: ["sorting", "columns", "hasRowActions", "selection", "activeFiltersCount", "reorderEnabled", "reorderPosition", "reorderStrategy"] }, { type: FsBodyComponent, selector: "[fs-list-body]", inputs: ["rows", "columns", "hasFooter", "rowActionsRaw", "groupActionsRaw", "rowEvents", "rowClass", "hasRowActions", "selection", "restoreMode", "rowRemoved", "activeFiltersCount", "reorderEnabled", "reorderPosition", "reorderStrategy", "reorderMultiple"] }, { type: FsFooterComponent, selector: "[fs-list-footer]", inputs: ["hasRowActions", "columns", "selection", "activeFiltersCount", "reorderEnabled", "reorderPosition", "reorderStrategy"] }, { type: FsListLoaderComponent, selector: "fs-list-loader", inputs: ["columns", "loaderLines"] }, { type: FsPaginationComponent, selector: "fs-list-pagination", inputs: ["pagination", "rows"] }], directives: [{ type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2$3.FilterStatusBarDirective, selector: "[fsFilterStatusBar]" }, { type: FsListContentInitDirective, selector: "[fsListContentInit]", inputs: ["fsListContentInit"] }, { type: FsListDraggableListDirective, selector: "[fsListDraggableList]", inputs: ["rows"] }, { type: FsListFooterDirective, selector: "[fs-list-footer]", inputs: ["colspan", "align", "class"] }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-list',
                    templateUrl: './list.component.html',
                    styleUrls: ['./list.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [
                        GroupExpandNotifierService,
                        PersistanceController,
                        ReorderController,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: ReorderController }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_LIST_DEFAULT_CONFIG]
                }] }, { type: i2$4.FsScrollService, decorators: [{
                    type: Optional
                }] }, { type: i1$2.MatDialogRef, decorators: [{
                    type: Optional
                }] }, { type: i4$1.DrawerRef, decorators: [{
                    type: Optional
                }] }, { type: i0.ElementRef }, { type: i5.SelectionDialog }, { type: i1$2.MatDialog }, { type: i0.ChangeDetectorRef }, { type: GroupExpandNotifierService }, { type: i7.Router }, { type: i7.ActivatedRoute }, { type: PersistanceController }, { type: i3.Location }]; }, propDecorators: { classFsList: [{
                type: HostBinding,
                args: ['class.fs-list']
            }], noRowsHighlight: [{
                type: HostBinding,
                args: ['class.fs-list-no-highlight']
            }], config: [{
                type: Input,
                args: ['config']
            }], loaderLines: [{
                type: Input
            }], filtersReady: [{
                type: Output
            }], headingTemplate: [{
                type: ContentChild,
                args: [FsListHeadingDirective, { read: TemplateRef }]
            }], headingContainerTemplate: [{
                type: ContentChild,
                args: [FsListHeadingContainerDirective, { read: TemplateRef }]
            }], subheadingTemplate: [{
                type: ContentChild,
                args: [FsListSubheadingDirective, { read: TemplateRef }]
            }], body: [{
                type: ViewChild,
                args: [FsBodyComponent]
            }], filterReference: [{
                type: ViewChild,
                args: [FilterComponent]
            }], columnTemplates: [{
                type: ContentChildren,
                args: [FsListColumnDirective]
            }], _emptyStateTemplate: [{
                type: ContentChild,
                args: [FsListEmptyStateDirective, { read: TemplateRef }]
            }] } });

class FsListManageSavedFiltersComponent {
    constructor(_externalParams) {
        this._externalParams = _externalParams;
        this._reorderReady = true;
        this._initList();
    }
    get savedFiltersController() {
        return this._externalParams.savedFiltersController;
    }
    _initList() {
        this.config = {
            status: false,
            filterInput: false,
            queryParam: false,
            persist: false,
            paging: false,
            reorder: {
                position: ReorderPosition.Left,
                moveDrop: () => {
                    return this._reorderReady;
                },
                done: (reorderData) => {
                    this._reorderReady = false;
                    const filterData = reorderData.map((data) => data.data);
                    this.savedFiltersController.order(filterData)
                        .pipe(tap(() => {
                        this._reorderReady = true;
                    }))
                        .subscribe(() => { });
                },
            },
            rowActions: [
                {
                    label: 'Apply',
                    click: (filter) => {
                        this._externalParams.setActiveSavedFilter(filter);
                    },
                },
                {
                    label: 'Delete',
                    remove: {
                        title: 'Confirm',
                        template: 'Are you sure you want to delete this filter?',
                    },
                    click: (savedFilter) => {
                        return this.savedFiltersController.delete(savedFilter);
                    },
                },
            ],
            fetch: (query) => {
                return this.savedFiltersController.load()
                    .pipe(map((response) => {
                    return { data: response };
                }));
            },
        };
    }
}
FsListManageSavedFiltersComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListManageSavedFiltersComponent, deps: [{ token: i2$3.ExternalParamsController }], target: i0.ɵɵFactoryTarget.Component });
FsListManageSavedFiltersComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsListManageSavedFiltersComponent, selector: "ng-component", ngImport: i0, template: "<h1 mat-dialog-title>\n  Manage Saved Filters\n</h1>\n<div mat-dialog-content>\n  <fs-list [config]=\"config\">\n    <fs-list-column name=\"name\" title=\"Name\">\n      <ng-template fs-list-cell let-row=\"row\">\n        {{ row.name }}\n      </ng-template>\n    </fs-list-column>\n  </fs-list>\n</div>\n<div mat-dialog-actions>\n  <button mat-button\n          [mat-dialog-close]=\"null\"\n          type=\"button\"\n          color=\"primary\">\n    Done\n  </button>\n</div>\n", components: [{ type: FsListComponent, selector: "fs-list", inputs: ["config", "loaderLines"], outputs: ["filtersReady"] }, { type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i1$2.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i1$2.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: FsListColumnDirective, selector: "fs-list-column", inputs: ["show", "title", "name", "customize", "sortable", "sortableDefault", "direction", "align", "width", "class"] }, { type: FsListCellDirective, selector: "[fs-list-cell]", inputs: ["colspan", "align", "class"] }, { type: i1$2.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }, { type: i1$2.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["type", "mat-dialog-close", "aria-label", "matDialogClose"], exportAs: ["matDialogClose"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListManageSavedFiltersComponent, decorators: [{
            type: Component,
            args: [{
                    templateUrl: './manage-saved-filters.component.html',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i2$3.ExternalParamsController }]; } });

class FsListContentDirective {
}
FsListContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsListContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsListContentDirective, selector: "[fs-list-content]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fs-list-content]',
                }]
        }] });

class FsListModule {
    static forRoot(config = {}) {
        return {
            ngModule: FsListModule,
            providers: [
                { provide: FS_LIST_CONFIG, useValue: config },
                {
                    provide: FS_LIST_DEFAULT_CONFIG,
                    useFactory: fsListConfigFactory,
                    deps: [FS_LIST_CONFIG],
                },
            ],
        };
    }
}
FsListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListModule, declarations: [
        // Components
        FsListComponent,
        FsRowComponent,
        FsRowActionsComponent,
        FsRowInlineActionComponent,
        FsRowMenuActionComponent,
        FsCellComponent,
        FsFooterRowComponent,
        FsFooterCellComponent,
        FsStatusComponent,
        FsListLoaderComponent,
        FsListSavedFiltersComponent,
        FsListManageSavedFiltersComponent,
        // Internal Components
        FsHeadComponent,
        FsHeadCellComponent,
        FsBodyComponent,
        FsFooterComponent,
        FsPaginationComponent,
        // Directives
        FsListColumnDirective,
        FsListCellDirective,
        FsListHeaderDirective,
        FsListFooterDirective,
        FsListGroupHeaderDirective,
        FsListGroupFooterDirective,
        FsListGroupExpandTriggerDirective,
        FsListDraggableListDirective,
        FsListDraggableRowDirective,
        FsListEmptyStateDirective,
        FsListContentDirective,
        FsListContentInitDirective,
        FsListSubheadingDirective,
        FsListHeadingDirective,
        FsListHeadingContainerDirective,
        // Dialog
        CustomizeColsDialogComponent,
        //Pipes
        ActionLabelPipe], imports: [CommonModule,
        RouterModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatRippleModule,
        FsFilterModule,
        FsMenuModule,
        FsScrollModule,
        FsPromptModule,
        MatDialogModule,
        MatTooltipModule,
        FsFileModule,
        FsCommonModule], exports: [FsListComponent,
        FsRowComponent,
        FsCellComponent,
        FsListLoaderComponent,
        FsListColumnDirective,
        FsListCellDirective,
        FsListHeaderDirective,
        FsListFooterDirective,
        FsListGroupHeaderDirective,
        FsListGroupFooterDirective,
        FsListGroupExpandTriggerDirective,
        FsListEmptyStateDirective,
        FsListContentDirective,
        FsListSubheadingDirective,
        FsListHeadingDirective,
        FsListHeadingContainerDirective] });
FsListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListModule, imports: [[
            CommonModule,
            RouterModule,
            MatButtonModule,
            MatIconModule,
            MatMenuModule,
            MatProgressSpinnerModule,
            MatCheckboxModule,
            MatRippleModule,
            FsFilterModule,
            FsMenuModule,
            FsScrollModule,
            FsPromptModule,
            MatDialogModule,
            MatTooltipModule,
            FsFileModule,
            FsCommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        MatButtonModule,
                        MatIconModule,
                        MatMenuModule,
                        MatProgressSpinnerModule,
                        MatCheckboxModule,
                        MatRippleModule,
                        FsFilterModule,
                        FsMenuModule,
                        FsScrollModule,
                        FsPromptModule,
                        MatDialogModule,
                        MatTooltipModule,
                        FsFileModule,
                        FsCommonModule,
                    ],
                    declarations: [
                        // Components
                        FsListComponent,
                        FsRowComponent,
                        FsRowActionsComponent,
                        FsRowInlineActionComponent,
                        FsRowMenuActionComponent,
                        FsCellComponent,
                        FsFooterRowComponent,
                        FsFooterCellComponent,
                        FsStatusComponent,
                        FsListLoaderComponent,
                        FsListSavedFiltersComponent,
                        FsListManageSavedFiltersComponent,
                        // Internal Components
                        FsHeadComponent,
                        FsHeadCellComponent,
                        FsBodyComponent,
                        FsFooterComponent,
                        FsPaginationComponent,
                        // Directives
                        FsListColumnDirective,
                        FsListCellDirective,
                        FsListHeaderDirective,
                        FsListFooterDirective,
                        FsListGroupHeaderDirective,
                        FsListGroupFooterDirective,
                        FsListGroupExpandTriggerDirective,
                        FsListDraggableListDirective,
                        FsListDraggableRowDirective,
                        FsListEmptyStateDirective,
                        FsListContentDirective,
                        FsListContentInitDirective,
                        FsListSubheadingDirective,
                        FsListHeadingDirective,
                        FsListHeadingContainerDirective,
                        // Dialog
                        CustomizeColsDialogComponent,
                        //Pipes
                        ActionLabelPipe,
                    ],
                    exports: [
                        FsListComponent,
                        FsRowComponent,
                        FsCellComponent,
                        FsListLoaderComponent,
                        FsListColumnDirective,
                        FsListCellDirective,
                        FsListHeaderDirective,
                        FsListFooterDirective,
                        FsListGroupHeaderDirective,
                        FsListGroupFooterDirective,
                        FsListGroupExpandTriggerDirective,
                        FsListEmptyStateDirective,
                        FsListContentDirective,
                        FsListSubheadingDirective,
                        FsListHeadingDirective,
                        FsListHeadingContainerDirective,
                    ],
                }]
        }] });
function fsListConfigFactory(config) {
    return merge$1({
        noResults: {
            message: 'No Results Found',
        },
    }, config);
}

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActionType, Column, FS_LIST_CONFIG, FS_LIST_DEFAULT_CONFIG, FsBodyComponent, FsCellComponent, FsFooterCellComponent, FsFooterComponent, FsFooterRowComponent, FsHeadCellComponent, FsHeadComponent, FsListCellDirective, FsListColumnDirective, FsListComponent, FsListContentDirective, FsListEmptyStateDirective, FsListFooterDirective, FsListGroupExpandTriggerDirective, FsListGroupFooterDirective, FsListGroupHeaderDirective, FsListHeaderDirective, FsListHeadingContainerDirective, FsListHeadingDirective, FsListLoaderComponent, FsListModule, FsListState, FsListSubheadingDirective, FsPaginationComponent, FsRowComponent, FsStatusComponent, List, PaginationController, PaginationStrategy, ReorderController, ReorderPosition, RowAction, RowType, SelectionChangeType, SelectionController, SortingController, SortingDirection, StyleConfig };
